import * as XLSX from 'xlsx';

export const SHEET_NAME = 'Sold Rates';

const COL = {
    VALUE: 1,
    FEE_CODE: 0,
    VENDOR: 1,
    FEE_DESC: 2,
    RATE_TYPE: 3,
    FEE_TYPE: 4,
    PLAN_START: 5,
    PLAN_END: 12,
};

const ROW = {
    EFFECTIVE_DATE: 2,
    GROUP_NAME: 4,
    GROUP_CODE: 5,
    SOLD_PLAN_COUNT: 6,
    BILLING_PERIOD_START: 7,
    BILLING_PERIOD_END: 8,
    CURRENT_PLAN_NAMES: 24,
    FALLBACK_PLAN_NAMES: 25,
    RATE_EE_ONLY: 27,
    RATE_EES: 28,
    RATE_EEC: 29,
    RATE_FAM: 30,
};

const MAX_PLAN_COUNT = COL.PLAN_END - COL.PLAN_START + 1;
export const MAX_SOLD_PLAN_COUNT = MAX_PLAN_COUNT;
const FEE_MATRIX_LABEL = 'FEE BUILD MATRIX';
const RISK_PREMIUM_LABEL = 'Risk Premium';
const PLAN_NAMES_LABEL = 'Plan Names';
const SOLD_PRODUCT_LABEL = 'Sold Product';
const DEFAULT_FEE_TYPE = 'PEPM'; // Most fees will be PEPM, but some will be PMPM

const TIER_ALIASES = {
    EEO: 'EE Only',
    'EE ONLY': 'EE Only',
    EE: 'EE Only',
    EMPLOYEE: 'EE Only',
    'EES / EE+1': 'EES / EE+1',
    ES: 'EES / EE+1',
    'EMPLOYEE & SPOUSE': 'EES / EE+1',
    EEC: 'EEC',
    EC: 'EEC',
    'EMPLOYEE & CHILD': 'EEC',
    FAM: 'FAM / EE+2+',
    FAMILY: 'FAM / EE+2+',
    'FAM / EE+2+': 'FAM / EE+2+',
};

const SUPPORTED_FEE_TYPES = {
    PEPM: {
        definitionKey: 'perSubscriptionPerMonthBillingRateDefinition',
        rateSetKey: 'pspmRateSet',
    },
    PMPM: {
        definitionKey: 'perMemberPerMonthBillingRateDefinition',
        rateSetKey: 'pmpmRateSet',
    },
};

export const COMPONENT_CREATE_REASON = {
    codeSetName: 'BillingRateComponentCreateReason',
    shortName: 'New Billing Rate Component',
};

export const COMPONENT_AMEND_REASON = {
    codeSetName: 'BillingRateComponentAmendReason',
    shortName: 'Changes in configuration',
};

export const RATE_CREATE_REASON = {
    codeSetName: 'BillingRateCreateReason',
    shortName: 'New configuration',
};

export const RATE_AMEND_REASON = {
    codeSetName: 'BillingRateAmendReason',
    shortName: 'Fixed configuration error',
};

export const RATE_SUBCATEGORY = {
    codeSetName: 'BillingRateSubCategory',
};

export const DEFAULT_RATE_SUBCATEGORY_VENDOR = 'Centivo';

/** Canonical fee description → rate subcategory vendor. Shared by ASA and renewal flows. */
export const FEE_SUBCATEGORY_VENDOR_BY_DESCRIPTION = {
    'Medical Admin': 'Centivo',
    'Specialty RX': 'Archimedes',
    'Broker Fee': 'Centivo',
    'Accelerated Stop Loss Reimbursement Services': 'Aegle',
    'Telemedicine': 'Centivo',
    'Success Fee': 'Centivo',
};

/** Returns shortName when set; otherwise the default rate subcategory vendor. */
export function resolveRateSubCategoryShortName(shortName) {
    const trimmed = String(shortName ?? '').trim();
    return trimmed || DEFAULT_RATE_SUBCATEGORY_VENDOR;
}

/**
 * Resolves rate subcategory vendor from a fee row (ASA, renewal, or mapped component).
 * Keep in sync with packages/backend/src/lambda/billingRateProcessor.js.
 */
export function resolveFeeSubCategoryVendor(fee) {
    const explicit = String(fee?.vendor ?? '').trim();
    if (explicit) return explicit;

    const description = fee?.description ?? '';
    const networkVendor = networkAccessVendorFromDescription(description);
    if (networkVendor) return networkVendor;

    const canonical = canonicalizeFeeDescription(description);
    if (canonical && FEE_SUBCATEGORY_VENDOR_BY_DESCRIPTION[canonical]) {
        return FEE_SUBCATEGORY_VENDOR_BY_DESCRIPTION[canonical];
    }

    return vendorFromRenewalNotes(fee?.notes) ?? null;
}

/** Sets rate subcategory to the default vendor on components that have none. Mutates in place. */
export function applyDefaultSubCategoryFallback(componentPayloads) {
    (componentPayloads ?? []).forEach((wrapper, index, arr) => {
        arr[index] = applyVendorToComponentWrapper(wrapper, DEFAULT_RATE_SUBCATEGORY_VENDOR);
    });
}

export function isHrpComponentTiered(hrpComponent) {
    return hrpComponent?.tiered === true
        || hrpComponent?.tiered === 1
        || hrpComponent?.tiered === '1';
}

function pickNonZeroHrpRateAmount(value) {
    return formatRateAmountString(value);
}

/** Flat or tier-specific rate from HRP component read-back. */
export function resolveHrpComponentRateAmount(hrpComponent) {
    if (!hrpComponent) return null;
    const flat = pickNonZeroHrpRateAmount(hrpComponent.defaultRate);
    if (flat) return flat;
    if (isHrpComponentTiered(hrpComponent)) {
        return pickNonZeroHrpRateAmount(hrpComponent.maxTierRate);
    }
    return pickNonZeroHrpRateAmount(hrpComponent.defaultRate);
}

function findHrpSnapshotComponent(hrpSnapshot, row) {
    if (!row?.hrpComponentKey && !row?.hrpComponentName) return null;
    return (hrpSnapshot?.components ?? []).find(
        (c) => (row.hrpComponentKey != null && c.billingRateComponentKey === row.hrpComponentKey)
            || (row.hrpComponentName && c.billingRateComponentName === row.hrpComponentName),
    ) ?? null;
}

function applyHrpSubCategoryToWrapper(wrapper, hrpComponent) {
    if (!wrapper?.billingRateComponentPayload?.billingRateComponent || !hrpComponent?.rateSubCategoryShortName) {
        return wrapper;
    }

    const shortName = hrpComponent.rateSubCategoryShortName;
    const component = wrapper.billingRateComponentPayload.billingRateComponent;
    const updated = {
        ...wrapper,
        billingRateComponentPayload: {
            ...wrapper.billingRateComponentPayload,
            billingRateComponent: {
                ...component,
                definition: {
                    ...component.definition,
                    rateClassifications: {
                        ...component.definition?.rateClassifications,
                        rateSubCategory: {
                            codeSetName: RATE_SUBCATEGORY.codeSetName,
                            shortName,
                        },
                    },
                },
            },
        },
    };

    if (hrpComponent.rateSubCategoryCode) {
        updated.subCategoryMatch = {
            codeEntry: hrpComponent.rateSubCategoryCode,
            matchType: 'exact',
            matchedShortName: shortName,
            confirmed: true,
        };
    }

    return updated;
}

export const BILLING_CATEGORY = {
    codeSetName: 'BillingCategory',
};

// ============================================================================
// Validation error model
//
// A single, extensible model for collecting errors raised by any step of the
// billing rate flow (file read, parsing, mapping, post-map checks). Each error
// carries a `code` and a `severity`. Severity defaults come from the registry
// below, so promoting an existing warning to a blocking hard stop (or adding a
// new code) is a one-line change.
// ============================================================================

export const VALIDATION_SEVERITY = {
    HARD_STOP: 'hardStop',
    WARNING: 'warning',
};

const { HARD_STOP, WARNING } = VALIDATION_SEVERITY;

// code -> { severity, message(meta) }. Codes absent from this registry default
// to WARNING severity and fall back to the supplied/`code` message.
export const VALIDATION_CODES = {
    SOLD_RATES_SHEET_MISSING: {
        severity: HARD_STOP,
        message: () => `The "${SHEET_NAME}" sheet is missing from the workbook.`,
    },
    NO_COMPONENTS_GENERATED: {
        severity: HARD_STOP,
        message: () => 'No billing rate components could be generated from the file.',
    },
    UNSUPPORTED_FILE_TYPE: {
        severity: HARD_STOP,
        message: (meta) => `Unsupported file type${meta?.fileName ? `: ${meta.fileName}` : ''}.`,
    },
    FILE_READ_FAILED: {
        severity: HARD_STOP,
        message: (meta) => `Could not read file${meta?.fileName ? ` "${meta.fileName}"` : ''}.`,
    },
    SOLD_PRODUCT_MISMATCH: {
        severity: HARD_STOP,
        message: (meta) => `This workbook is marked "${meta?.soldProductLabel ?? 'a different plan type'}" but "${meta?.selectedLabel ?? 'the selected plan type'}" is selected. Switch the plan type or choose another file.`,
    },
    COVERAGE_TIER_SET_NOT_FOUND: {
        severity: HARD_STOP,
        message: (meta) => `Coverage tier set not found for benefit plan ${meta?.benefitPlanHccId ?? 'unknown'}. Select a coverage tier set to continue.`,
    },
    COVERAGE_TIER_SET_FETCH_FAILED: {
        severity: HARD_STOP,
        message: (meta) => `Coverage tier set fetch failed for benefit plan ${meta?.benefitPlanHccId ?? 'unknown'}${meta?.fetchError ? `: ${meta.fetchError}` : ''}. Select a coverage tier set to continue.`,
    },
    UNSUPPORTED_COVERAGE_TIER_SET: {
        severity: HARD_STOP,
        message: (meta) => `Coverage tier set "${meta?.coverageTierSetName ?? 'unknown'}" is not supported for benefit plan ${meta?.benefitPlanHccId ?? 'unknown'}. Select a coverage tier set to continue.`,
    },
    ACCOUNT_NOT_FOUND: {
        severity: HARD_STOP,
        message: (meta) => `Account "${meta?.accountHccId ?? 'unknown'}" was not found in HRP. Select the correct account to continue.`,
    },
    ACCOUNT_INSUFFICIENT_PLANS: {
        severity: HARD_STOP,
        message: (meta) => `Account "${meta?.accountHccId ?? 'unknown'}" has ${meta?.actualPlanCount ?? 0} active benefit plan(s) but the spreadsheet has ${meta?.expectedPlanCount ?? 0}. Select the correct account to continue.`,
    },
    BENEFIT_PLAN_NOT_FOUND: {
        severity: HARD_STOP,
        message: (meta) => `Benefit plan "${meta?.planName ?? meta?.benefitPlanHccId ?? 'unknown'}" was not found for this account. Select the correct benefit plan to continue.`,
    },
    BENEFIT_PLANS_FETCH_FAILED: {
        severity: HARD_STOP,
        message: (meta) => `Could not load benefit plans for account ${meta?.accountHccId ?? 'unknown'}${meta?.fetchError ? `: ${meta.fetchError}` : ''}. Try again.`,
    },
    RENEWAL_REQUIRED: {
        severity: WARNING,
        message: (meta) => `The amount for "${meta?.feeDescription ?? 'this component'}" is not available in the ASA for the current year. Enter the rate manually or resolve it in the HRP reconciliation drawer.`,
    },
};

/** Hard-stop codes that require manual coverage tier set selection per plan. */
export const COVERAGE_TIER_SET_HARD_STOP_CODES = [
    'COVERAGE_TIER_SET_NOT_FOUND',
    'COVERAGE_TIER_SET_FETCH_FAILED',
    'UNSUPPORTED_COVERAGE_TIER_SET',
];

/** Hard-stop codes that require manual account selection. */
export const ACCOUNT_HARD_STOP_CODES = ['ACCOUNT_NOT_FOUND', 'ACCOUNT_INSUFFICIENT_PLANS'];

/** Hard-stop codes that require manual benefit plan remap per workbook plan. */
export const BENEFIT_PLAN_HARD_STOP_CODES = ['BENEFIT_PLAN_NOT_FOUND'];

export function createValidationError(code, meta = {}) {
    const definition = VALIDATION_CODES[code];
    const { severity, message, ...rest } = meta;
    return {
        code,
        severity: severity ?? definition?.severity ?? WARNING,
        message: message ?? definition?.message?.(meta) ?? code,
        ...rest,
    };
}

export function toValidationError(error, meta = {}) {
    const code = error?.code ?? 'FILE_READ_FAILED';
    return createValidationError(code, { message: error?.message, ...meta });
}

export function createValidationCollector(initial = []) {
    const errors = [...initial];
    const collector = {
        add(error) {
            if (error) errors.push(error);
            return collector;
        },
        addAll(list = []) {
            list.forEach((error) => collector.add(error));
            return collector;
        },
        all() {
            return errors;
        },
        bySeverity(severity) {
            return errors.filter((error) => error.severity === severity);
        },
        hardStops() {
            return collector.bySeverity(HARD_STOP);
        },
        hasHardStop() {
            return errors.some((error) => error.severity === HARD_STOP);
        },
        messages(severity) {
            return (severity ? collector.bySeverity(severity) : errors).map((error) => error.message);
        },
    };
    return collector;
}

// NOTE: These can be queried from COVERAGE_TIER_SET_HISTORY_FACT but require static mapping to tiers below
export const COVERAGE_TIER_SET_NAMES = {
    '4:domesticPartner': '4-Tier Allow Domestic Partner',
    '4:spouse': '4-Tier Spouse',
    '3:domesticPartner': '3-Tier Subscriber Plus 1 and Family; Allow DP',
    '3:spouse': '3-Tier Subscriber Plus 1 and Family; Spouse',
    '2:domesticPartner': '2-Tier Allow Domestic Partner',
    '2:spouse': '2-Tier Spouse',
};

const _isDevEnv = (typeof import.meta.env !== "undefined" ? import.meta.env.VITE_ENVIRONMENT : undefined) === 'DEV';

// DEV HRP registered most groups with "+" on these two members. AITEC is the
// exception: its DEV catalog matches STAGE/PROD ("Plus", and FAM "2+").
export const DEV_STAGING_ALIGNED_GROUP_IDS = new Set(['AITEC']);

function normalizeCoverageTierGroupId(groupId) {
    return String(groupId ?? '').trim().toUpperCase();
}

export function usesStagingAlignedCoverageTiers(groupId, { isDevEnv = _isDevEnv } = {}) {
    if (!isDevEnv) return true;
    return DEV_STAGING_ALIGNED_GROUP_IDS.has(normalizeCoverageTierGroupId(groupId));
}

export function getCoverageTierMappings(groupId, { isDevEnv = _isDevEnv } = {}) {
    const stagingAligned = usesStagingAlignedCoverageTiers(groupId, { isDevEnv });
    const subscriberPlusSpouse = stagingAligned ? 'Subscriber Plus Spouse' : 'Subscriber + Spouse';
    const subscriberPlusChild = stagingAligned ? 'Subscriber Plus Child or Children' : 'Subscriber + Child or Children';
    const dpFamTwoDependents = isDevEnv && DEV_STAGING_ALIGNED_GROUP_IDS.has(normalizeCoverageTierGroupId(groupId))
        ? 'Spouse or DP Plus 2+ Dependents No Subscriber'
        : 'Spouse or DP Plus 2 Dependents No Subscriber';

    // TODO: These can partially be queried from RATE_ENTRY_FCT_TO_COV_TIER (only those in-use)
    return {
    '4:spouse': {
        'EE Only': ['Subscriber Only', '1 Member No Subscriber'],
        'EES / EE+1': [
            subscriberPlusSpouse,
            'Subscriber Plus 1 Dependent',
            'Spouse Plus Ex-Spouse No Subscriber',
            'Spouse Plus 1 Dependent No Subscriber',
            '2 Dependents No Subscriber',
        ],
        EEC: [
            subscriberPlusChild,
            'Spouse Plus Child or Children No Subscriber',
            'Child or Children Plus 1 Dependent No Subscriber',
            '2+ Children No Subscriber',
        ],
        'FAM / EE+2+': [
            'Family',
            'Family with Ex-Spouse',
            'Subscriber Plus Spouse and Ex-Spouse',
            'Subscriber Plus Child or Children and Dependent',
            'Subscriber Plus 2+ Dependents',
            'Spouse Plus Child and Dependent No Subscriber',
            'Spouse and Ex-Sp Plus Child or Dep No Subscriber',
            'Spouse Plus 2+ Dependents No Subscriber',
            'Child or Children Plus 2+ Dependents No Subscriber',
            '3+ Dependents',
        ],
    },
    '4:domesticPartner': {
        'EE Only': ['Subscriber Only', '1 Member No Subscriber'],
        'EES / EE+1': [
            'Subscriber Plus Spouse or Domestic Partner',
            'Subscriber Plus 1 Dependent',
            'Spouse or DP Plus Ex-Spouse No Subscriber',
            'Spouse or DP Plus 1 Dependent No Subscriber',
            '2 Dependents No Subscriber',
        ],
        EEC: [
            subscriberPlusChild,
            'Spouse or DP Plus Child or Children No Subscriber',
            'Child or Children Plus 1 Dependent No Subscriber',
            '2+ Children No Subscriber',
        ],
        'FAM / EE+2+': [
            'Family',
            'Family with Ex-Spouse',
            'Subscriber Plus Spouse or DP and Ex-Spouse',
            'Subscriber Plus Child or Children and Dependents',
            'Subscriber Plus 2+ Dependents',
            'Spouse or DP Plus Child and Dep No Subscriber',
            'Spouse and Ex Sp Plus Child or Dep No Subscriber',
            dpFamTwoDependents,
            'Child or Children Plus 2+ Dependents No Subscriber',
            '3+ Dependents No Subscriber',
        ],
    },
    '3:spouse': {
        'EE Only': ['Subscriber Only'],
        'EES / EE+1': ['Subscriber Plus 1', '1 Member No Subscriber', '2 Members No Subscriber'],
        'FAM / EE+2+': ['Family', 'Family No Subscriber'],
    },
    '3:domesticPartner': {
        'EE Only': ['Subscriber Only'],
        'EES / EE+1': ['Subscriber Plus 1', '1 Member No Subscriber', '2 Members No Subscriber'],
        'FAM / EE+2+': ['Family', 'Family No Subscriber'],
    },
    '2:spouse': {
        'EE Only': ['Subscriber Only', '1 Member No Subscriber'],
        'FAM / EE+2+': ['Family', 'Family No Subscriber'],
    },
    '2:domesticPartner': {
        'EE Only': ['Subscriber Only', '1 Member No Subscriber'],
        'FAM / EE+2+': ['Family', 'Family No Subscriber'],
    },
    };
}

export const COVERAGE_TIER_MAPPINGS = getCoverageTierMappings();

const PLACEHOLDER_PATTERN = /^\[insert(?:\s+[^\]]+)?\]$/i; // Detects placeholder values in workbook like "[insert description here]""

const cell = (rows, row, col) => rows[row]?.[col] ?? null;
const trim = (value) => (typeof value === 'string' ? value.trim() : value);
const hasValue = (row) => row?.some((value) => value !== null && value !== '') ?? false;
const hasLabel = (value, label) => String(value ?? '').trim().toUpperCase().includes(label.toUpperCase());

function blankIfPlaceholder(value) {
    if (value == null) return undefined;
    const trimmed = String(value).trim();
    if (trimmed === '' || /^#+$/.test(trimmed) || PLACEHOLDER_PATTERN.test(trimmed)) {
        return undefined;
    }
    return value;
}

function normalizeMissing(value) {
    return blankIfPlaceholder(trim(value)) ?? null;
}

function toIsoDate(value) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
        return value.trim().slice(0, 10);
    }
    return value;
}

function normalizeTier(value) {
    return TIER_ALIASES[String(value ?? '').trim().toUpperCase()] ?? null;
}

function parseColonTier(description) {
    const text = String(description ?? '').trim();
    const idx = text.lastIndexOf(':');
    if (idx < 0) return null;

    const tier = normalizeTier(text.slice(idx + 1));
    if (!tier) return null;

    return {
        stem: text.slice(0, idx).trim(),
        tier,
    };
}

export function stripInlineParentheticals(description) {
    return String(description ?? "")
        .replace(/\s*\([^)]*\)/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

/** Unicode dash/minus variants → ASCII hyphen (HRP rejects non-ASCII in identifiers). */
function normalizeUnicodeDashes(text) {
    return String(text ?? "").replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
}

export function hasNonAsciiHrpIdentifier(value) {
    if (value == null || value === "") return false;
    return /[^\x00-\x7F]/.test(String(value));
}

function findIllegalHrpIdentifierCharacters(value) {
    const chars = [];
    for (const ch of String(value ?? "")) {
        if (ch.charCodeAt(0) <= 0x7F || chars.includes(ch)) continue;
        chars.push(ch);
    }
    return chars;
}

function formatIllegalCharacterForDisplay(ch) {
    if (!ch) return "unknown";
    const code = ch.codePointAt(0);
    if (code == null || code < 0x20 || code === 0x7f) {
        return `U+${(code ?? 0).toString(16).toUpperCase().padStart(4, "0")}`;
    }
    return `"${ch}"`;
}

/** Examples in validation errors; not exhaustive — validation allows any ASCII (0x00–0x7F). */
export const HRP_IDENTIFIER_ASCII_EXAMPLES = "A-Z, a-z, 0-9, spaces, and punctuation such as - : ( ) & _ + = ; ' . ,";

export function formatNonAsciiHrpIdentifierError(label, value) {
    const illegal = findIllegalHrpIdentifierCharacters(value);
    const charDesc = illegal.length === 0
        ? "unknown"
        : illegal.map(formatIllegalCharacterForDisplay).join(", ");
    const noun = illegal.length === 1 ? "character" : "characters";
    return `${label} contains illegal ${noun} ${charDesc} - Use only ASCII characters (${HRP_IDENTIFIER_ASCII_EXAMPLES})`;
}

/** Ensures a string is safe for HRP componentName / componentId fields. */
export function sanitizeHrpAsciiIdentifier(value) {
    if (value == null || value === "") return value;
    let text = normalizeUnicodeDashes(String(value));
    if (!/[^\x00-\x7F]/.test(text)) {
        text = text.replace(/\s+/g, " ").trim();
    } else {
        text = text.normalize("NFKD").replace(/[^\x00-\x7F]/g, "");
        text = text.replace(/\s+/g, " ").trim();
    }
    return text.replace(/\s+-\s*$/g, "").replace(/^\s*-\s+/g, "").trim();
}

/**
 * Maps ASA, renewal, and HRP fee label variants to canonical ASA component names.
 * Level-funded Sold Rates keep spreadsheet labels (no alias rewrite).
 * Keep in sync with packages/backend/src/lambda/billingRateProcessor.js.
 */
export function canonicalizeFeeDescription(description, { planType } = {}) {
    let text = stripInlineParentheticals(description);
    if (!text) return text;

    text = normalizeUnicodeDashes(text).replace(/\s+/g, " ").trim();
    if (planType === 'level-funded') return text;

    if (/^network access fee:\s*.+/i.test(text)) {
        const vendor = text.replace(/^network access fee:\s*/i, "").trim();
        return vendor ? `Network Access Fee: ${vendor}` : text;
    }

    const networkAccessDash = text.match(/^network access\s+-\s*(.+)$/i);
    if (networkAccessDash) {
        const vendor = networkAccessDash[1].trim();
        return vendor ? `Network Access Fee: ${vendor}` : text;
    }

    const networkAccessFor = text.match(/^network access fees?\s+for\s+(.+)$/i);
    if (networkAccessFor) {
        return `Network Access Fee: ${networkAccessFor[1].trim()}`;
    }

    const vendorNetworkAccess = text.match(/^(.+?)\s+network access fees?$/i);
    if (vendorNetworkAccess) {
        const vendor = vendorNetworkAccess[1].trim();
        if (!/^3(?:rd|ʳᵈ)\s*party$/i.test(vendor)) {
            return `Network Access Fee: ${vendor}`;
        }
    }

    if (/^medical\s+administration\b/i.test(text)) {
        return "Medical Admin";
    }

    if (/^archimedes\b/i.test(text) || /^specialty\s*rx\b/i.test(text)) {
        return "Specialty RX";
    }

    if (/^broker(?:\s+(?:fees?|commission|compensation))?$/i.test(text)) {
        return "Broker Fee";
    }

    if (/compliance support/i.test(text)) {
        return "CAA Compliance Support";
    }

    if (/^success fees?$/i.test(text)) {
        return "Success Fee";
    }

    if (/^telemedicine\b/i.test(text)) {
        return "Telemedicine";
    }

    return text;
}

/** Vendor suffix from canonical "Network Access Fee: {Vendor}" labels, if present. */
export function extractNetworkAccessVendor(description) {
    const canonical = canonicalizeFeeDescription(description);
    const candidates = [canonical, description].filter(Boolean);
    for (const candidate of candidates) {
        const match = /network access fee:\s*(.+)$/i.exec(String(candidate).trim());
        if (match) return match[1].trim().toLowerCase();
    }
    return null;
}

function networkAccessVendorFromDescription(description) {
    const canonical = canonicalizeFeeDescription(description);
    const match = /network access fee:\s*(.+)$/i.exec(String(canonical ?? '').trim());
    return match?.[1]?.trim() ?? null;
}

function vendorFromRenewalNotes(notes) {
    const text = String(notes ?? '').trim();
    if (!text || text.length > 50 || /[.;]/.test(text)) return null;
    if (/pass\s*through|credited|quarterly|admin invoice|subject to/i.test(text)) return null;
    return text;
}

/** @deprecated Alias for FEE_SUBCATEGORY_VENDOR_BY_DESCRIPTION. */
const RENEWAL_FEE_VENDOR_BY_DESCRIPTION = FEE_SUBCATEGORY_VENDOR_BY_DESCRIPTION;

/**
 * @deprecated Use resolveFeeSubCategoryVendor — kept for existing imports/tests.
 */
export function resolveRenewalFeeVendor(fee) {
    return resolveFeeSubCategoryVendor(fee);
}

function vendorQualifiedFeeLabelsCompatible(left, right) {
    const leftVendor = extractNetworkAccessVendor(left);
    const rightVendor = extractNetworkAccessVendor(right);
    if (leftVendor && rightVendor && leftVendor !== rightVendor) return false;
    return true;
}

/**
 * Re-applies ASA Centivo network-access filtering on Proceed (covers cached extractions
 * from before the Lambda filter ran, or submissions processed without redeploying).
 * Sold-rate workbooks (LF) omit asaDocumentFlags and list vendors explicitly in the fee matrix.
 */
export function filterAsaNetworkAccessFees(fees, { asaDocumentFlags } = {}) {
    if (!Array.isArray(fees)) return fees ?? [];
    if (asaDocumentFlags == null) return fees;

    const hasCentivoRow = asaDocumentFlags.hasCentivoNetworkAccessRow;
    if (hasCentivoRow === true) return fees;

    const hasOtherNetworkVendor = fees.some((fee) => {
        const vendor = extractNetworkAccessVendor(fee?.description);
        return vendor && vendor !== 'centivo';
    });

    if (hasCentivoRow === false || hasOtherNetworkVendor) {
        return fees.filter((fee) => extractNetworkAccessVendor(fee?.description) !== 'centivo');
    }
    return fees;
}

export function normalizeParsedFee(fee, { planType } = {}) {
    if (!fee) return fee;
    let description = fee.description != null
        ? canonicalizeFeeDescription(fee.description, { planType })
        : fee.description;
    const dashTier = parseDashTier(description);
    if (!dashTier) {
        return description === fee.description ? fee : { ...fee, description };
    }
    const canonicalStem = canonicalizeFeeDescription(dashTier.stem, { planType });
    const canonicalDescription = `${canonicalStem} - ${dashTier.tier}`;
    return {
        ...fee,
        description: canonicalDescription,
        lookup2: fee.lookup2 ?? dashTier.tier,
    };
}

export function parseDashTier(description) {
    const text = normalizeUnicodeDashes(String(description ?? ''));
    const idx = text.lastIndexOf(' - ');
    if (idx < 0) return null;

    const tier = normalizeTier(text.slice(idx + 3));
    if (!tier) return null;

    return {
        stem: text.slice(0, idx).trim(),
        tier,
    };
}

function sheetToRows(sheet, xlsx = XLSX) {
    return xlsx.utils.sheet_to_json(sheet, {
        header: 1,
        blankrows: true,
        defval: null,
        raw: true,
    });
}

function findFirstRow(rows, predicate) {
    return rows.findIndex((row) => hasValue(row) && predicate(row));
}

function findFeeStartRow(rows) {
    const matrixRow = findFirstRow(rows, (row) => hasLabel(row[COL.FEE_CODE], FEE_MATRIX_LABEL));
    if (matrixRow < 0) return 33;

    const feeRow = rows.findIndex((row, index) => (
        index > matrixRow && /^CBC\d+$/i.test(String(row?.[COL.FEE_CODE] ?? '').trim())
    ));
    return feeRow >= 0 ? feeRow : matrixRow + 1;
}

function findRiskPremiumHeaderRow(rows) {
    return findFirstRow(rows, (row) => hasLabel(row[COL.FEE_CODE], RISK_PREMIUM_LABEL));
}

function numericSoldPlanCount(value) {
    if (typeof value === 'string' && value.trim() !== '') {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return Number.isFinite(value) ? value : null;
}

/** A usable "# of sold plans" value. Blank, zero, and error cells are missing. */
function explicitSoldPlanCount(value) {
    const count = numericSoldPlanCount(value);
    if (count == null || count < 1) return null;
    return Math.min(count, MAX_PLAN_COUNT);
}

function detectPlanCount(rows, soldPlanCount) {
    const explicit = explicitSoldPlanCount(soldPlanCount);
    if (explicit != null) return explicit;
    if (Number.isFinite(soldPlanCount)) {
        return Math.min(Math.max(soldPlanCount, 0), MAX_PLAN_COUNT);
    }

    let count = 0;
    for (let col = COL.PLAN_START; col <= COL.PLAN_END; col += 1) {
        const current = normalizeMissing(cell(rows, ROW.CURRENT_PLAN_NAMES, col));
        const fallback = normalizeMissing(cell(rows, ROW.FALLBACK_PLAN_NAMES, col));
        if (current == null && fallback == null) break;
        count += 1;
    }
    return count;
}

function parseGroup(rows) {
    // If every group field in COL.VALUE (col 1) is absent, the data is in the
    // next column instead.
    const GROUP_ROWS = [
        ROW.GROUP_NAME,
        ROW.GROUP_CODE,
        ROW.SOLD_PLAN_COUNT,
        ROW.EFFECTIVE_DATE,
        ROW.BILLING_PERIOD_START,
        ROW.BILLING_PERIOD_END,
    ];
    const col = GROUP_ROWS.every((row) => normalizeMissing(cell(rows, row, COL.VALUE)) == null)
        ? COL.VALUE + 1
        : COL.VALUE;

    return {
        name: normalizeMissing(cell(rows, ROW.GROUP_NAME, col)),
        code: normalizeMissing(cell(rows, ROW.GROUP_CODE, col)),
        soldPlanCount: cell(rows, ROW.SOLD_PLAN_COUNT, col),
        effectiveDate: toIsoDate(cell(rows, ROW.EFFECTIVE_DATE, col)),
        billingPeriodStart: toIsoDate(cell(rows, ROW.BILLING_PERIOD_START, col)),
        billingPeriodEnd: toIsoDate(cell(rows, ROW.BILLING_PERIOD_END, col)),
    };
}

/** Normalizes a "Sold Product" cell to the internal plan-type discriminator. */
function normalizeSoldProductPlanType(value) {
    const text = String(value ?? '').trim().toLowerCase();
    if (!text) return null;
    if (text === 'aso') return 'aso';
    if (text.includes('level') || text === 'lf') return 'level-funded';
    return null;
}

/** Reads the sold product ("ASO" / "Level Funded") from the Sold Rates header, when present. */
function detectSoldProduct(rows) {
    const rowIndex = findFirstRow(rows, (row) => hasLabel(row[COL.FEE_CODE], SOLD_PRODUCT_LABEL));
    if (rowIndex < 0) return null;
    const row = rows[rowIndex] ?? [];
    for (let col = COL.FEE_CODE + 1; col < row.length; col += 1) {
        const value = normalizeMissing(row[col]);
        if (value != null && String(value).trim() !== '') return normalizeSoldProductPlanType(value);
    }
    return null;
}

/** ASO Sold Rates leave the short-name row blank; plan identity lives on the "Plan Names" row. */
function parsePlanNameFallbacks(rows, planCount) {
    const rowIndex = findFirstRow(rows, (row) => hasLabel(row[COL.FEE_DESC], PLAN_NAMES_LABEL));
    if (rowIndex < 0) return [];
    return Array.from({ length: planCount }, (_, index) =>
        normalizeMissing(cell(rows, rowIndex, COL.PLAN_START + index)),
    );
}

function parsePlans(rows, planCount, { planNameFallback = false } = {}) {
    const fallbackNames = planNameFallback ? parsePlanNameFallbacks(rows, planCount) : [];
    return Array.from({ length: planCount }, (_, index) => {
        const col = COL.PLAN_START + index;
        const rawPlanName = normalizeMissing(cell(rows, ROW.CURRENT_PLAN_NAMES, col));
        const primaryPlanName = /plan\s+\d+/i.test(rawPlanName ?? "") ? null : rawPlanName;
        const primaryPlanId = normalizeMissing(cell(rows, ROW.FALLBACK_PLAN_NAMES, col));
        // Only reach for the Plan Names row when the standard rows carry no identity (ASO layout).
        const fallbackName = (primaryPlanName == null && primaryPlanId == null)
            ? (fallbackNames[index] ?? null)
            : null;
        const planName = primaryPlanName ?? fallbackName;
        const planId = primaryPlanId ?? fallbackName;

        return {
            index: index + 1,
            name: planId,
            planName,
            planId,
            planNameSource: planName != null ? 'planName' : planId != null ? 'planId' : null,
            benefitPlanCode: null,
            billPlanCode: null,
            billingRateTotals: {
                eeOnly: cell(rows, ROW.RATE_EE_ONLY, col),
                eesEePlus1: cell(rows, ROW.RATE_EES, col),
                eec: cell(rows, ROW.RATE_EEC, col),
                famEePlus2: cell(rows, ROW.RATE_FAM, col),
            },
        };
    });
}

function parseFees(rows, planCount) {
    const startRow = findFeeStartRow(rows);
    const riskPremiumHeaderRow = findRiskPremiumHeaderRow(rows);
    const fees = [];

    for (let rowIndex = startRow; rowIndex < rows.length; rowIndex += 1) {
        const row = rows[rowIndex];
        if (!hasValue(row) || rowIndex === riskPremiumHeaderRow) continue;

        const rawDescription = trim(cell(rows, rowIndex, COL.FEE_DESC));
        const tier = normalizeTier(cell(rows, rowIndex, COL.RATE_TYPE));
        const fee = {
            rowNumber: rowIndex + 1,
            lookup1: trim(cell(rows, rowIndex, COL.FEE_CODE)),
            lookup2: tier,
            source: null,
            description: tier ? `${rawDescription} - ${tier}` : rawDescription,
            standardFeeAmount: null,
            feeType: trim(cell(rows, rowIndex, COL.FEE_TYPE)),
            planAmounts: Array.from(
                { length: planCount },
                (_, index) => cell(rows, rowIndex, COL.PLAN_START + index),
            ),
            vendor: normalizeMissing(cell(rows, rowIndex, COL.VENDOR)),
            frequency: null,
            notes: null,
        };

        if (fee.description == null && fee.feeType == null && fee.planAmounts.every((amount) => amount == null)) {
            continue;
        }

        if (!fee.feeType && soldRateRowHasAmount(fee)) {
            fee.feeType = DEFAULT_FEE_TYPE;
        }

        fees.push(fee);
    }

    return fees;
}

export function parseSoldRateSheet(workbook, { xlsx = XLSX, planNameFallback = false, soldPlanCount = null } = {}) {
    const sheet = workbook.Sheets[SHEET_NAME];
    if (!sheet) {
        throw Object.assign(
            new Error(`Sheet "${SHEET_NAME}" not found in workbook.`),
            { code: 'SOLD_RATES_SHEET_MISSING' },
        );
    }

    const rows = sheetToRows(sheet, xlsx);
    const group = parseGroup(rows);
    const resolvedPlanCount = explicitSoldPlanCount(soldPlanCount) ?? explicitSoldPlanCount(group.soldPlanCount);
    if (resolvedPlanCount == null) {
        return {
            sheetName: SHEET_NAME,
            soldProduct: detectSoldProduct(rows),
            group,
            plans: [],
            fees: [],
            soldPlanCountMissing: true,
        };
    }

    const plans = parsePlans(rows, detectPlanCount(rows, resolvedPlanCount), { planNameFallback });

    return {
        sheetName: SHEET_NAME,
        soldProduct: detectSoldProduct(rows),
        group: { ...group, soldPlanCount: resolvedPlanCount },
        plans,
        fees: parseFees(rows, plans.length),
        soldPlanCountMissing: false,
    };
}

export function readSoldRateWorkbook(arrayBuffer, { xlsx = XLSX } = {}) {
    return xlsx.read(arrayBuffer, {
        type: 'array',
        cellDates: true,
        sheets: [SHEET_NAME],
    });
}

export function parseSoldRateWorkbook(arrayBuffer, options = {}) {
    return parseSoldRateSheet(readSoldRateWorkbook(arrayBuffer, options), options);
}

/** Normalize a rate amount to a string with exactly two decimal places (e.g. 10 → "10.00"). */
export function formatRateAmountString(raw, { allowZero = false } = {}) {
    if (raw == null || raw === '') return null;
    const amount = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(amount)) return null;
    if (amount === 0 && !allowZero) return null;
    return amount.toFixed(2);
}

function toAmountString(raw) {
    return formatRateAmountString(raw);
}

function resolveGroupId(group) {
    return blankIfPlaceholder(group?.code) ?? blankIfPlaceholder(group?.name) ?? null;
}

/** Component names use the account HCC ID prefix; LF keeps raw fee labels instead of ASA canonical names. */
function resolveComponentNamePrefix(group) {
    return resolveGroupId(group);
}

function resolveExtractedFeeDescription(fee, { planType } = {}) {
    const raw = fee?.description ?? null;
    if (planType === 'level-funded') {
        return stripInlineParentheticals(raw) ?? raw;
    }
    return canonicalizeFeeDescription(raw);
}

function coverageTierMappingKey(coverageTierDesignation) {
    if (!coverageTierDesignation) return null;
    const { tierCount, partnerType } = coverageTierDesignation;
    return `${tierCount}:${partnerType}`;
}

function normalizePartnerType(value) {
    const text = String(value ?? '').trim().toLowerCase();
    if (!text) return null;
    if (text.includes('domestic') || /\bdp\b/.test(text)) return 'domesticPartner';
    if (text.includes('spouse')) return 'spouse';
    return null;
}

function normalizeCoverageTierDesignation(value) {
    if (!value) return null;

    if (typeof value === 'object') {
        const coverageTierSetName = value.coverageTierSetName
            ?? value.coverage_tier_set_name
            ?? value.coverageTierDesignation
            ?? value.coverage_tier_designation;
        const fromSetName = normalizeCoverageTierDesignation(coverageTierSetName);
        if (fromSetName) return fromSetName;

        const tierCount = Number(value.tierCount ?? value.tier_count);
        const partnerType = normalizePartnerType(value.partnerType ?? value.partner_type);

        if (Number.isFinite(tierCount) && partnerType) {
            return { tierCount, partnerType };
        }
    }

    const text = String(value).trim();
    const matchingEntry = Object.entries(COVERAGE_TIER_SET_NAMES)
        .find(([, coverageTierSetName]) => coverageTierSetName.toLowerCase() === text.toLowerCase());

    if (matchingEntry) {
        const [tierCount, partnerType] = matchingEntry[0].split(':');
        return { tierCount: Number(tierCount), partnerType };
    }

    const tierCount = Number(text.match(/\b([234])(?:\s*-\s*tier|\s*tier)?\b/i)?.[1]);
    const partnerType = normalizePartnerType(text);

    if (Number.isFinite(tierCount) && partnerType) {
        return { tierCount, partnerType };
    }

    return null;
}

function resolveCoverageTierDesignation(options = {}) {
    return normalizeCoverageTierDesignation(options.coverageTierDesignation);
}

function firstResponseRow(response) {
    if (Array.isArray(response?.rows)) return response.rows[0] ?? null;
    if (Array.isArray(response)) return response[0] ?? null;
    return response ?? null;
}

export function resolveCoverageTierSetNameFromHrpResponse(response) {
    const row = firstResponseRow(response);
    if (!row) return null;

    return blankIfPlaceholder(
        row.coverageTierSetName
        ?? row.coverage_tier_set_name
        ?? row.coverageTierDesignation
        ?? row.coverage_tier_designation
        ?? (typeof row === 'string' ? row : null),
    ) ?? null;
}

export function resolveCoverageTierDesignationFromHrpResponse(response) {
    return normalizeCoverageTierDesignation(resolveCoverageTierSetNameFromHrpResponse(response));
}

function resolveUploadBenefitPlanHccId(plan) {
    // planId and name are populated from the same spreadsheet row in parsePlans;
    // fall back to name so callers that construct plan objects without an explicit
    // planId field still resolve correctly.
    return blankIfPlaceholder(plan?.planId) ?? blankIfPlaceholder(plan?.name) ?? null;
}

function coverageTierFetchError({ code, message, plan, benefitPlanHccId, ...meta }) {
    return createValidationError(code, {
        message,
        plan: planRef(plan),
        benefitPlanHccId,
        ...meta,
    });
}

function dedupeActiveBenefitPlans(plans) {
    const byHccId = new Map();
    (plans ?? [])
        .filter((p) => p.benefitPlanHccId && p.benefitPlanStatus === 'a')
        .forEach((p) => {
            const existing = byHccId.get(p.benefitPlanHccId);
            const version = p.versionEffDateKey ?? Number.NEGATIVE_INFINITY;
            const existingVersion = existing?.versionEffDateKey ?? Number.NEGATIVE_INFINITY;
            if (!existing || version > existingVersion) byHccId.set(p.benefitPlanHccId, p);
        });
    return [...byHccId.values()];
}

/** Maps a benefit plan HCC ID to the HRP SOAP benefit plan name. */
export function resolveHrpBenefitPlanName(benefitPlanHccId, accountBenefitPlans) {
    if (!benefitPlanHccId) return null;
    const needle = String(benefitPlanHccId).trim();
    const match = dedupeActiveBenefitPlans(accountBenefitPlans).find(
        (p) => String(p?.benefitPlanHccId ?? '').trim() === needle,
    );
    return match?.benefitPlanName ?? null;
}

/**
 * HRP billing rate SOAP expects billingRate.benefitPlan to be the plan name, not
 * the HCC ID. Auto-generated payloads often only have the HCC ID on the wrapper.
 */
export function enrichRatePayloadsForHrpSubmit(ratePayloads, accountBenefitPlans) {
    const plans = dedupeActiveBenefitPlans(accountBenefitPlans);
    if (plans.length === 0) return;

    (ratePayloads ?? []).forEach((wrapper) => {
        const hccId =
            wrapper?.benefitPlan
            ?? wrapper?.plan?.name
            ?? wrapper?.billingRatePayload?.billingRate?.benefitPlan
            ?? null;
        const hrpName = resolveHrpBenefitPlanName(hccId, plans);
        const billingRate = wrapper?.billingRatePayload?.billingRate;
        if (!hrpName || !billingRate) return;
        billingRate.benefitPlan = hrpName;
    });
}

/** Search terms to locate an HRP account when the workbook group name is informal or incomplete. */
export function buildAccountNameSearchCandidates(groupName) {
    const name = String(groupName ?? '').trim();
    if (!name) return [];

    const candidates = new Set([name]);
    candidates.add(name.replace(/\band\b/gi, '&'));
    const withoutPossessive = name.replace(/'s\b/gi, '').trim();
    if (withoutPossessive && withoutPossessive !== name) candidates.add(withoutPossessive);

    const words = name.split(/\s+/).filter((word) => word.length > 2);
    if (words.length >= 2) {
        candidates.add(`${words[0]} ${words[1]}`.replace(/'s$/i, ''));
    }
    if (words[0]) candidates.add(words[0]);

    return [...candidates];
}

function workbookPlanHccIds(parsed) {
    return (parsed.plans ?? [])
        .map((plan) => resolveUploadBenefitPlanHccId(plan))
        .filter(Boolean)
        .map((id) => String(id).trim());
}

function accountCoversWorkbookPlans(accountBenefitPlans, requiredPlanIds) {
    if (requiredPlanIds.length === 0) return true;
    const realIds = new Set(accountBenefitPlans.map((p) => String(p.benefitPlanHccId ?? '').trim()));
    return requiredPlanIds.every((id) => realIds.has(id));
}

async function resolveAccountByGroupName(parsed, fetchAccounts, fetchBenefitPlansByAccountKey) {
    const groupName = blankIfPlaceholder(parsed.group?.name);
    if (!groupName) return null;

    const requiredPlanIds = workbookPlanHccIds(parsed);
    const candidates = buildAccountNameSearchCandidates(groupName);

    for (const candidate of candidates) {
        let rows = [];
        try {
            const res = await fetchAccounts({ accountName: candidate, limit: 10 });
            rows = res?.rows ?? [];
        } catch {
            rows = [];
        }
        if (rows.length === 0) continue;

        if (requiredPlanIds.length === 0) {
            if (rows.length === 1) return rows[0];
            continue;
        }

        for (const row of rows) {
            try {
                const res = await fetchBenefitPlansByAccountKey({ accountKey: row.accountKey });
                const activePlans = dedupeActiveBenefitPlans(res ?? []);
                if (accountCoversWorkbookPlans(activePlans, requiredPlanIds)) {
                    return row;
                }
            } catch {
                /* try next account row */
            }
        }
    }

    return null;
}

/**
 * Account + benefit plan existence checks run before coverage tier set lookups.
 * Two HRP calls: fetchAccounts (account), then fetchBenefitPlansByAccountKey (all plans).
 */
export async function validateBillingRateAccountAndPlans(parsed, {
    fetchAccounts,
    fetchBenefitPlansByAccountKey,
    accountOverride = null,
} = {}) {
    const errors = [];
    if (!parsed || !fetchAccounts || !fetchBenefitPlansByAccountKey) {
        return { errors, account: null, accountBenefitPlans: [] };
    }

    const accountHccId = accountOverride?.accountHccId
        ?? blankIfPlaceholder(parsed.group?.code)
        ?? null;
    const accountNameCandidate = accountOverride?.accountName
        ?? blankIfPlaceholder(parsed.group?.name)
        ?? null;

    let account = accountOverride?.accountKey ? accountOverride : null;
    if (!account && accountHccId) {
        try {
            const res = await fetchAccounts({ accountHccId, limit: 1 });
            account = res?.rows?.[0] ?? null;
        } catch {
            account = null;
        }
    }
    if (!account && accountNameCandidate) {
        account = await resolveAccountByGroupName(parsed, fetchAccounts, fetchBenefitPlansByAccountKey);
    }

    if (!account) {
        errors.push(createValidationError('ACCOUNT_NOT_FOUND', {
            accountHccId: accountHccId ?? accountNameCandidate,
        }));
        return { errors, account: null, accountBenefitPlans: [] };
    }

    let accountBenefitPlans = [];
    try {
        const res = await fetchBenefitPlansByAccountKey({ accountKey: account.accountKey });
        accountBenefitPlans = dedupeActiveBenefitPlans(res ?? []);
    } catch (error) {
        errors.push(createValidationError('BENEFIT_PLANS_FETCH_FAILED', {
            accountHccId: account.accountHccId,
            fetchError: error?.message ?? 'Unknown error',
        }));
        return { errors, account, accountBenefitPlans: [] };
    }

    // Account must have at least as many active plans as the spreadsheet; otherwise re-prompt account.
    const expectedPlanCount = (parsed.plans ?? []).length;
    if (accountBenefitPlans.length < expectedPlanCount) {
        errors.push(createValidationError('ACCOUNT_INSUFFICIENT_PLANS', {
            accountHccId: account.accountHccId,
            expectedPlanCount,
            actualPlanCount: accountBenefitPlans.length,
        }));
        return { errors, account, accountBenefitPlans };
    }

    const realIds = new Set(accountBenefitPlans.map((p) => String(p.benefitPlanHccId ?? '').trim()));
    (parsed.plans ?? []).forEach((plan) => {
        const benefitPlanHccId = resolveUploadBenefitPlanHccId(plan);
        if (!benefitPlanHccId) return;
        if (!realIds.has(String(benefitPlanHccId).trim())) {
            errors.push(createValidationError('BENEFIT_PLAN_NOT_FOUND', {
                plan: planRef(plan),
                benefitPlanHccId,
                planName: blankIfPlaceholder(plan.planName) ?? blankIfPlaceholder(plan.planId) ?? null,
            }));
        }
    });

    return { errors, account, accountBenefitPlans };
}

// Checks whether any generated rate payloads target benefit plans that already
// have a billing rate in HRP. Returns an array of benefitPlanHccId strings for
// plans where getBillingRateByBenefitPlanKey returned a non-empty result.
export async function fetchRateCollisions(
    accountBenefitPlans,
    ratePayloads,
    fetchBillingRateByBenefitPlanKey,
) {
    if (!fetchBillingRateByBenefitPlanKey) return [];

    const getRateBenefitPlanId = (payload) =>
        payload?.benefitPlan ?? payload?.plan?.name ?? null;

    const rateBenefitPlanIds = new Set(
        (ratePayloads ?? []).map(getRateBenefitPlanId).filter((v) => v != null && v !== ""),
    );

    const relevantPlans = (accountBenefitPlans ?? []).filter(
        (plan) =>
            plan?.benefitPlanHccId &&
            plan?.benefitPlanKey &&
            rateBenefitPlanIds.has(plan.benefitPlanHccId),
    );

    if (relevantPlans.length === 0) return [];

    const checks = await Promise.all(
        relevantPlans.map(async (plan) => {
            const response = await fetchBillingRateByBenefitPlanKey({
                benefitPlanKey: plan.benefitPlanKey,
            }).catch(() => null);
            const rows = Array.isArray(response) ? response : [];
            return { plan, hasExisting: rows.length > 0 };
        }),
    );

    return checks
        .filter(({ hasExisting }) => hasExisting)
        .map(({ plan }) => plan.benefitPlanHccId);
}

export async function fetchBillingRateUploadContext(parsed, {
    fetchCoverageTierSetByBenefitPlan,
    coverageTierSetOverrides = {},
} = {}) {
    const byBenefitPlanHccId = {};
    const errors = [];

    if (!parsed || !Array.isArray(parsed.plans) || !fetchCoverageTierSetByBenefitPlan) {
        return { byBenefitPlanHccId, errors };
    }

    const plansByBenefitPlanHccId = new Map();

    parsed.plans.forEach((plan) => {
        const benefitPlanHccId = resolveUploadBenefitPlanHccId(plan);
        if (!benefitPlanHccId) return;

        if (!plansByBenefitPlanHccId.has(benefitPlanHccId)) {
            plansByBenefitPlanHccId.set(benefitPlanHccId, plan);
        }
    });

    await Promise.all(Array.from(plansByBenefitPlanHccId.entries()).map(async ([benefitPlanHccId, plan]) => {
        const overrideName = coverageTierSetOverrides[benefitPlanHccId];
        if (overrideName) {
            byBenefitPlanHccId[benefitPlanHccId] = { coverageTierSetName: overrideName };
            return;
        }

        try {
            const response = await fetchCoverageTierSetByBenefitPlan({ benefitPlanHccId });
            const coverageTierSetName = resolveCoverageTierSetNameFromHrpResponse(response);

            if (!coverageTierSetName) {
                errors.push(coverageTierFetchError({
                    code: 'COVERAGE_TIER_SET_NOT_FOUND',
                    plan,
                    benefitPlanHccId,
                }));
                return;
            }

            byBenefitPlanHccId[benefitPlanHccId] = { coverageTierSetName };
        } catch (error) {
            errors.push(coverageTierFetchError({
                code: 'COVERAGE_TIER_SET_FETCH_FAILED',
                plan,
                benefitPlanHccId,
                fetchError: error?.message ?? 'Unknown error',
            }));
        }
    }));

    return { byBenefitPlanHccId, errors };
}

/**
 * Re-resolves every plan's coverage tier set from HRP and rewrites imported
 * component payloads that disagree. Only for JSON import: an export carries the
 * source environment's tier set names, and a mismatch is otherwise silent
 * because HRP accepts the payload and the component ends up built against the
 * wrong tier structure. Corrections are skipped when a component is shared by
 * plans whose tier sets disagree, since there is no single right answer.
 * Mutates the component payloads in place.
 */
export async function applyHrpCoverageTierSets({
    componentPayloads,
    plans,
    fetchCoverageTierSetByBenefitPlan,
}) {
    const uploadContext = await fetchBillingRateUploadContext(
        { plans: Array.isArray(plans) ? plans : [] },
        { fetchCoverageTierSetByBenefitPlan },
    );

    // Keyed by plan index because that is what a component's own plans array carries;
    // renewal-required components never reach a rate payload, so the component wrapper
    // is the only reliable place to learn which plans a component belongs to.
    const hrpNameByPlanIndex = new Map();
    (Array.isArray(plans) ? plans : []).forEach((plan) => {
        if (plan?.index == null) return;
        const benefitPlanHccId = resolveUploadBenefitPlanHccId(plan);
        const entry = benefitPlanHccId
            ? (uploadContext.byBenefitPlanHccId ?? {})[benefitPlanHccId]
            : null;
        const name = resolveCoverageTierSetNameFromContextEntry(entry);
        if (name) hrpNameByPlanIndex.set(plan.index, name);
    });

    const corrected = [];
    const unverified = (uploadContext.errors ?? []).map((error) => error.message);

    (componentPayloads ?? []).forEach((wrapper) => {
        const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
        const definition = component?.definition;
        if (!definition) return;

        const hrpNames = new Set(
            (wrapper.plans ?? [])
                .map((plan) => hrpNameByPlanIndex.get(plan?.index))
                .filter(Boolean),
        );

        if (hrpNames.size === 0) {
            unverified.push((wrapper.plans ?? []).length === 0
                ? `${component.componentName}: not linked to any plan, so its coverage tier set could not be verified`
                : `${component.componentName}: no HRP coverage tier set could be resolved for its plans`);
            return;
        }
        if (hrpNames.size > 1) {
            unverified.push(`${component.componentName}: plans disagree (${[...hrpNames].join(" / ")})`);
            return;
        }

        const [hrpName] = [...hrpNames];
        if (definition.coverageTierSetName === hrpName) return;
        corrected.push({
            componentName: component.componentName,
            from: definition.coverageTierSetName ?? null,
            to: hrpName,
        });
        definition.coverageTierSetName = hrpName;
    });

    return { uploadContext, corrected, unverified };
}

function resolveCoverageTierNames(soldRateTier, coverageTierDesignation, groupId) {
    if (!soldRateTier) return { coverageTierNames: [] };

    const mappingKey = coverageTierMappingKey(coverageTierDesignation);
    const coverageTierNames = mappingKey
        ? getCoverageTierMappings(groupId)[mappingKey]?.[soldRateTier]
        : null;

    if (!coverageTierNames) {
        return {
            error: {
                code: 'UNSUPPORTED_COVERAGE_TIER_MAPPING',
                message: mappingKey
                    ? `Coverage tier "${soldRateTier}" is not configured for coverage tier designation "${mappingKey}".`
                    : `Coverage tier "${soldRateTier}" cannot be mapped because no coverage tier set was resolved from HRP for this plan.`,
            },
        };
    }

    return { coverageTierNames };
}

function getCoverageTierSetName(coverageTierDesignation) {
    return coverageTierDesignation?.coverageTierSetName
        ?? COVERAGE_TIER_SET_NAMES[coverageTierMappingKey(coverageTierDesignation)];
}

function resolveCoverageTierSetNameFromContextEntry(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') return entry;
    return entry.coverageTierSetName
        ?? entry.coverage_tier_set_name
        ?? entry.coverageTierDesignation
        ?? entry.coverage_tier_designation
        ?? null;
}

function getUploadContext(options = {}) {
    return options.uploadContext ?? {};
}

function resolveCoverageTierByPlan(parsed, options, errors) {
    const fallback = resolveCoverageTierDesignation(options);
    const byBenefitPlanHccId = getUploadContext(options).byBenefitPlanHccId ?? {};

    return new Map(parsed.plans.map((plan) => {
        const benefitPlanHccId = resolveUploadBenefitPlanHccId(plan);
        const contextEntry = benefitPlanHccId ? byBenefitPlanHccId[benefitPlanHccId] : null;

        // No designation rather than a guess. A plan with a benefitPlanHccId that HRP could
        // not resolve also carries a COVERAGE_TIER_SET_* hard stop, which blocks proceed;
        // a plan with no benefitPlanHccId at all is caught by benefit plan validation.
        // Tiered fees on such a plan fail mapping instead of building against a wrong set.
        if (!contextEntry) return [plan.index, fallback];

        const coverageTierSetName = resolveCoverageTierSetNameFromContextEntry(contextEntry);
        const designation = normalizeCoverageTierDesignation(contextEntry);

        if (!designation) {
            errors.push(coverageTierFetchError({
                code: 'UNSUPPORTED_COVERAGE_TIER_SET',
                plan,
                benefitPlanHccId,
                coverageTierSetName,
            }));
            return [plan.index, fallback];
        }

        return [plan.index, {
            ...designation,
            coverageTierSetName: coverageTierSetName ?? getCoverageTierSetName(designation),
        }];
    }));
}

function getCoverageTierDesignationForPlan(plan, coverageTierByPlan) {
    return coverageTierByPlan.get(plan.index) ?? null;
}

export function parseDescriptionTier(description) {
    return parseDashTier(description) ?? parseColonTier(description);
}

function feeTierInfo(fee) {
    const lookupTier = normalizeTier(fee.lookup2);
    const descriptionTier = parseDescriptionTier(fee.description);

    if (lookupTier) {
        return {
            tier: lookupTier,
            stem: descriptionTier?.tier === lookupTier ? descriptionTier.stem : String(fee.description ?? '').trim(),
        };
    }

    return descriptionTier;
}

function resolveFeeType(rawFeeType) {
    const feeType = String(rawFeeType ?? '').trim().toUpperCase() || DEFAULT_FEE_TYPE;
    const mapping = SUPPORTED_FEE_TYPES[feeType];

    if (!mapping) {
        return {
            error: {
                code: 'UNSUPPORTED_FEE_TYPE',
                message: `Unsupported feeType "${rawFeeType}". Supported values: ${Object.keys(SUPPORTED_FEE_TYPES).join(', ')}.`,
            },
        };
    }

    return { mapping };
}

function buildRateSet(amount, tierName, coverageTierNames) {
    const formattedAmount = formatRateAmountString(amount, { allowZero: true }) ?? amount;
    if (!tierName) return { defaultRate: { amount: formattedAmount } };

    return {
        specificRates: coverageTierNames.map((coverageTierName) => ({
            members: { coverageTierName },
            value: { amount: formattedAmount },
        })),
        defaultRate: { amount: '0.00' },
    };
}

function buildComponentName(groupId, feeDescription, planId) {
    return [groupId, feeDescription, planId]
        .map(blankIfPlaceholder)
        .filter(Boolean)
        .join(' ');
}

function componentNameDisambiguationSuffix(fee, attempt) {
    const vendor = String(fee?.vendor ?? '').trim();
    const lookup1 = String(fee?.lookup1 ?? '').trim();
    const rowNumber = fee?.rowNumber;
    if (attempt === 0 && vendor) return vendor;
    if (attempt === 1 && lookup1) return lookup1;
    if (rowNumber != null) return `Row ${rowNumber}`;
    return `Variant ${attempt + 1}`;
}

/** Ensures component names stay unique when LF rows share the same fee description. */
export function buildUniqueComponentName(baseName, fee, usedNames) {
    const trimmedBase = String(baseName ?? '').trim();
    if (!trimmedBase) return trimmedBase;

    if (!usedNames?.has(trimmedBase)) {
        usedNames.add(trimmedBase);
        return trimmedBase;
    }

    for (let attempt = 0; attempt < 8; attempt += 1) {
        const suffix = componentNameDisambiguationSuffix(fee, attempt);
        const candidate = `${trimmedBase} ${suffix}`.trim();
        if (!usedNames.has(candidate)) {
            usedNames.add(candidate);
            return candidate;
        }
    }

    const fallback = `${trimmedBase} Row ${fee?.rowNumber ?? usedNames.size + 1}`;
    usedNames.add(fallback);
    return fallback;
}

function buildComponentDescription(groupId, planId, { amend = false } = {}) {
    const verb = amend ? 'Amending' : 'Adding';
    const id = blankIfPlaceholder(groupId);
    const plan = blankIfPlaceholder(planId);
    const parts = plan
        ? [`${verb} billing rate component for`, id, 'plan', plan]
        : [`${verb} billing rate component for all`, id, 'plans'];

    return parts.filter(Boolean).join(' ');
}

function buildBillingRateDescription(groupId, { amend = false } = {}) {
    const verb = amend ? 'Amending' : 'Adding';
    return [`${verb} billing components for`, blankIfPlaceholder(groupId)].filter(Boolean).join(' ');
}

function buildPayload({ parsed, componentName, description, mapping, rateSet, coverageTierDesignation, vendor }) {
    const sanitizedName = sanitizeHrpAsciiIdentifier(componentName);
    const rateClassifications = {
        componentId: sanitizedName,
        rateSubCategory: {
            codeSetName: RATE_SUBCATEGORY.codeSetName,
            shortName: resolveRateSubCategoryShortName(vendor),
        },
    };

    return {
        asOfDate: parsed.group.effectiveDate ?? undefined,
        maintenanceInfo: { transactionType: 'Create', overrideAction: false },
        maintenanceReasonCode: COMPONENT_CREATE_REASON,
        maintenanceComment: description,
        billingRateComponent: {
            componentName: sanitizedName,
            description,
            definition: {
                rateClassifications,
                coverageTierSetName: getCoverageTierSetName(coverageTierDesignation),
            },
            [mapping.definitionKey]: {
                [mapping.rateSetKey]: rateSet,
            },
        },
    };
}

function resolveBenefitPlanIdentifier(plan) {
    return blankIfPlaceholder(plan?.planId) ?? null;
}

function buildBillingCategory(feeCode) {
    return {
        codeSetName: BILLING_CATEGORY.codeSetName,
        codeEntry: blankIfPlaceholder(feeCode) ?? undefined,
    };
}

function buildBillingRateDefinition(parsed, componentName) {
    return {
        componentReference: {
            lockDate: parsed.group.effectiveDate ?? undefined,
            component: sanitizeHrpAsciiIdentifier(componentName),
        },
    };
}

function buildBillingCategoryRate(category, billingRateDefinitions) {
    return {
        partialIntervalCalculationMethod: 'Wash',
        washDay: 15,
        billingPartyPercentages: [
            {
                billingParty: 'Account',
                billPercentage: 100,
            },
        ],
        billingRateDefinitions,
        billingCategory: category,
    };
}

function billingRateCategoryKey(category) {
    return JSON.stringify(category);
}

function addBillingRateDefinition({
    parsed,
    groupId,
    plan,
    componentName,
    feeCode,
    billingRatePayloadsByPlan,
}) {
    const benefitPlan = resolveBenefitPlanIdentifier(plan);
    const category = buildBillingCategory(feeCode);
    const planKey = benefitPlan;

    if (!billingRatePayloadsByPlan.has(planKey)) {
        billingRatePayloadsByPlan.set(planKey, {
            plan,
            account: groupId,
            accountName: parsed?.group?.name ?? null,
            benefitPlan,
            componentNames: [],
            categories: new Map(),
        });
    }

    const planEntry = billingRatePayloadsByPlan.get(planKey);
    planEntry.componentNames.push(componentName);

    // Billing rate definitions that share a billing category code are grouped
    // under one billing category. When the code is missing we can't group on it
    // (every code-less category would serialize identically), so each component
    // gets its own billing category object instead of collapsing into one.
    const hasCode = category.codeEntry != null && category.codeEntry !== '';
    const categoryKey = hasCode
        ? billingRateCategoryKey(category)
        : `__no-code__:${componentName}`;
    if (!planEntry.categories.has(categoryKey)) {
        planEntry.categories.set(categoryKey, {
            category,
            billingRateDefinitions: [],
        });
    }

    planEntry.categories
        .get(categoryKey)
        .billingRateDefinitions
        .push(buildBillingRateDefinition(parsed, componentName));
}

function buildBillingRatePayloadsFromAccumulator(parsed, groupId, billingRatePayloadsByPlan) {
    return Array.from(billingRatePayloadsByPlan.values()).map((entry) => ({
        plan: planRef(entry.plan),
        account: entry.account,
        accountName: entry.accountName,
        benefitPlan: entry.benefitPlan,
        componentNames: entry.componentNames,
        billingRatePayload: {
            asOfDate: parsed.group.effectiveDate ?? undefined,
            maintenanceInfo: { transactionType: 'Create', overrideAction: false },
            maintenanceReasonCode: RATE_CREATE_REASON,
            maintenanceComment: buildBillingRateDescription(groupId),
            billingRate: {
                billingCategoryRates: Array.from(entry.categories.values()).map((categoryEntry) => (
                    buildBillingCategoryRate(categoryEntry.category, categoryEntry.billingRateDefinitions)
                )),
                benefitPlan: entry.plan.planName ?? entry.benefitPlan,
                account: entry.account,
            },
        },
    }));
}

function planRef(plan) {
    return { index: plan.index, name: plan.name };
}

function analyzeFee(plans, fee, coverageTierByPlan, { forceShared = false } = {}) {
    const perPlan = plans.map((plan) => ({
        plan,
        amount: toAmountString(fee.planAmounts?.[plan.index - 1] ?? fee.standardFeeAmount),
        coverageTierDesignation: getCoverageTierDesignationForPlan(plan, coverageTierByPlan),
    }));

    if (forceShared) {
        const firstAmount = perPlan.find((e) => e.amount != null)?.amount
            ?? toAmountString(fee.standardFeeAmount);
        if (firstAmount == null) return { perPlan, usable: [], shared: false };
        const normalized = perPlan.map((e) => ({ ...e, amount: firstAmount }));
        return { perPlan: normalized, usable: normalized, shared: true };
    }

    const amounts = perPlan.map((entry) => entry.amount);
    const coverageTierMappingKeys = perPlan.map((entry) => coverageTierMappingKey(entry.coverageTierDesignation));
    const shared = amounts.length > 1
        && amounts[0] != null
        && amounts.every((amount) => amount === amounts[0])
        && coverageTierMappingKeys.every((key) => key === coverageTierMappingKeys[0]);

    return {
        perPlan,
        usable: perPlan.filter((entry) => entry.amount != null),
        shared,
    };
}

function addFeePayload(
    parsed,
    groupId,
    fee,
    billingRateComponentPayloads,
    billingRatePayloadsByPlan,
    errors,
    coverageTierByPlan,
    billingCategoryLookup,
    { forceShared = false, planType, usedComponentNames } = {},
) {
    const { perPlan, usable, shared } = analyzeFee(parsed.plans, fee, coverageTierByPlan, { forceShared });
    if (usable.length === 0 && fee.notes !== 'RENEWAL_REQUIRED') return;

    const { mapping, error } = resolveFeeType(fee.feeType);
    const tierName = feeTierInfo(fee)?.tier ?? null;
    const descKey = String(fee.description ?? '').trim().toLowerCase();
    const tierKey = fee.lookup2 ? `${descKey} ${String(fee.lookup2).trim().toLowerCase()}` : null;
    const rateCategory =
        (tierKey ? billingCategoryLookup?.get(tierKey) : null) ??
        billingCategoryLookup?.get(descKey) ??
        null;
    const mappingOptions = { planType };
    const componentNamePrefix = resolveComponentNamePrefix(parsed.group, mappingOptions) ?? groupId;
    const addEntry = ({ amount, plans, planId, coverageTierDesignation }) => {
        const { coverageTierNames, error: coverageTierError } = resolveCoverageTierNames(tierName, coverageTierDesignation, groupId);
        const feeDescription = resolveExtractedFeeDescription(fee, mappingOptions);
        const baseComponentName = buildComponentName(componentNamePrefix, feeDescription, planId);
        const componentName = usedComponentNames
            ? buildUniqueComponentName(baseComponentName, fee, usedComponentNames)
            : baseComponentName;
        const sanitizedComponentName = sanitizeHrpAsciiIdentifier(componentName);
        const description = buildComponentDescription(groupId, planId);
        const entry = {
            shared: plans.length > 1,
            tiered: Boolean(tierName),
            plans: plans.map(planRef),
            feeRow: fee.rowNumber,
            feeDescription,
            feeType: fee.feeType ?? null,
            amount,
            componentName: sanitizedComponentName,
            renewalRequired: fee.notes === 'RENEWAL_REQUIRED',
        };

        if (error) {
            errors.push({ ...entry, ...error });
            return;
        }

        if (coverageTierError) {
            errors.push({ ...entry, ...coverageTierError });
            return;
        }

        billingRateComponentPayloads.push({
            ...entry,
            billingRateComponentPayload: buildPayload({
                parsed,
                componentName: sanitizedComponentName,
                description,
                mapping,
                rateSet: buildRateSet(amount, tierName, coverageTierNames),
                coverageTierDesignation,
                vendor: resolveRateSubCategoryShortName(resolveFeeSubCategoryVendor(fee)),
            }),
        });

        plans.forEach((plan) => {
            const benefitPlan = resolveBenefitPlanIdentifier(plan);
            if (!benefitPlan) {
                errors.push({
                    ...entry,
                    plan: planRef(plan),
                    code: 'MISSING_BENEFIT_PLAN',
                    message: `Missing benefit plan identifier for plan index ${plan.index}.`,
                });
                return;
            }

            if (!entry.renewalRequired) {
                addBillingRateDefinition({
                    parsed,
                    groupId,
                    plan,
                    componentName: sanitizedComponentName,
                    feeCode: fee.lookup1 ?? rateCategory ?? (forceShared ? fee.description : null),
                    billingRatePayloadsByPlan,
                });
            }
        });
    };

    if (fee.notes === 'RENEWAL_REQUIRED') {
        addEntry({
            amount: null,
            plans: perPlan.length > 0 ? perPlan.map((e) => e.plan) : [{}],
            planId: null,
            coverageTierDesignation: perPlan[0]?.coverageTierDesignation ?? null,
        });
        return;
    }

    if (shared) {
        addEntry({
            amount: perPlan[0].amount,
            plans: perPlan.map((entry) => entry.plan),
            coverageTierDesignation: perPlan[0].coverageTierDesignation,
        });
        return;
    }

    usable.forEach(({ plan, amount, coverageTierDesignation }) => {
        addEntry({ amount, plans: [plan], planId: plan.planId, coverageTierDesignation });
    });
}

// LLM extracts aggregate stop loss as "Aggregate Stop Loss - Composite" but HRP only has
// "Aggregate Accommodation" (CBC036). These aliases bridge that gap.
const BILLING_CATEGORY_ALIASES = {
    'aggregate stop loss': 'aggregate accommodation',
};

export function buildBillingCategoryLookup(categories) {
    if (!Array.isArray(categories)) return null;
    const map = new Map();
    categories.forEach((c) => {
        const shortNm = String(c.shortNm ?? '').trim();
        map.set(shortNm.toLowerCase(), c.cdEntryTxt);
        // Also index by "stem - canonical tier" so LLM fees normalized via normalizeParsedFee can match.
        // e.g. "Specific Stop Loss: Employee" → "specific stop loss - ee only" → CBC045
        const colonTier = parseColonTier(shortNm);
        if (colonTier) {
            map.set(`${colonTier.stem} - ${colonTier.tier}`.toLowerCase(), c.cdEntryTxt);
        }
    });
    // Apply aliases: point LLM description variants to the canonical HRP short name's code.
    Object.entries(BILLING_CATEGORY_ALIASES).forEach(([alias, canonical]) => {
        const code = map.get(canonical);
        if (code != null) map.set(alias, code);
    });
    return map;
}

function indexRowsByKey(rows, getKey) {
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const raw = getKey(row);
        if (raw == null) return;
        const key = String(raw).trim().toLowerCase();
        if (key && !map.has(key)) map.set(key, row);
    });
    return map;
}

// Builds the set of componentNames that collide with an existing HRP record.
/** Collision keys are compared case-insensitively: HRP's uniqueness check is
 * case-insensitive (e.g. "Specialty Rx" collides with an existing "Specialty RX"),
 * so a case-only difference must still flip the payload to Amend. */
function normalizeComponentCollisionKey(value) {
    return String(value ?? '').trim().toLowerCase();
}

function collidingComponentNames(collisionRows) {
    const names = new Set();
    (collisionRows ?? []).forEach((row) => {
        [row?.existingName, row?.candidateValue].forEach((value) => {
            const key = normalizeComponentCollisionKey(value);
            if (key) names.add(key);
        });
    });
    return names;
}

/** Collision-check inputs for all component wrappers (step 1 import and pre-submit). */
export function buildComponentCollisionCandidates(componentPayloads) {
    return (componentPayloads ?? [])
        .map((wrapper) => {
            const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
            return {
                componentName: component?.componentName ?? '',
                componentId: component?.definition?.rateClassifications?.componentId ?? '',
            };
        })
        .filter((candidate) => candidate.componentName || candidate.componentId);
}

/**
 * When a component name exists globally in HRP but was not in the account snapshot,
 * upgrade unconfirmed reconciliation rows from "create" to "amend".
 */
export function enrichReconciliationRowsWithGlobalCollisions(reconciliationRows, globalCollisionRows) {
    const collidingNames = collidingComponentNames(globalCollisionRows);
    if (collidingNames.size === 0) return reconciliationRows ?? [];

    return (reconciliationRows ?? []).map((row) => {
        if (row.confirmed || row.skipped) return row;
        const name = row.extractedName;
        if (!name || !collidingNames.has(normalizeComponentCollisionKey(name))) return row;
        if (
            row.hrpComponentName
            && row.matchType
            && row.matchType !== 'extracted-only'
            && !String(row.matchType).includes('global-collision')
        ) {
            return row;
        }
        const nameKey = normalizeComponentCollisionKey(name);
        const collisionRow = (globalCollisionRows ?? []).find(
            (r) => normalizeComponentCollisionKey(r?.existingName) === nameKey
                || normalizeComponentCollisionKey(r?.candidateValue) === nameKey,
        );
        const existingName = collisionRow?.existingName ?? name;
        const baseMatchType = row.matchType ?? 'extracted-only';
        return {
            ...row,
            hrpComponentName: existingName,
            suggestedAction: 'amend',
            matchType: baseMatchType === 'extracted-only'
                ? 'global-collision'
                : `${baseMatchType}+global-collision`,
        };
    });
}

/**
 * Re-checks global component name collisions and flips matching payloads to Amend.
 * Returns cloned payloads so callers can submit without mutating builder state.
 */
export async function applyPreSubmitComponentCollisionAmendments({
    componentPayloads,
    groupId,
    checkBillingRateComponentCollisions,
}) {
    const payloads = structuredClone(componentPayloads ?? []);
    const candidates = buildComponentCollisionCandidates(payloads);
    let collisionRows = [];
    if (candidates.length > 0 && typeof checkBillingRateComponentCollisions === 'function') {
        const res = await checkBillingRateComponentCollisions(candidates).catch(() => null);
        collisionRows = Array.isArray(res?.rows) ? res.rows : [];
    }
    applyComponentCollisionAmendments(payloads, collisionRows, groupId);
    return {
        componentPayloads: payloads,
        componentInputs: payloads.map((wrapper) => wrapper?.billingRateComponentPayload),
        collisionRows,
    };
}

/**
 * Re-checks whether benefit plans already have billing rates in HRP and flips
 * matching payloads to Amend. Returns cloned payloads so callers can submit
 * without mutating builder state.
 */
export async function applyPreSubmitRateCollisionAmendments({
    ratePayloads,
    accountHccId,
    accountKey,
    fetchAccounts,
    fetchBenefitPlansByAccountKey,
    fetchBillingRateByBenefitPlanKey,
}) {
    const payloads = structuredClone(ratePayloads ?? []);
    let resolvedAccountKey = accountKey ?? null;
    if (!resolvedAccountKey && accountHccId && typeof fetchAccounts === 'function') {
        const accounts = await fetchAccounts({ accountHccId, limit: 1 }).catch(() => null);
        resolvedAccountKey = accounts?.rows?.[0]?.accountKey ?? null;
    }

    let accountBenefitPlans = [];
    if (resolvedAccountKey && typeof fetchBenefitPlansByAccountKey === 'function') {
        const res = await fetchBenefitPlansByAccountKey({ accountKey: resolvedAccountKey }).catch(() => []);
        accountBenefitPlans = dedupeActiveBenefitPlans(Array.isArray(res) ? res : []);
    }

    enrichRatePayloadsForHrpSubmit(payloads, accountBenefitPlans);

    const rateCollisions = await fetchRateCollisions(
        accountBenefitPlans,
        payloads,
        fetchBillingRateByBenefitPlanKey,
    );
    applyRateCollisionAmendments(payloads, rateCollisions);
    return {
        ratePayloads: payloads,
        rateInputs: payloads.map((wrapper) => wrapper?.billingRatePayload),
        rateCollisions,
    };
}

// If the wrapper's componentName collides, rewrites its
// maintenanceInfo/reasonCode/comment to reflect an amendment. Mutates in place.
function amendComponentWrapper(wrapper, collidingNames, groupId) {
    const componentName =
        wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
    if (!collidingNames.has(normalizeComponentCollisionKey(componentName))) return;
    const payload = wrapper.billingRateComponentPayload;
    payload.maintenanceInfo = { ...payload.maintenanceInfo, transactionType: 'Amend' };
    payload.maintenanceReasonCode = COMPONENT_AMEND_REASON;
    payload.maintenanceComment = buildComponentDescription(groupId, null, { amend: true });
}

// Applies the correct maintenance fields to a single component payload after a
// live collision check (used when a component is edited and its name changes).
// If the name collides it writes "Amend" values; if not, it resets to "Create"
// values so a payload that was previously an amendment is corrected.
// Mutates the wrapper in place.
export function applyComponentCollisionAmendment(wrapper, collisionRows, groupId) {
    const payload = wrapper?.billingRateComponentPayload;
    if (!payload) return;
    const collidingNames = collidingComponentNames(collisionRows);
    const componentName =
        wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
    if (collidingNames.has(normalizeComponentCollisionKey(componentName))) {
        payload.maintenanceInfo = { ...payload.maintenanceInfo, transactionType: 'Amend' };
        payload.maintenanceReasonCode = COMPONENT_AMEND_REASON;
        payload.maintenanceComment = buildComponentDescription(groupId, null, { amend: true });
    } else {
        payload.maintenanceInfo = { ...payload.maintenanceInfo, transactionType: 'Create' };
        payload.maintenanceReasonCode = COMPONENT_CREATE_REASON;
        payload.maintenanceComment = buildComponentDescription(groupId, null, { amend: false });
    }
}

// For each component payload whose componentName appears in a collision row,
// rewrites the maintenanceInfo/reasonCode/comment to reflect an amendment
// rather than a create. Mutates the payloads in place.
export function applyComponentCollisionAmendments(componentPayloads, collisionRows, groupId) {
    const collidingNames = collidingComponentNames(collisionRows);
    if (collidingNames.size === 0) return;
    (componentPayloads ?? []).forEach((wrapper) =>
        amendComponentWrapper(wrapper, collidingNames, groupId),
    );
}

// For each rate payload whose benefitPlan ID appears in the list of rate
// collisions (plan IDs that already have a billing rate in HRP), rewrites the
// maintenanceInfo/reasonCode/comment to reflect an amendment rather than a
// create. Mutates the payloads in place.
export function applyRateCollisionAmendments(ratePayloads, rateCollisions) {
    const collidingPlanIds = new Set(
        (rateCollisions ?? []).filter((name) => name != null && name !== ''),
    );
    if (collidingPlanIds.size === 0) return;

    (ratePayloads ?? []).forEach((wrapper) => {
        const planId =
            wrapper?.benefitPlan ?? wrapper?.billingRatePayload?.billingRate?.benefitPlan;
        if (!collidingPlanIds.has(planId)) return;
        const payload = wrapper.billingRatePayload;
        payload.maintenanceInfo = { ...payload.maintenanceInfo, transactionType: 'Amend' };
        payload.maintenanceReasonCode = RATE_AMEND_REASON;
        payload.maintenanceComment = buildBillingRateDescription(wrapper.account, { amend: true });
    });
}

// Like applyRateCollisionAmendments, but also resets non-colliding payloads to
// Create. Only for JSON import, where the payloads arrive carrying another
// environment's flags and either stale value fails unresolvably at HRP: a stale
// Create is rejected as already existing, a stale Amend as not found. Callers
// must confirm the collision check actually ran, because fetchRateCollisions
// returns an empty list both for "none found" and "could not check", and the
// latter would downgrade every payload to Create. Mutates the payloads in place.
export function applyRateCollisionTransactionTypes(ratePayloads, rateCollisions) {
    const collidingPlanIds = new Set(
        (rateCollisions ?? []).filter((name) => name != null && name !== ''),
    );

    (ratePayloads ?? []).forEach((wrapper) => {
        const payload = wrapper?.billingRatePayload;
        if (!payload) return;
        const planId = wrapper?.benefitPlan ?? payload?.billingRate?.benefitPlan;
        const amend = collidingPlanIds.has(planId);
        payload.maintenanceInfo = {
            ...payload.maintenanceInfo,
            transactionType: amend ? 'Amend' : 'Create',
        };
        payload.maintenanceReasonCode = amend ? RATE_AMEND_REASON : RATE_CREATE_REASON;
        payload.maintenanceComment = buildBillingRateDescription(wrapper.account, { amend });
    });
}

// Normalizes componentName/componentId (and the matching rate-side
// componentReference.component) the same way the extraction pipeline and the
// manual edit form already do (sanitizeHrpAsciiIdentifier), so a JSON import
// can't carry stray double spaces or leading/trailing whitespace straight
// through to collision checks and step two. Mutates the payloads in place.
export function sanitizeImportedComponentAndRateIdentifiers(componentPayloads, ratePayloads) {
    const renamed = new Map();

    (componentPayloads ?? []).forEach((wrapper) => {
        const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
        if (!component) return;

        const originalName = component.componentName;
        const sanitizedName = sanitizeHrpAsciiIdentifier(originalName);
        if (sanitizedName !== originalName) {
            if (originalName) renamed.set(originalName, sanitizedName);
            component.componentName = sanitizedName;
        }

        const rateClassifications = component.definition?.rateClassifications;
        if (rateClassifications) {
            rateClassifications.componentId = sanitizeHrpAsciiIdentifier(rateClassifications.componentId);
        }
    });

    if (renamed.size === 0) return;

    (ratePayloads ?? []).forEach((wrapper) => {
        const billingCategoryRates = wrapper?.billingRatePayload?.billingRate?.billingCategoryRates ?? [];
        billingCategoryRates.forEach((rate) => {
            (rate?.billingRateDefinitions ?? []).forEach((def) => {
                const current = def?.componentReference?.component;
                if (current != null && renamed.has(current)) {
                    def.componentReference.component = renamed.get(current);
                }
            });
        });
    });
}

// Enriches freshly-built payloads with values resolved from the fetched
// reference lists (fetched in step one). For each component payload, if its
// rate sub category shortName is a substring of a fetched sub category's
// shortNm, the matching codeEntry is written onto the payload. For each rate
// payload's billing categories, if the codeEntry matches a fetched billing
// category, the matching shortName is written onto the payload. Mutates the
// payloads in place.
// Split a string into lowercase word tokens of 3+ characters.
function wordTokens(str) {
    return String(str ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length >= 3);
}

// Dice coefficient on word-token sets. Range [0, 1]; 1 = identical token sets.
// More reliable than character-level Fuse.js for long proper-noun strings because
// it measures word overlap rather than character-level edit distance.
function diceWordScore(a, b) {
    const ta = new Set(wordTokens(a));
    const tb = new Set(wordTokens(b));
    if (ta.size === 0 && tb.size === 0) return 1;
    if (ta.size === 0 || tb.size === 0) return 0;
    let shared = 0;
    ta.forEach((t) => { if (tb.has(t)) shared += 1; });
    return (2 * shared) / (ta.size + tb.size);
}

// Minimum Dice score for sub-category fuzzy lookup only — not used for HRP links.
const FUZZY_DICE_THRESHOLD = 0.15;

export function applyFetchedCategoryLookups(
    componentPayloads,
    ratePayloads,
    { subCategories, billingCategories } = {},
) {
    applyDefaultSubCategoryFallback(componentPayloads);

    const subCategoryRows = (Array.isArray(subCategories) ? subCategories : []).filter(
        (r) => r?.shortNm != null && r?.cdEntryTxt != null,
    );

    (componentPayloads ?? []).forEach((wrapper) => {
        const rateSubCategory =
            wrapper?.billingRateComponentPayload?.billingRateComponent?.definition
                ?.rateClassifications?.rateSubCategory;
        const shortName = rateSubCategory?.shortName;
        if (!rateSubCategory || shortName == null || shortName === '') return;
        const needle = String(shortName).trim().toLowerCase();

        // Match metadata is stored on the wrapper (a frontend-only sidecar) rather
        // than on rateSubCategory, which must stay limited to { codeSetName, shortName }
        // for the HRP payload.
        const exactMatch = subCategoryRows.find(
            (r) => String(r.shortNm).trim().toLowerCase() === needle,
        );
        if (exactMatch?.cdEntryTxt != null) {
            wrapper.subCategoryMatch = {
                codeEntry: exactMatch.cdEntryTxt,
                matchType: 'exact',
                matchedShortName: exactMatch.shortNm,
            };
            return;
        }

        const subMatch = subCategoryRows.find((r) => {
            const hay = String(r.shortNm).trim().toLowerCase();
            return hay.includes(needle) || needle.includes(hay);
        });
        if (subMatch?.cdEntryTxt != null) {
            wrapper.subCategoryMatch = {
                codeEntry: subMatch.cdEntryTxt,
                matchType: 'substring',
                matchedShortName: subMatch.shortNm,
            };
            return;
        }

        // Word-level Dice scoring: ranks candidates by shared word tokens rather
        // than character-level edit distance. This avoids Fuse.js / Bitap
        // misbehaving on long strings (pattern length > 32 chars is silently
        // truncated), which caused unrelated candidates with incidental character
        // overlap to outscore semantically close ones.
        // Additionally, the candidate must share the needle's first meaningful word —
        // the most distinctive part of a vendor name — to prevent common words like
        // "health" from producing false matches (e.g. "Skyward Accident & Health" → "Health Compass").
        const needleFirstToken = wordTokens(shortName)[0];
        let bestRow = null;
        let bestScore = FUZZY_DICE_THRESHOLD;
        for (const row of subCategoryRows) {
            if (needleFirstToken && !wordTokens(row.shortNm).includes(needleFirstToken)) continue;
            const score = diceWordScore(shortName, row.shortNm);
            if (score > bestScore) {
                bestScore = score;
                bestRow = row;
            }
        }
        if (bestRow?.cdEntryTxt != null) {
            wrapper.subCategoryMatch = {
                codeEntry: bestRow.cdEntryTxt,
                matchType: 'fuzzy',
                matchedShortName: bestRow.shortNm,
            };
            return;
        }

        // No match in this environment's sub category rows. Clear any stale
        // subCategoryMatch carried over from an imported file (resolved against
        // the source environment's code table) so the missing match surfaces as
        // a validation error instead of silently reusing a codeEntry that
        // doesn't exist here.
        delete wrapper.subCategoryMatch;
    });

    const billingCategoryByCode = indexRowsByKey(billingCategories, (r) => r?.cdEntryTxt);
    (ratePayloads ?? []).forEach((wrapper) => {
        const billingCategoryRates =
            wrapper?.billingRatePayload?.billingRate?.billingCategoryRates ?? [];
        billingCategoryRates.forEach((rate) => {
            const billingCategory = rate?.billingCategory;
            const codeEntry = billingCategory?.codeEntry;
            if (!billingCategory || codeEntry == null || codeEntry === '') return;
            const matched = billingCategoryByCode.get(String(codeEntry).trim().toLowerCase());
            if (matched?.shortNm != null) {
                wrapper.billingCategoryShortNames = wrapper.billingCategoryShortNames ?? {};
                wrapper.billingCategoryShortNames[codeEntry] = matched.shortNm;
            }
        });
    });
}

export function getUnconfirmedSubCategoryMatches(componentPayloads) {
    return (componentPayloads ?? []).flatMap((wrapper, index) => {
        const shortName =
            wrapper?.billingRateComponentPayload?.billingRateComponent?.definition
                ?.rateClassifications?.rateSubCategory?.shortName;
        const { matchType, confirmed, matchedShortName } = wrapper?.subCategoryMatch ?? {};
        if ((matchType === 'substring' || matchType === 'fuzzy') && !confirmed) {
            return [{ index, extractedName: shortName, matchedName: matchedShortName, matchType }];
        }
        return [];
    });
}

export function mapToBillingComponents(parsed, options = {}) {
    const billingRateComponentPayloads = [];
    const billingRatePayloadsByPlan = new Map();
    const errors = [...(getUploadContext(options).errors ?? [])];

    if (!parsed || !Array.isArray(parsed.plans) || !Array.isArray(parsed.fees)) {
        return { billingRateComponentPayloads, billingRatePayloads: [], errors };
    }

    const groupId = resolveGroupId(parsed.group);
    const coverageTierByPlan = resolveCoverageTierByPlan(parsed, options, errors);
    const billingCategoryLookup = (options.planType === 'aso' || options.forceShared)
        ? buildBillingCategoryLookup(options.billingRateCategories)
        : null;
    const forceShared = Boolean(options.forceShared);
    const usedComponentNames = new Set();
    parsed.fees.forEach((fee) => {
        addFeePayload(
            parsed,
            groupId,
            fee,
            billingRateComponentPayloads,
            billingRatePayloadsByPlan,
            errors,
            coverageTierByPlan,
            billingCategoryLookup,
            { forceShared, planType: options.planType, usedComponentNames },
        );
    });

    const billingRatePayloads = buildBillingRatePayloadsFromAccumulator(
        parsed,
        groupId,
        billingRatePayloadsByPlan,
    );

    return { billingRateComponentPayloads, billingRatePayloads, errors, groupId };
}

// ============================================================================
// Batch submission flow
//
// Pure helpers for submitting a billing rate batch job and polling until
// terminal. The component layer wires these to the real API helpers and a
// real timer-based `delay`; tests inject mocks for all three.
// ============================================================================

export const SUBMIT_BILLING_RATE_BATCH_SK = 'SUBMIT_BILLING_RATE_BATCH';
export const POLL_INTERVAL_MS = 3000;

const arrayLength = (value) => (Array.isArray(value) ? value.length : 0);

const emptySubmitOutcomes = () => ({
    created: 0,
    amended: 0,
    skippedDuplicate: 0,
    failed: 0,
});

export function normalizeSubmitOutcomes(value) {
    if (!value || typeof value !== 'object') return emptySubmitOutcomes();
    return {
        created: value.created ?? 0,
        amended: value.amended ?? 0,
        skippedDuplicate: value.skippedDuplicate ?? 0,
        failed: value.failed ?? 0,
    };
}

/** Human-readable create/update line for post-submit success (e.g. "2 created, 3 updated"). */
export function formatSubmitOutcomeSummary(outcomes, { legacySuccessCount } = {}) {
    const normalized = normalizeSubmitOutcomes(outcomes);
    const parts = [];
    if (normalized.created > 0) parts.push(`${normalized.created} created`);
    if (normalized.amended > 0) parts.push(`${normalized.amended} updated`);
    if (parts.length > 0) return parts.join(', ');
    if (legacySuccessCount != null && legacySuccessCount > 0) {
        return `${legacySuccessCount} submitted successfully`;
    }
    return 'none submitted';
}

export const SUBMIT_LINE_ACTION_LABELS = {
    created: 'Created',
    amended: 'Updated',
    skippedDuplicate: 'Already in HRP',
    failed: 'Failed',
    skippedDependent: 'Skipped',
};

export function formatSubmitLineAction(action) {
    return SUBMIT_LINE_ACTION_LABELS[action] ?? String(action ?? '');
}

function isAmendMaintenanceReason(maintenanceReasonCode) {
    return maintenanceReasonCode?.codeSetName?.includes('Amend') ?? false;
}

export function formatSubmitLineDetail(line) {
    if (line?.error) return line.error;
    if (line?.missingComponents?.length) {
        return `Missing components: ${line.missingComponents.join(', ')}`;
    }
    return '—';
}

/** Builds per-line submit results for UI tables from the batch response (with optional input fallback). */
export function parseJobResponseBody(value) {
    if (value == null) return {};
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return {};
        try {
            const parsed = JSON.parse(trimmed);
            return parsed != null && typeof parsed === 'object' ? parsed : { value: parsed };
        } catch {
            return { raw: value };
        }
    }
    if (typeof value === 'object') return value;
    return { value };
}

export function buildSubmitResultLineItems(responseBody, { components = [], rates = [] } = {}) {
    const body = parseJobResponseBody(responseBody);
    if (Array.isArray(body?.componentResults) || Array.isArray(body?.rateResults)) {
        return {
            componentLines: body?.componentResults ?? [],
            rateLines: body?.rateResults ?? [],
        };
    }

    const componentFailureByIndex = new Map(
        (body?.componentFailures ?? []).map((failure) => [failure.index, failure.error]),
    );
    const rateFailureByIndex = new Map(
        (body?.rateFailures ?? []).map((failure) => [failure.index, failure.error]),
    );
    const skippedRateByIndex = new Map(
        (body?.skippedRates ?? []).map((skipped) => [skipped.index, skipped]),
    );

    const componentLines = (components ?? []).map((input, index) => {
        const name = input?.billingRateComponent?.componentName?.trim() || `Component ${index + 1}`;
        const failureError = componentFailureByIndex.get(index);
        if (failureError) {
            return { index, name, action: 'failed', error: failureError };
        }
        return {
            index,
            name,
            action: isAmendMaintenanceReason(input?.maintenanceReasonCode) ? 'amended' : 'created',
        };
    });

    const rateLines = (rates ?? []).map((input, index) => {
        const benefitPlan = input?.billingRate?.benefitPlan?.trim() || `Rate ${index + 1}`;
        const skipped = skippedRateByIndex.get(index);
        if (skipped) {
            return {
                index,
                benefitPlan,
                action: 'skippedDependent',
                missingComponents: skipped.missingComponents ?? [],
            };
        }
        const failureError = rateFailureByIndex.get(index);
        if (failureError) {
            return { index, benefitPlan, action: 'failed', error: failureError };
        }
        return {
            index,
            benefitPlan,
            action: isAmendMaintenanceReason(input?.maintenanceReasonCode) ? 'amended' : 'created',
        };
    });

    return { componentLines, rateLines };
}

export function buildSubmitResultTableRows(lineItems) {
    return (lineItems ?? []).map((line) => [
        line.name ?? line.benefitPlan ?? '—',
        formatSubmitLineAction(line.action),
        formatSubmitLineDetail(line),
    ]);
}

function resolveSubmitPlanKey(wrapper, rateIndex) {
    return wrapper?.benefitPlan
        ?? wrapper?.plan?.name
        ?? wrapper?.billingRatePayload?.billingRate?.benefitPlan
        ?? `plan-${rateIndex + 1}`;
}

function resolveSubmitPlanLabel(wrapper, rateIndex) {
    return wrapper?.billingRatePayload?.billingRate?.benefitPlan
        ?? wrapper?.plan?.name
        ?? wrapper?.benefitPlan
        ?? `Plan ${rateIndex + 1}`;
}

/**
 * Groups submit line results by benefit plan (HRP-style: plan → billing rate → components).
 */
export function buildSubmitResultByPlan(responseBody, {
    components = [],
    rates = [],
    ratePayloads = [],
    componentPayloads = [],
    billingCategoryRows = [],
} = {}) {
    const { componentLines, rateLines } = buildSubmitResultLineItems(responseBody, { components, rates });

    const componentLineByName = new Map();
    (componentLines ?? []).forEach((line) => {
        if (line?.name) componentLineByName.set(line.name, line);
    });

    const assignedComponentNames = new Set();
    const planGroups = (ratePayloads ?? []).map((wrapper, rateIndex) => {
        const planId = resolveSubmitPlanKey(wrapper, rateIndex);
        const planLabel = resolveSubmitPlanLabel(wrapper, rateIndex);
        const rateLine = rateLines[rateIndex] ?? {
            index: rateIndex,
            benefitPlan: planLabel,
            action: 'failed',
            error: 'Rate outcome missing from submit response',
        };

        const componentNames = Array.isArray(wrapper?.componentNames) && wrapper.componentNames.length > 0
            ? wrapper.componentNames
            : collectComponentNamesFromRateInput(rates[rateIndex]);

        const planComponents = componentNames.map((name) => {
            assignedComponentNames.add(name);
            return componentLineByName.get(name) ?? {
                name,
                action: 'failed',
                error: 'Component outcome missing from submit response',
            };
        });

        return {
            planId,
            planLabel,
            rateLine,
            components: planComponents,
            rateDetails: buildSubmitPlanRateDetails(wrapper, { billingCategoryRows }),
        };
    });

    if (planGroups.length === 0 && rateLines.length > 0) {
        rateLines.forEach((rateLine, rateIndex) => {
            const wrapper = ratePayloads[rateIndex];
            planGroups.push({
                planId: rateLine.benefitPlan ?? `plan-${rateIndex + 1}`,
                planLabel: rateLine.benefitPlan ?? `Plan ${rateIndex + 1}`,
                rateLine,
                components: [],
                rateDetails: wrapper
                    ? buildSubmitPlanRateDetails(wrapper, { billingCategoryRows })
                    : null,
            });
        });
    }

    const orphanComponents = (componentLines ?? []).filter(
        (line) => line?.name && !assignedComponentNames.has(line.name),
    );

    if (orphanComponents.length === 0 && componentPayloads?.length > 0) {
        (componentPayloads ?? []).forEach((wrapper) => {
            const name = wrapper?.componentName
                ?? wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
            if (!name || assignedComponentNames.has(name)) return;
            const line = componentLineByName.get(name);
            if (line) orphanComponents.push(line);
        });
    }

    return { planGroups, orphanComponents };
}

function collectComponentNamesFromRateInput(rateInput) {
    const names = [];
    const categoryRates = rateInput?.billingRate?.billingCategoryRates ?? [];
    for (const category of categoryRates) {
        for (const def of category?.billingRateDefinitions ?? []) {
            const name = def?.componentReference?.component;
            if (name) names.push(name);
        }
    }
    return names;
}

function resolveSubmitPlanCategoryLabel(codeEntry, billingCategoryRows, billingCategoryShortNames) {
    const matchedCategory = (billingCategoryRows ?? []).find((r) => r?.cdEntryTxt === codeEntry);
    const shortNm = matchedCategory?.shortNm ?? billingCategoryShortNames?.[codeEntry] ?? '';
    if (shortNm) return `${shortNm} (${codeEntry})`;
    return codeEntry || '—';
}

/**
 * Read-only rate structure for submit results (effective date, categories, definitions).
 */
export function buildSubmitPlanRateDetails(wrapper, { billingCategoryRows = [] } = {}) {
    const billingRate = wrapper?.billingRatePayload?.billingRate;
    if (!billingRate) return null;

    const billingCategoryShortNames = wrapper?.billingCategoryShortNames ?? {};
    const asOfDate = wrapper?.billingRatePayload?.asOfDate ?? billingRate?.asOfDate ?? null;
    const benefitPlanId = wrapper?.benefitPlan ?? wrapper?.plan?.name ?? billingRate?.benefitPlan ?? null;
    const billingCategoryRates = billingRate?.billingCategoryRates ?? [];

    const categories = billingCategoryRates.map((cat) => {
        const codeEntry = cat?.billingCategory?.codeEntry ?? '';
        const components = (cat?.billingRateDefinitions ?? []).map((def) => ({
            name: def?.componentReference?.component ?? '',
            lockDate: def?.componentReference?.lockDate ?? '',
        }));

        return {
            codeEntry,
            categoryLabel: resolveSubmitPlanCategoryLabel(codeEntry, billingCategoryRows, billingCategoryShortNames),
            partialIntervalCalculationMethod: cat?.partialIntervalCalculationMethod ?? '',
            components,
            billingRateDefinitions: cat?.billingRateDefinitions ?? [],
        };
    });

    return {
        asOfDate,
        benefitPlanId,
        billingCategoryRates,
        categories,
    };
}

export function summarizeResponseBody(responseBody) {
    const body = parseJobResponseBody(responseBody);
    const componentChangedCount = body.componentChangedCount ?? 0;
    const rateChangedCount = body.rateChangedCount ?? 0;
    const componentFailures = arrayLength(body.componentFailures);
    const rateFailures = arrayLength(body.rateFailures);
    const skippedRates = arrayLength(body.skippedRates);
    const componentOutcomes = normalizeSubmitOutcomes(body.componentOutcomes);
    const rateOutcomes = normalizeSubmitOutcomes(body.rateOutcomes);
    const hasOutcomeBreakdown = body.componentOutcomes != null || body.rateOutcomes != null;

    return {
        componentChangedCount,
        rateChangedCount,
        componentOutcomes,
        rateOutcomes,
        hasOutcomeBreakdown,
        componentFailures,
        rateFailures,
        skippedRates,
        hasIssues: componentFailures + rateFailures + skippedRates > 0,
    };
}

/** Keep the local draft after a thrown submit or a completed job with SOAP failures. */
export function shouldPreserveBillingRateDraft({ failed = false, hasIssues = false } = {}) {
    return failed === true || hasIssues === true;
}

/** Payload for POST /audit/submit-billingRate after batch submit completes. */
export function buildBatchSubmitAuditPayload({
    submissionId = null,
    clientName = null,
    jobRef = null,
    responseBody = null,
    summary = null,
    componentCount = null,
    rateCount = null,
    failed = false,
    errorMessage = null,
} = {}) {
    const parsed = parseJobResponseBody(responseBody);
    const resolvedSummary = summary ?? summarizeResponseBody(responseBody);

    return {
        action: failed ? "BATCH_FAILED" : "BATCH_SUBMITTED",
        submissionId,
        clientName,
        status: failed ? "failure" : "success",
        hrpJobPk: jobRef?.pk ?? null,
        hrpJobSk: jobRef?.sk ?? null,
        componentCount,
        rateCount,
        componentChangedCount: resolvedSummary.componentChangedCount ?? parsed.componentChangedCount ?? null,
        rateChangedCount: resolvedSummary.rateChangedCount ?? parsed.rateChangedCount ?? null,
        componentOutcomes: resolvedSummary.componentOutcomes ?? parsed.componentOutcomes ?? null,
        rateOutcomes: resolvedSummary.rateOutcomes ?? parsed.rateOutcomes ?? null,
        componentFailureCount: resolvedSummary.componentFailures ?? null,
        rateFailureCount: resolvedSummary.rateFailures ?? null,
        skippedRateCount: resolvedSummary.skippedRates ?? null,
        ...(errorMessage ? { errorMessage } : {}),
    };
}

/**
 * Submit a billing rate batch job and poll until terminal.
 *
 * @param {Object}   params
 * @param {Function} params.submitBatchJob   async ({components, rates, concurrency?}) => {pk, sk?}
 * @param {Function} params.fetchJobStatus   async ({pk, sk}) => {requestStatus, responseBody?, ...}
 * @param {Function} params.delay            async (ms) => void
 * @param {Array}    params.components       HRP component payloads
 * @param {Array}    params.rates            HRP rate payloads
 * @param {string}   [params.sk]             Defaults to SUBMIT_BILLING_RATE_BATCH_SK
 * @param {number}   [params.pollIntervalMs] Defaults to POLL_INTERVAL_MS
 * @param {number}   [params.concurrency]    Forwarded to submitBatchJob if provided
 * @returns {Promise<{jobRef: {pk, sk}, summary, responseBody}>}
 */
function sanitizeHrpComponentInput(input) {
    if (!input?.billingRateComponent) return input;
    const brc = input.billingRateComponent;
    const sanitizedName = sanitizeHrpAsciiIdentifier(brc.componentName);
    const rateClassifications = brc.definition?.rateClassifications;
    return {
        ...input,
        billingRateComponent: {
            ...brc,
            componentName: sanitizedName,
            ...(brc.definition
                ? {
                    definition: {
                        ...brc.definition,
                        ...(rateClassifications
                            ? {
                                rateClassifications: {
                                    ...rateClassifications,
                                    componentId: sanitizeHrpAsciiIdentifier(
                                        rateClassifications.componentId ?? sanitizedName,
                                    ),
                                },
                            }
                            : {}),
                    },
                }
                : {}),
        },
    };
}

function sanitizeHrpRateInput(input) {
    const billingRate = input?.billingRate;
    if (!billingRate?.billingCategoryRates) return input;
    return {
        ...input,
        billingRate: {
            ...billingRate,
            billingCategoryRates: billingRate.billingCategoryRates.map((cr) => ({
                ...cr,
                billingRateDefinitions: (cr.billingRateDefinitions ?? []).map((def) => {
                    const component = def?.componentReference?.component;
                    if (!component) return def;
                    return {
                        ...def,
                        componentReference: {
                            ...def.componentReference,
                            component: sanitizeHrpAsciiIdentifier(component),
                        },
                    };
                }),
            })),
        },
    };
}

export async function runBillingRateBatchSubmission({
    submitBatchJob,
    fetchJobStatus,
    delay,
    components,
    rates,
    sk = SUBMIT_BILLING_RATE_BATCH_SK,
    pollIntervalMs = POLL_INTERVAL_MS,
    concurrency,
}) {
    const sanitizedComponents = (components ?? []).map(sanitizeHrpComponentInput);
    const sanitizedRates = (rates ?? []).map((rate) => sanitizeHrpRateInput({
        ...rate,
        billingRate: {
            ...rate?.billingRate,
            billingCategoryRates: (rate?.billingRate?.billingCategoryRates ?? []).map((cr) => {
                const { shortName: _shortName, ...billingCategory } = cr?.billingCategory ?? {};
                return { ...cr, billingCategory };
            }),
        },
    }));

    const submitResult = await submitBatchJob({
        components: sanitizedComponents,
        rates: sanitizedRates,
        ...(concurrency != null ? { concurrency } : {}),
    });

    const pk = submitResult?.pk;
    if (!pk) {
        throw new Error('Batch submit response missing pk', { cause: submitResult });
    }
    const resolvedSk = submitResult?.sk ?? sk;
    const jobRef = { pk, sk: resolvedSk };

    let jobStatusRes;
    do {
        await delay(pollIntervalMs);
        jobStatusRes = await fetchJobStatus({ pk, sk: resolvedSk });
    } while (jobStatusRes?.requestStatus === 'PENDING');

    if (jobStatusRes?.requestStatus === 'ERROR') {
        throw new Error(
            `Submission failed before SOAP calls began. Check connector-api logs for pk ${pk}.`,
            { cause: jobStatusRes },
        );
    }

    if (jobStatusRes?.requestStatus !== 'COMPLETED') {
        throw new Error(
            `Unexpected job status: ${jobStatusRes?.requestStatus ?? 'unknown'}`,
            { cause: jobStatusRes },
        );
    }

    const responseBody = parseJobResponseBody(jobStatusRes.responseBody);

    return {
        jobRef,
        responseBody,
        summary: summarizeResponseBody(responseBody),
    };
}

// ─── Renewal Confirmation matching ───────────────────────────────────────────

const FUZZY_MATCH_THRESHOLD = 0.72;

function normalizeForMatch(str) {
    return String(canonicalizeFeeDescription(stripInlineParentheticals(str)) ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function levenshteinDistance(a, b) {
    const m = a.length;
    const n = b.length;
    const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
    for (let j = 1; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            dp[i][j] = a[i - 1] === b[j - 1]
                ? dp[i - 1][j - 1]
                : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
    }
    return dp[m][n];
}

function jaccardWordSimilarity(a, b) {
    const setA = new Set(a.split(" ").filter(Boolean));
    const setB = new Set(b.split(" ").filter(Boolean));
    if (setA.size === 0 && setB.size === 0) return 1;
    if (setA.size === 0 || setB.size === 0) return 0;
    let intersection = 0;
    setA.forEach((w) => { if (setB.has(w)) intersection++; });
    return intersection / (setA.size + setB.size - intersection);
}

function stringSimilarity(a, b) {
    const na = normalizeForMatch(a);
    const nb = normalizeForMatch(b);
    if (na === nb) return 1;
    if (!na || !nb) return 0;
    const levenshtein = 1 - levenshteinDistance(na, nb) / Math.max(na.length, nb.length);
    const jaccard = jaccardWordSimilarity(na, nb);
    // Take the higher of the two — handles both character-level edits (Levenshtein)
    // and word-reordering like "Cigna Network Access Fee" vs "Network Access Fee: Cigna".
    return Math.max(levenshtein, jaccard);
}

export function filterRenewalFees(fees) {
    return (fees ?? []).filter(
        (f) => f?.source === "RENEWAL_CONFIRMATION" || f?.currentAmount != null,
    );
}

function soldRateRowHasAmount(fee) {
    return (fee?.planAmounts ?? []).some((amount) => {
        if (amount == null || amount === '') return false;
        const n = Number(amount);
        return Number.isFinite(n) && n !== 0;
    });
}

function resolveBillableFeeType(fee) {
    const explicit = String(fee?.feeType ?? '').trim().toUpperCase();
    if (explicit) return explicit;
    if (
        soldRateRowHasAmount(fee)
        || (fee?.standardFeeAmount != null && Number(fee.standardFeeAmount) !== 0)
    ) {
        return DEFAULT_FEE_TYPE;
    }
    return '';
}

const BILLABLE_RATE_FEE_TYPES = new Set(["PEPM", "PMPM"]);

/** Renewal Exhibit A rows that are flat allowances or programs, not per-subscriber billing rates. */
const RENEWAL_NON_RATE_FEE_PATTERNS = [
    /wellness\s+allowance/i,
    /case\s+management/i,
];

/** True when a fee row maps to a PEPM/PMPM billing rate component (excludes flat allowances, etc.). */
export function isBillableRateFee(fee) {
    const feeType = resolveBillableFeeType(fee);
    if (!BILLABLE_RATE_FEE_TYPES.has(feeType)) return false;
    const description = stripInlineParentheticals(fee?.description ?? "");
    return !RENEWAL_NON_RATE_FEE_PATTERNS.some((pattern) => pattern.test(description));
}

/** Renewal-document fees that can become billing rate components. */
export function filterBillableRenewalFees(fees) {
    return filterRenewalFees(fees).filter(isBillableRateFee);
}

/** True when the submission has renewal-document fees but no ASA/stoploss primary fees. */
export function isRenewalOnlySubmission(fees) {
    return filterBillablePrimaryFees(fees ?? []).length === 0
        && filterBillableRenewalFees(fees ?? []).length > 0;
}

/** Primary-source fees used to build initial billing components (excludes renewal document rows). */
export function filterPrimaryFees(fees) {
    const renewalSet = new Set(filterRenewalFees(fees ?? []));
    return (fees ?? []).filter((f) => !renewalSet.has(f));
}

/** Primary fees that map to PEPM/PMPM billing rate components. */
export function filterBillablePrimaryFees(fees) {
    return filterPrimaryFees(fees).filter(isBillableRateFee);
}

export function isBillableComponentWrapper(wrapper) {
    return isBillableRateFee({
        description: wrapper?.feeDescription,
        feeType: wrapper?.feeType,
    });
}

export function filterBillableComponentPayloads(componentPayloads) {
    return (componentPayloads ?? []).filter(isBillableComponentWrapper);
}

function primaryFeeDedupKey(fee, { planType } = {}) {
    const desc = String(canonicalizeFeeDescription(fee?.description, { planType }) ?? "").trim().toLowerCase();
    const tier = String(fee?.lookup2 ?? "").trim().toLowerCase();
    const vendor = String(fee?.vendor ?? "").trim().toLowerCase();
    const feeType = String(fee?.feeType ?? "PEPM").trim().toUpperCase();
    return `${desc}|${tier}|${vendor}|${feeType}`;
}

/** Collapse exact duplicate primary fee rows (e.g. duplicate ASA network access vendors). */
export function dedupePrimaryFees(fees, { planType } = {}) {
    const seen = new Map();
    const result = [];

    for (const fee of fees ?? []) {
        const key = primaryFeeDedupKey(fee, { planType });
        const existing = seen.get(key);
        if (!existing) {
            seen.set(key, fee);
            result.push(fee);
            continue;
        }
        if (existing.standardFeeAmount == null && fee.standardFeeAmount != null) {
            const idx = result.indexOf(existing);
            if (idx >= 0) result[idx] = fee;
            seen.set(key, fee);
        }
    }

    return result;
}

function componentMatchLabels(wrapper, accountHccId) {
    const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
    return [
        wrapper?.feeDescription,
        component?.componentName,
        normalizeHrpComponentName(component?.componentName, accountHccId),
    ].filter(Boolean);
}

function componentPlanIds(wrapper) {
    return new Set(
        (wrapper?.plans ?? [])
            .map((plan) => plan?.planId ?? plan?.name ?? plan?.planName)
            .filter(Boolean)
            .map((id) => String(id).trim()),
    );
}

function componentRateSubCategoryVendor(wrapper) {
    return String(
        wrapper?.billingRateComponentPayload?.billingRateComponent?.definition
            ?.rateClassifications?.rateSubCategory?.shortName ?? '',
    ).trim().toLowerCase();
}

function componentsTargetDisjointPlans(wrapperA, wrapperB) {
    const plansA = componentPlanIds(wrapperA);
    const plansB = componentPlanIds(wrapperB);
    if (plansA.size === 0 || plansB.size === 0) return false;
    for (const planId of plansA) {
        if (plansB.has(planId)) return false;
    }
    return true;
}

/** Stable tier key for tiered components (e.g. stop loss EE Only vs FAM / EE+2+). */
function componentTierIdentity(wrapper) {
    if (!wrapper?.tiered) return null;
    const fromDescription = parseDescriptionTier(wrapper?.feeDescription)?.tier;
    if (fromDescription) return fromDescription;
    const componentName = wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
    return parseDescriptionTier(normalizeHrpComponentName(componentName, null))?.tier ?? null;
}

function componentDescriptionStem(wrapper) {
    return parseDescriptionTier(wrapper?.feeDescription)?.stem ?? null;
}

function componentLabelsMatch(wrapperA, wrapperB, accountHccId) {
    const descA = wrapperA?.feeDescription;
    const descB = wrapperB?.feeDescription;
    if (descA && descB) {
        return scoreRenewalFeeAgainstLabel(descA, descB) >= FUZZY_MATCH_THRESHOLD;
    }

    const labelsA = componentMatchLabels(wrapperA, accountHccId);
    const labelsB = componentMatchLabels(wrapperB, accountHccId);
    for (const labelA of labelsA) {
        for (const labelB of labelsB) {
            if (scoreRenewalFeeAgainstLabel(labelA, labelB) >= FUZZY_MATCH_THRESHOLD) {
                return true;
            }
        }
    }
    return false;
}

function pickPreferredComponentWrapper(a, b) {
    const amountA = getWrapperRateAmount(a);
    const amountB = getWrapperRateAmount(b);
    if (amountA != null && amountB == null) return a;
    if (amountB != null && amountA == null) return b;
    if (a?.renewalRequired && !b?.renewalRequired) return b;
    if (b?.renewalRequired && !a?.renewalRequired) return a;
    if (a?.renewalDocumentSource && !b?.renewalDocumentSource) return a;
    if (b?.renewalDocumentSource && !a?.renewalDocumentSource) return b;
    const descA = String(a?.feeDescription ?? "").length;
    const descB = String(b?.feeDescription ?? "").length;
    return descB > descA ? b : a;
}

function mergeComponentWrapperInto(keep, drop) {
    let merged = { ...keep };
    const dropAmount = getWrapperRateAmount(drop);
    if (dropAmount != null && (getWrapperRateAmount(merged) == null || drop.renewalDocumentSource)) {
        merged = applyAmountToComponentWrapper(merged, dropAmount);
    }
    if (drop.feeDescription && (!merged.feeDescription || merged.renewalRequired)) {
        merged = { ...merged, feeDescription: canonicalizeFeeDescription(drop.feeDescription) };
    }
    if (drop.feeType && !merged.feeType) {
        merged = { ...merged, feeType: drop.feeType };
    }
    const dropVendor = componentRateSubCategoryVendor(drop);
    if (dropVendor && !componentRateSubCategoryVendor(merged)) {
        merged = applyVendorToComponentWrapper(merged, dropVendor);
    }
    if (drop.renewalDocumentSource) {
        merged = { ...merged, renewalDocumentSource: true, renewalRequired: false };
    }
    return merged;
}

/** Merge fuzzy-duplicate component payloads (e.g. Medical Admin + Medical Administration). */
export function dedupeFuzzyComponentPayloads(componentPayloads, accountHccId) {
    if (!Array.isArray(componentPayloads) || componentPayloads.length < 2) {
        return [...(componentPayloads ?? [])];
    }

    const used = new Set();
    const result = [];

    for (let i = 0; i < componentPayloads.length; i++) {
        if (used.has(i)) continue;
        let merged = componentPayloads[i];
        used.add(i);

        for (let j = i + 1; j < componentPayloads.length; j++) {
            if (used.has(j)) continue;
            const other = componentPayloads[j];
            if (componentsTargetDisjointPlans(merged, other)) continue;
            const vendorA = componentRateSubCategoryVendor(merged);
            const vendorB = componentRateSubCategoryVendor(other);
            if (vendorA && vendorB && vendorA !== vendorB) continue;
            if (Boolean(merged.tiered) !== Boolean(other.tiered)) continue;
            if (merged.tiered && other.tiered) {
                const stemA = componentDescriptionStem(merged);
                const stemB = componentDescriptionStem(other);
                if (stemA && stemB && stemA.toLowerCase() !== stemB.toLowerCase()) continue;
                const tierA = componentTierIdentity(merged);
                const tierB = componentTierIdentity(other);
                if (tierA && tierB && tierA !== tierB) continue;
            }
            if (!componentLabelsMatch(merged, other, accountHccId)) continue;

            const preferred = pickPreferredComponentWrapper(merged, other);
            const otherWrapper = preferred === merged ? other : merged;
            merged = mergeComponentWrapperInto(preferred, otherWrapper);
            used.add(j);
        }

        result.push(merged);
    }

    return result;
}

/**
 * Merges renewal-document fees into primary components using fuzzy matching.
 * Runs before HRP reconciliation so accounts without existing HRP components still dedupe.
 */
export function applyDocumentReconciliation({
    componentPayloads,
    renewalFees,
    accountHccId,
    groupId,
    plans = [],
    effectiveDate = null,
    skipFuzzyDedupe = false,
}) {
    if (!Array.isArray(componentPayloads)) return componentPayloads;

    applyRenewalDocumentOverrides({
        componentPayloads,
        renewalFees: renewalFees ?? [],
        hrpComponents: [],
        accountHccId,
    });

    if (!skipFuzzyDedupe) {
        const deduped = dedupeFuzzyComponentPayloads(componentPayloads, accountHccId);
        componentPayloads.length = 0;
        componentPayloads.push(...deduped);
    }

    const assignments = assignRenewalFees(
        renewalFees ?? [],
        componentPayloads,
        [],
        accountHccId,
    );

    for (const assignment of assignments) {
        if (assignment.target !== "unmatched") continue;
        const fee = assignment.fee;
        if (!isBillableRateFee(fee)) continue;
        const amount = fee?.standardFeeAmount ?? fee?.currentAmount;
        if (amount == null) continue;

        componentPayloads.push({
            ...buildComponentWrapperFromRenewalFee(fee, {
                groupId,
                plans,
                asOfDate: effectiveDate,
            }),
            renewalDocumentSource: true,
        });
    }

    const billable = filterBillableComponentPayloads(componentPayloads);
    componentPayloads.length = 0;
    componentPayloads.push(...billable);

    return componentPayloads;
}

function buildEmptyRatePayloadForPlan(plan, { groupId, groupName, effectiveDate }) {
    const benefitPlan = resolveBenefitPlanIdentifier(plan);
    return {
        plan: planRef(plan),
        account: groupId,
        accountName: groupName ?? null,
        benefitPlan,
        componentNames: [],
        billingRatePayload: {
            asOfDate: effectiveDate ?? undefined,
            maintenanceInfo: { transactionType: 'Create', overrideAction: false },
            maintenanceReasonCode: RATE_CREATE_REASON,
            maintenanceComment: buildBillingRateDescription(groupId),
            billingRate: {
                billingCategoryRates: [],
                benefitPlan: plan.planName ?? benefitPlan,
                account: groupId,
            },
        },
    };
}

function billingCategoryCodeKey(category) {
    const codeEntry = category?.billingCategory?.codeEntry;
    return codeEntry != null && codeEntry !== '' ? String(codeEntry) : null;
}

function dedupeBillingCategoryDefinitions(category) {
    const seen = new Set();
    const billingRateDefinitions = [];
    for (const def of category?.billingRateDefinitions ?? []) {
        const name = def?.componentReference?.component;
        if (!name || seen.has(name)) continue;
        seen.add(name);
        billingRateDefinitions.push(def);
    }
    return billingRateDefinitions.length === (category?.billingRateDefinitions?.length ?? 0)
        ? category
        : { ...category, billingRateDefinitions };
}

/** Collapse duplicate billing categories that share a CBC code after reference renames. */
function mergeBillingCategoryRatesByCode(categories) {
    const merged = [];
    const indexByCode = new Map();

    for (const category of categories ?? []) {
        const deduped = dedupeBillingCategoryDefinitions(category);
        if ((deduped?.billingRateDefinitions?.length ?? 0) === 0) continue;

        const codeKey = billingCategoryCodeKey(deduped);
        if (!codeKey) {
            merged.push(deduped);
            continue;
        }

        const existingIndex = indexByCode.get(codeKey);
        if (existingIndex == null) {
            indexByCode.set(codeKey, merged.length);
            merged.push(deduped);
            continue;
        }

        const existing = merged[existingIndex];
        merged[existingIndex] = dedupeBillingCategoryDefinitions({
            ...existing,
            billingRateDefinitions: [
                ...(existing?.billingRateDefinitions ?? []),
                ...(deduped?.billingRateDefinitions ?? []),
            ],
        });
    }

    return merged;
}

function replaceComponentReferencesInBillingCategories(categories, fromNames, toName) {
    if (!toName || fromNames.size === 0) return categories ?? [];

    let touched = false;
    const updated = (categories ?? []).map((category) => ({
        ...category,
        billingRateDefinitions: (category?.billingRateDefinitions ?? []).map((def) => {
            const current = def?.componentReference?.component;
            if (!current || !fromNames.has(current) || current === toName) return def;
            touched = true;
            return {
                ...def,
                componentReference: {
                    ...def.componentReference,
                    component: toName,
                },
            };
        }),
    }));

    return touched ? mergeBillingCategoryRatesByCode(updated) : categories ?? [];
}

/** Rewrites billing-rate component references when reconciliation maps an extracted name to HRP. */
export function renameComponentReferencesInRatePayloads(ratePayloads, fromName, toName) {
    if (!Array.isArray(ratePayloads) || !fromName || !toName || fromName === toName) {
        return ratePayloads;
    }

    return ratePayloads.map((wrapper) => {
        const billingRate = wrapper?.billingRatePayload?.billingRate;
        if (!billingRate) return wrapper;

        const fromNames = new Set([fromName]);
        const updatedCats = replaceComponentReferencesInBillingCategories(
            billingRate?.billingCategoryRates,
            fromNames,
            toName,
        );
        const catsChanged = updatedCats !== billingRate?.billingCategoryRates;

        let updatedComponentNames = wrapper?.componentNames;
        if (Array.isArray(updatedComponentNames) && updatedComponentNames.includes(fromName)) {
            updatedComponentNames = [...new Set(
                updatedComponentNames.map((name) => (name === fromName ? toName : name)),
            )];
        }

        if (!catsChanged && updatedComponentNames === wrapper?.componentNames) return wrapper;

        return {
            ...wrapper,
            componentNames: updatedComponentNames,
            billingRatePayload: {
                ...wrapper.billingRatePayload,
                billingRate: {
                    ...billingRate,
                    billingCategoryRates: updatedCats,
                },
            },
        };
    });
}

/** Inject one resolved component into rate payloads that belong to the component's plans. */
function injectComponentDefinitionIntoRatePayloads(ratePayloads, componentWrapper, { fees = [], billingCategoryRows = [] } = {}) {
    if (!Array.isArray(ratePayloads) || !componentWrapper) return ratePayloads ?? [];

    const componentName =
        componentWrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
    if (!componentName) return ratePayloads;

    const legacyNames = new Set(
        [componentWrapper?.componentName, componentName].filter(Boolean),
    );

    const componentPlanNames = new Set(
        (componentWrapper?.plans ?? []).map((p) => p?.name).filter(Boolean),
    );
    if (componentPlanNames.size === 0) return ratePayloads;

    const fee = (fees ?? []).find((f) => f?.rowNumber === componentWrapper?.feeRow);
    const descKey = String(fee?.description ?? componentWrapper?.feeDescription ?? '').trim().toLowerCase();
    const tierKey = fee?.lookup2 ? `${descKey} ${String(fee.lookup2).trim().toLowerCase()}` : null;
    const categoryLookup = buildBillingCategoryLookup(billingCategoryRows);
    const codeEntry = (tierKey ? categoryLookup?.get(tierKey) : null) ?? categoryLookup?.get(descKey) ?? undefined;
    const shortNm = codeEntry
        ? ((billingCategoryRows ?? []).find((r) => r?.cdEntryTxt === codeEntry)?.shortNm ?? null)
        : null;

    return ratePayloads.map((wrapper) => {
        const planName = wrapper?.plan?.name ?? wrapper?.benefitPlan;
        if (!componentPlanNames.has(planName)) return wrapper;

        const billingRate = wrapper?.billingRatePayload?.billingRate;
        let cats = replaceComponentReferencesInBillingCategories(
            billingRate?.billingCategoryRates,
            legacyNames,
            componentName,
        );

        const alreadyReferenced = (cats ?? []).some((cat) =>
            (cat?.billingRateDefinitions ?? []).some(
                (def) => def?.componentReference?.component === componentName,
            ),
        );
        if (alreadyReferenced) {
            if (cats === billingRate?.billingCategoryRates) return wrapper;
            return {
                ...wrapper,
                billingRatePayload: {
                    ...wrapper.billingRatePayload,
                    billingRate: {
                        ...billingRate,
                        billingCategoryRates: cats,
                    },
                },
            };
        }

        const lockDate = (cats ?? [])
            .flatMap((cat) => cat?.billingRateDefinitions ?? [])
            .find((def) => def?.componentReference?.lockDate)
            ?.componentReference?.lockDate ?? wrapper?.billingRatePayload?.asOfDate ?? undefined;

        const newCat = {
            partialIntervalCalculationMethod: 'Wash',
            washDay: 15,
            billingPartyPercentages: [{ billingParty: 'Account', billPercentage: 100 }],
            billingRateDefinitions: [{ componentReference: { lockDate, component: componentName } }],
            billingCategory: { codeSetName: 'BillingCategory', codeEntry },
        };

        const updatedShortNames = codeEntry && shortNm
            ? { ...wrapper.billingCategoryShortNames, [codeEntry]: shortNm }
            : wrapper.billingCategoryShortNames;

        const legacyComponentNames = (wrapper?.componentNames ?? []).filter(
            (name) => name && name !== componentName && legacyNames.has(name),
        );
        const nextComponentNames = Array.isArray(wrapper.componentNames)
            ? [...new Set([...wrapper.componentNames.filter((name) => !legacyComponentNames.includes(name)), componentName])]
            : [componentName];

        return {
            ...wrapper,
            componentNames: nextComponentNames,
            billingCategoryShortNames: updatedShortNames,
            billingRatePayload: {
                ...wrapper.billingRatePayload,
                billingRate: {
                    ...billingRate,
                    billingCategoryRates: mergeBillingCategoryRatesByCode([...(cats ?? []), newCat]),
                },
            },
        };
    });
}

/**
 * Builds billing rate payloads when renewal-only reconciliation created components
 * without running mapToBillingComponents on primary fees (e.g. renewal-only upload).
 */
export function syncBillingRatePayloadsFromComponents(
    ratePayloads,
    componentPayloads,
    {
        plans = [],
        groupId,
        groupName = null,
        effectiveDate = null,
        fees = [],
        billingCategoryRows = [],
    } = {},
) {
    const resolvedComponents = (componentPayloads ?? []).filter((wrapper) => wrapper?.renewalRequired !== true);
    if (resolvedComponents.length === 0) return ratePayloads ?? [];

    let rates = [...(ratePayloads ?? [])];
    if (rates.length === 0 && (plans ?? []).length > 0) {
        rates = (plans ?? []).map((plan) => buildEmptyRatePayloadForPlan(plan, {
            groupId,
            groupName,
            effectiveDate,
        }));
    }

    for (const wrapper of resolvedComponents) {
        rates = injectComponentDefinitionIntoRatePayloads(rates, wrapper, { fees, billingCategoryRows });
    }

    return rates;
}

/** Resolve account HCC ID from imported or in-progress builder payloads. */
export function extractBillingRateAccountHccId({ ratePayloads, componentPayloads } = {}) {
    return ratePayloads?.[0]?.account
        ?? componentPayloads?.[0]?.billingRateComponentPayload?.billingRateComponent?.componentName?.split?.(" ")?.[0]
        ?? null;
}

/** Normalize an extracted or parsed date value to YYYY-MM-DD for HRP queries. */
export function normalizeBillingRateAsOfDate(value) {
    if (value == null || value === "") return null;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
        return trimmed;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
    }
    return null;
}

/** Default as-of date for HRP lookups when extraction did not capture one. */
export function getDefaultBillingRateAsOfDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/** Promote top-level renewal period fields onto group when the LLM returned them separately. */
export function promoteRenewalDatesToGroup(parsed) {
    if (!parsed || typeof parsed !== "object") return parsed;
    const renewalStart = normalizeBillingRateAsOfDate(parsed.renewalPeriodStart);
    if (!renewalStart) return parsed;
    const group = parsed.group ?? {};
    const renewalEnd = normalizeBillingRateAsOfDate(parsed.renewalPeriodEnd);
    return {
        ...parsed,
        group: {
            ...group,
            effectiveDate: group.effectiveDate ?? renewalStart,
            billingPeriodStart: group.billingPeriodStart ?? renewalStart,
            billingPeriodEnd: group.billingPeriodEnd ?? renewalEnd ?? group.billingPeriodEnd,
        },
    };
}

/** Stamp resolved as-of date onto component payloads missing one (required for HRP lookups). */
export function stampAsOfDateOnComponentPayloads(componentPayloads, asOfDate) {
    const normalized = normalizeBillingRateAsOfDate(asOfDate);
    if (!normalized || !Array.isArray(componentPayloads)) return;
    for (const wrapper of componentPayloads) {
        const payload = wrapper?.billingRateComponentPayload;
        if (!payload || payload.asOfDate) continue;
        payload.asOfDate = normalized;
    }
}

/** Resolve the as-of date for HRP billing rate lookups from payloads or parsed group metadata. */
export function extractBillingRateAsOfDate({
    componentPayloads,
    ratePayloads,
    group,
    parsed,
    asOfDateHint,
} = {}) {
    const promoted = promoteRenewalDatesToGroup(parsed);
    const promotedGroup = promoted?.group ?? group;
    const candidates = [
        asOfDateHint,
        componentPayloads?.[0]?.billingRateComponentPayload?.asOfDate,
        ratePayloads?.[0]?.billingRatePayload?.asOfDate,
        group?.effectiveDate,
        group?.billingPeriodStart,
        promotedGroup?.effectiveDate,
        promotedGroup?.billingPeriodStart,
        promoted?.renewalPeriodStart,
        parsed?.renewalPeriodStart,
        parsed?.group?.effectiveDate,
        parsed?.group?.billingPeriodStart,
    ];
    for (const candidate of candidates) {
        const normalized = normalizeBillingRateAsOfDate(candidate);
        if (normalized) return normalized;
    }
    return null;
}

/** Resolve as-of date from merged extraction, falling back to per-file extracted data on the submission. */
export async function resolveBillingRateAsOfDate({
    componentPayloads,
    ratePayloads,
    group,
    parsed,
    submissionSk,
    fetchSubmission,
} = {}) {
    const promotedParsed = promoteRenewalDatesToGroup(parsed);
    let asOfDate = extractBillingRateAsOfDate({
        componentPayloads,
        ratePayloads,
        group: promotedParsed?.group ?? group,
        parsed: promotedParsed,
    });
    if (asOfDate) return asOfDate;
    if (!submissionSk || !fetchSubmission) return getDefaultBillingRateAsOfDate();

    const record = await fetchSubmission(submissionSk).catch(() => null);
    asOfDate = extractBillingRateAsOfDate({
        parsed: promoteRenewalDatesToGroup(record?.extractedData),
        group: record?.extractedData?.group,
    });
    if (asOfDate) return asOfDate;

    for (const file of record?.files ?? []) {
        asOfDate = extractBillingRateAsOfDate({
            parsed: promoteRenewalDatesToGroup(file.extractedData),
            group: file.extractedData?.group,
        });
        if (asOfDate) return asOfDate;
    }
    return getDefaultBillingRateAsOfDate();
}

function applyAmountToRateSet(rateSet, amountStr, tiered) {
    const formattedAmount = formatRateAmountString(amountStr, { allowZero: true }) ?? amountStr;
    if (tiered && Array.isArray(rateSet?.specificRates) && rateSet.specificRates.length > 0) {
        return {
            ...rateSet,
            specificRates: rateSet.specificRates.map((sr) => ({
                ...sr,
                value: { ...(sr.value ?? {}), amount: formattedAmount },
            })),
            defaultRate: { amount: "0.00" },
        };
    }
    return { ...rateSet, defaultRate: { amount: formattedAmount } };
}

function applyVendorToComponentWrapper(wrapper, vendor) {
    const trimmed = String(vendor ?? '').trim();
    if (!trimmed || componentRateSubCategoryVendor(wrapper)) return wrapper;

    const payload = wrapper?.billingRateComponentPayload;
    const component = payload?.billingRateComponent;
    if (!component) return wrapper;

    return {
        ...wrapper,
        billingRateComponentPayload: {
            ...payload,
            billingRateComponent: {
                ...component,
                definition: {
                    ...component.definition,
                    rateClassifications: {
                        ...component.definition?.rateClassifications,
                        rateSubCategory: {
                            codeSetName: RATE_SUBCATEGORY.codeSetName,
                            shortName: trimmed,
                        },
                    },
                },
            },
        },
    };
}

function applyAmountToComponentWrapper(wrapper, amount) {
    const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
    if (!component || amount == null) return wrapper;

    const amountStr = formatRateAmountString(amount, { allowZero: true }) ?? String(amount);
    const tiered = Boolean(wrapper?.tiered);
    const rateSet = component.definition?.billingRateDefinition?.rateSet
        ?? component.definition?.perSubscriptionPerMonthBillingRateDefinition?.rateSet
        ?? component.definition?.perMemberPerMonthBillingRateDefinition?.rateSet;

    let updatedComponent = component;
    if (rateSet) {
        const nextRateSet = applyAmountToRateSet(rateSet, amountStr, tiered);
        updatedComponent = {
            ...component,
            definition: {
                ...component.definition,
                ...(component.definition?.billingRateDefinition ? {
                    billingRateDefinition: {
                        ...component.definition.billingRateDefinition,
                        rateSet: nextRateSet,
                    },
                } : component.definition?.perSubscriptionPerMonthBillingRateDefinition ? {
                    perSubscriptionPerMonthBillingRateDefinition: {
                        ...component.definition.perSubscriptionPerMonthBillingRateDefinition,
                        rateSet: nextRateSet,
                    },
                } : {
                    perMemberPerMonthBillingRateDefinition: {
                        ...component.definition.perMemberPerMonthBillingRateDefinition,
                        rateSet: nextRateSet,
                    },
                }),
            },
        };
    } else if (component.perSubscriptionPerMonthBillingRateDefinition?.pspmRateSet) {
        updatedComponent = {
            ...component,
            perSubscriptionPerMonthBillingRateDefinition: {
                ...component.perSubscriptionPerMonthBillingRateDefinition,
                pspmRateSet: applyAmountToRateSet(
                    component.perSubscriptionPerMonthBillingRateDefinition.pspmRateSet,
                    amountStr,
                    tiered,
                ),
            },
        };
    } else if (component.perMemberPerMonthBillingRateDefinition?.pmpmRateSet) {
        updatedComponent = {
            ...component,
            perMemberPerMonthBillingRateDefinition: {
                ...component.perMemberPerMonthBillingRateDefinition,
                pmpmRateSet: applyAmountToRateSet(
                    component.perMemberPerMonthBillingRateDefinition.pmpmRateSet,
                    amountStr,
                    tiered,
                ),
            },
        };
    }

    return {
        ...wrapper,
        amount: amountStr,
        renewalRequired: false,
        billingRateComponentPayload: {
            ...wrapper.billingRateComponentPayload,
            billingRateComponent: updatedComponent,
        },
    };
}

function getWrapperRateAmount(wrapper) {
    const c = wrapper?.billingRateComponentPayload?.billingRateComponent;
    const rateSet =
        c?.perSubscriptionPerMonthBillingRateDefinition?.pspmRateSet
        ?? c?.perMemberPerMonthBillingRateDefinition?.pmpmRateSet;
    const defaultAmount = rateSet?.defaultRate?.amount;
    const wrapperAmount = wrapper?.amount;
    const pick = (value) => (value != null && value !== "" ? String(value) : null);

    const defaultNum = defaultAmount != null && defaultAmount !== ""
        ? Number(defaultAmount)
        : NaN;
    if (!Number.isNaN(defaultNum) && defaultNum !== 0) return pick(defaultAmount);

    return pick(wrapperAmount) ?? pick(defaultAmount);
}

function normalizeHrpComponentName(name, accountHccId) {
    const prefix = accountHccId ? new RegExp(`^${String(accountHccId).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i") : null;
    return prefix ? String(name ?? "").replace(prefix, "").trim() : String(name ?? "").trim();
}

/** Renewal fee names often expand abbreviations used in ASA/stoploss (Admin → Administration). */
function renewalWordTokensMatch(tokenA, tokenB) {
    if (tokenA === tokenB) return true;
    const minPrefixLen = 4;
    if (tokenA.length >= minPrefixLen && tokenB.startsWith(tokenA)) return true;
    if (tokenB.length >= minPrefixLen && tokenA.startsWith(tokenB)) return true;
    return false;
}

function renewalDiceWordScore(a, b) {
    const ta = wordTokens(a);
    const tb = wordTokens(b);
    if (ta.length === 0 && tb.length === 0) return 1;
    if (ta.length === 0 || tb.length === 0) return 0;
    let shared = 0;
    for (const tokenA of ta) {
        if (tb.some((tokenB) => renewalWordTokensMatch(tokenA, tokenB))) {
            shared += 1;
        }
    }
    return (2 * shared) / (ta.length + tb.length);
}

function scoreRenewalFeeAgainstLabel(feeDescription, label) {
    if (!feeDescription || !label) return 0;
    if (!vendorQualifiedFeeLabelsCompatible(feeDescription, label)) return 0;
    const canonicalFee = canonicalizeFeeDescription(feeDescription);
    const canonicalLabel = canonicalizeFeeDescription(label);
    if (canonicalFee && canonicalLabel && canonicalFee.toLowerCase() === canonicalLabel.toLowerCase()) {
        return 1;
    }
    return Math.max(
        stringSimilarity(canonicalFee, canonicalLabel),
        renewalDiceWordScore(canonicalFee, canonicalLabel),
    );
}

function scoreFeeLabelsForHrpMatch(labelA, labelB) {
    if (!labelA || !labelB) return 0;
    if (!vendorQualifiedFeeLabelsCompatible(labelA, labelB)) return 0;
    return Math.max(
        scoreRenewalFeeAgainstLabel(labelA, labelB),
        diceWordScore(labelA, labelB),
    );
}

function componentStemLabel(label) {
    const parsed = parseDashTier(stripInlineParentheticals(label ?? ""));
    return parsed?.stem ?? null;
}

/** Non-tiered renewal fees must not attach to tiered stoploss rows (match HRP instead). */
function renewalExtractedMatchAllowed(feeDescription, wrapper, score) {
    if (score < FUZZY_MATCH_THRESHOLD) return false;
    if (!wrapper?.tiered) return true;
    const normalized = stripInlineParentheticals(feeDescription ?? "");
    return parseDashTier(normalized) != null;
}

function findBestRenewalComponentMatch(feeDescription, componentPayloads, usedIndexes, accountHccId) {
    let bestIndex = null;
    let bestScore = 0;
    let bestName = null;

    (componentPayloads ?? []).forEach((wrapper, index) => {
        if (usedIndexes.has(index)) return;
        const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
        const labels = [
            wrapper?.feeDescription,
            component?.componentName,
            normalizeHrpComponentName(component?.componentName, accountHccId),
        ].filter(Boolean);

        for (const label of labels) {
            const score = scoreRenewalFeeAgainstLabel(feeDescription, label);
            if (score > bestScore) {
                bestScore = score;
                bestIndex = index;
                bestName = wrapper?.feeDescription ?? label;
            }
        }
    });

    return { index: bestIndex, score: bestScore, name: bestName };
}

function findBestRenewalHrpMatch(feeDescription, hrpComponents, usedHrpKeys, accountHccId) {
    let bestKey = null;
    let bestScore = 0;
    let bestComponent = null;

    for (const h of hrpComponents ?? []) {
        if (usedHrpKeys.has(h.billingRateComponentKey)) continue;
        const labels = [
            h.billingRateComponentName,
            h.componentId,
            normalizeHrpComponentName(h.billingRateComponentName, accountHccId),
        ].filter(Boolean);

        for (const label of labels) {
            const score = scoreRenewalFeeAgainstLabel(feeDescription, label);
            if (score > bestScore) {
                bestScore = score;
                bestKey = h.billingRateComponentKey;
                bestComponent = h;
            }
        }
    }

    return { hrpComponentKey: bestKey, score: bestScore, component: bestComponent };
}

function renewalMatchTypeFromScore(score) {
    if (score >= 1) return "exact";
    if (score >= FUZZY_MATCH_THRESHOLD) return "fuzzy";
    return "unmatched";
}

/**
 * Assigns each renewal fee to an extracted component, an HRP component, or neither.
 */
export function assignRenewalFees(renewalFees, componentPayloads, hrpComponents, accountHccId) {
    if (!Array.isArray(renewalFees) || renewalFees.length === 0) return [];

    const usedExtracted = new Set();
    const usedHrp = new Set();
    const assignments = [];

    renewalFees.forEach((fee, feeIndex) => {
        const base = { feeIndex, fee };
        const description = fee.description ?? "";

        const extracted = findBestRenewalComponentMatch(description, componentPayloads, usedExtracted, accountHccId);
        const extractedWrapper = extracted.index != null ? componentPayloads[extracted.index] : null;
        if (
            extracted.index != null
            && renewalExtractedMatchAllowed(description, extractedWrapper, extracted.score)
        ) {
            usedExtracted.add(extracted.index);
            assignments.push({
                ...base,
                target: "extracted",
                targetIndex: extracted.index,
                matchedName: extracted.name,
                matchType: renewalMatchTypeFromScore(extracted.score),
            });
            return;
        }

        const hrp = findBestRenewalHrpMatch(description, hrpComponents, usedHrp, accountHccId);
        if (hrp.hrpComponentKey != null && hrp.score >= FUZZY_MATCH_THRESHOLD) {
            usedHrp.add(hrp.hrpComponentKey);
            assignments.push({
                ...base,
                target: "hrp",
                hrpComponentKey: hrp.hrpComponentKey,
                hrpComponent: hrp.component,
                matchedName: hrp.component?.billingRateComponentName ?? null,
                matchType: renewalMatchTypeFromScore(hrp.score),
            });
            return;
        }

        assignments.push({
            ...base,
            target: "unmatched",
            matchType: "renewal-only",
        });
    });

    return assignments;
}

/**
 * Applies renewal-document fee data onto matched extracted component payloads.
 * Renewal is the latest source and wins over ASA/stoploss when both contain the same fee.
 */
export function applyRenewalDocumentOverrides({ componentPayloads, renewalFees, hrpComponents, accountHccId }) {
    if (!Array.isArray(componentPayloads) || !renewalFees?.length) return;

    const assignments = assignRenewalFees(
        renewalFees,
        componentPayloads,
        hrpComponents ?? [],
        accountHccId,
    );

    for (const assignment of assignments) {
        if (assignment.target !== "extracted" || assignment.targetIndex == null) continue;
        const fee = assignment.fee;
        if (!fee) continue;

        let wrapper = componentPayloads[assignment.targetIndex];
        if (fee.description) {
            wrapper = { ...wrapper, feeDescription: canonicalizeFeeDescription(fee.description) };
        }
        if (fee.feeType) {
            wrapper = { ...wrapper, feeType: fee.feeType };
        }
        wrapper = { ...wrapper, renewalDocumentSource: true };

        if (fee.standardFeeAmount != null) {
            wrapper = applyAmountToComponentWrapper(wrapper, fee.standardFeeAmount);
        }

        const vendor = resolveRateSubCategoryShortName(resolveFeeSubCategoryVendor(fee));
        wrapper = applyVendorToComponentWrapper(wrapper, vendor);

        componentPayloads[assignment.targetIndex] = wrapper;
    }
}

export function buildComponentWrapperFromRenewalFee(renewalFee, { groupId, plans, hrpComponentName = null, asOfDate = null }) {
    const feeTypeUpper = String(renewalFee?.feeType ?? "PEPM").trim().toUpperCase();
    const isPmpm = feeTypeUpper === "PMPM";
    const defKey = isPmpm
        ? "perMemberPerMonthBillingRateDefinition"
        : "perSubscriptionPerMonthBillingRateDefinition";
    const rateSetKey = isPmpm ? "pmpmRateSet" : "pspmRateSet";
    const amount = renewalFee?.standardFeeAmount != null
        ? formatRateAmountString(renewalFee.standardFeeAmount, { allowZero: true })
        : null;
    const componentName = hrpComponentName ?? [groupId, canonicalizeFeeDescription(renewalFee?.description)].filter(Boolean).join(" ");
    const description = `Adding billing rate component for all ${groupId ?? ""} plans`.trim();
    const vendor = resolveRateSubCategoryShortName(resolveFeeSubCategoryVendor(renewalFee));

    return {
        tiered: false,
        renewalRequired: false,
        renewalSource: hrpComponentName ? "hrp-reconciliation" : "create",
        feeDescription: canonicalizeFeeDescription(renewalFee?.description) ?? null,
        feeType: renewalFee?.feeType ?? "PEPM",
        amount,
        plans: plans ?? [],
        shared: true,
        billingRateComponentPayload: {
            ...(asOfDate ? { asOfDate } : {}),
            maintenanceInfo: { transactionType: "Create", overrideAction: false },
            maintenanceReasonCode: COMPONENT_CREATE_REASON,
            maintenanceComment: description,
            billingRateComponent: {
                componentName,
                description,
                definition: {
                    rateClassifications: {
                        componentId: componentName,
                        rateSubCategory: {
                            codeSetName: RATE_SUBCATEGORY.codeSetName,
                            shortName: vendor,
                        },
                    },
                },
                [defKey]: {
                    [rateSetKey]: {
                        defaultRate: { amount: amount ?? "0.00" },
                    },
                },
            },
        },
    };
}

/** Build a submission component wrapper when keeping an HRP-only component (recreate in payload). */
export function buildComponentWrapperFromHrpComponent(hrpComponent, { groupId, plans, amount = null, feeDescription = null }) {
    const componentName = hrpComponent?.billingRateComponentName ?? hrpComponent?.componentId;
    if (!componentName) return null;

    const billingMethod = String(hrpComponent?.billingMethod ?? "pspm").trim().toLowerCase();
    const isPmpm = billingMethod === "pmpm";
    const defKey = isPmpm
        ? "perMemberPerMonthBillingRateDefinition"
        : "perSubscriptionPerMonthBillingRateDefinition";
    const rateSetKey = isPmpm ? "pmpmRateSet" : "pspmRateSet";
    const feeType = isPmpm ? "PMPM" : "PEPM";
    const desc = feeDescription ?? normalizeHrpComponentName(componentName, groupId) ?? componentName;
    const maintenanceDescription = hrpComponent?.componentDescription
        ?? `Adding billing rate component for all ${groupId ?? ""} plans`.trim();
    const hrpRate = resolveHrpComponentRateAmount(hrpComponent);
    const rateAmount = amount != null && amount !== ""
        ? (formatRateAmountString(amount, { allowZero: true }) ?? String(amount))
        : (hrpRate ?? "0.00");
    const subCategoryShortName = hrpComponent?.rateSubCategoryShortName ?? null;

    const wrapper = {
        tiered: false,
        renewalRequired: false,
        hrpKeepSource: true,
        renewalDocumentSource: false,
        feeDescription: desc,
        feeType,
        amount: amount != null && amount !== ""
            ? (formatRateAmountString(amount, { allowZero: true }) ?? String(amount))
            : hrpRate,
        plans: plans ?? [],
        shared: true,
        billingRateComponentPayload: {
            maintenanceInfo: { transactionType: "Create", overrideAction: false },
            maintenanceReasonCode: COMPONENT_CREATE_REASON,
            maintenanceComment: maintenanceDescription,
            billingRateComponent: {
                componentName,
                description: maintenanceDescription,
                definition: {
                    rateClassifications: {
                        componentId: componentName,
                        rateSubCategory: {
                            codeSetName: RATE_SUBCATEGORY.codeSetName,
                            shortName: subCategoryShortName,
                        },
                    },
                },
                [defKey]: {
                    [rateSetKey]: {
                        defaultRate: { amount: rateAmount },
                    },
                },
            },
        },
    };

    applyComponentCollisionAmendment(wrapper, [{
        existingName: componentName,
        existingId: componentName,
        collidedOn: "both",
    }], groupId);

    return applyHrpSubCategoryToWrapper(wrapper, hrpComponent);
}

/**
 * Applies Keep HRP to an extracted match or recreates an hrp-only component in the submission payloads.
 * Mutates componentPayloads. Returns row patch fields (including rowId when a new payload is appended).
 */
export function applyHrpKeepReconciliationDecision({ componentPayloads, row, groupId, plans, hrpSnapshot }) {
    if (!row?.hrpComponentName) {
        return { rowId: row?.rowId, extractedIndex: row?.extractedIndex ?? null };
    }

    const hrpComponent = findHrpSnapshotComponent(hrpSnapshot, row);

    if (row.extractedIndex != null) {
        applyHrpKeepToComponentPayload({ componentPayloads, row, groupId, hrpComponent });
        const wrapper = componentPayloads[row.extractedIndex];
        return {
            rowId: row.rowId,
            extractedIndex: row.extractedIndex,
            extractedName: wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName ?? row.hrpComponentName,
            extractedFeeDescription: wrapper?.feeDescription ?? row.hrpComponentName,
            nameMismatch: false,
        };
    }

    const hrpComponentResolved = hrpComponent ?? {
        billingRateComponentName: row.hrpComponentName,
        billingMethod: row.hrpBillingMethod,
        componentId: row.hrpComponentName,
    };

    const amount = row.renewalNewAmount
        ?? row.extractedAmount
        ?? resolveHrpComponentRateAmount(hrpComponentResolved)
        ?? null;
    const feeDescription = row.extractedFeeDescription
        ?? normalizeHrpComponentName(row.hrpComponentName, groupId)
        ?? row.hrpComponentName;

    const newWrapper = buildComponentWrapperFromHrpComponent(hrpComponentResolved, {
        groupId,
        plans,
        amount,
        feeDescription,
    });
    if (!newWrapper) {
        return { rowId: row.rowId, extractedIndex: null };
    }

    const newIndex = componentPayloads.length;
    componentPayloads.push(newWrapper);

    return {
        rowId: `extracted-${newIndex}`,
        extractedIndex: newIndex,
        extractedName: row.hrpComponentName,
        extractedFeeDescription: feeDescription,
        nameMismatch: false,
        matchType: row.matchType === "hrp-only+renewal" ? "exact+renewal" : "exact",
        hrpOnlyKeepCreated: true,
        drawerSectionKey: row.drawerSectionKey ?? "hrp-only",
    };
}

/**
 * Match renewal fees from a RENEWAL_CONFIRMATION extraction against component payloads.
 *
 * Returns an array of match objects, one per renewal fee, in the same order:
 *   { feeIndex, description, currentAmount, standardFeeAmount, feeType, notes,
 *     matchType: "exact"|"fuzzy"|"unmatched",
 *     matchedComponentIndex: number|null,
 *     matchedComponentName: string|null }
 *
 * matchedComponentIndex refers to the position in the full componentPayloads array.
 */
export function matchRenewalFees(renewalFees, componentPayloads, accountHccId = null) {
    if (!Array.isArray(renewalFees) || !Array.isArray(componentPayloads)) return [];

    const usedIndexes = new Set();

    return renewalFees.map((fee, feeIndex) => {
        const base = {
            feeIndex,
            description: fee.description ?? null,
            currentAmount: fee.currentAmount ?? null,
            standardFeeAmount: fee.standardFeeAmount ?? null,
            feeType: fee.feeType ?? null,
            notes: fee.notes ?? null,
        };

        const { index, score, name } = findBestRenewalComponentMatch(
            fee.description,
            componentPayloads,
            usedIndexes,
            accountHccId,
        );

        if (index != null && score >= 1) {
            usedIndexes.add(index);
            return { ...base, matchType: "exact", matchedComponentIndex: index, matchedComponentName: name };
        }

        if (index != null && score >= FUZZY_MATCH_THRESHOLD) {
            usedIndexes.add(index);
            return { ...base, matchType: "fuzzy", matchedComponentIndex: index, matchedComponentName: name };
        }

        return { ...base, matchType: "unmatched", matchedComponentIndex: null, matchedComponentName: null };
    });
}

/** LF: hide hrp-only Centivo when the upload has other vendors but no Centivo row (mirrors filterAsaNetworkAccessFees). */
function shouldShowHrpOnlyComponent(h, componentPayloads, accountHccId, planType) {
    const hrpLabel = normalizeHrpComponentName(h.billingRateComponentName, accountHccId);
    const hrpVendor = extractNetworkAccessVendor(hrpLabel);
    if (!hrpVendor) return true;

    const extractedVendors = new Set(
        (componentPayloads ?? [])
            .flatMap((wrapper) => {
                const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
                return [
                    wrapper?.feeDescription,
                    component?.componentName,
                    normalizeHrpComponentName(component?.componentName, accountHccId),
                ];
            })
            .map((label) => extractNetworkAccessVendor(label))
            .filter(Boolean),
    );

    if (extractedVendors.size === 0) return true;

    if (planType === "level-funded") {
        if (hrpVendor !== "centivo") return true;
        return extractedVendors.has("centivo");
    }

    return extractedVendors.has(hrpVendor);
}

function normalizeBillingCategoryCode(code) {
    if (code == null || code === '') return null;
    return String(code).trim().toUpperCase();
}

/** Maps componentName → CBC code from plan billing rate payloads. */
export function buildComponentBillingCategoryLookup(ratePayloads) {
    const map = new Map();
    for (const rp of ratePayloads ?? []) {
        for (const cat of rp.billingRatePayload?.billingRate?.billingCategoryRates ?? []) {
            const code = normalizeBillingCategoryCode(cat.billingCategory?.codeEntry);
            if (!code) continue;
            for (const def of cat.billingRateDefinitions ?? []) {
                const comp = def.componentReference?.component;
                if (comp) map.set(comp, code);
            }
        }
    }
    return map;
}

/** Maps componentName → lock date from plan billing rate payloads. */
export function buildComponentLockDateLookup(ratePayloads) {
    const map = new Map();
    for (const rp of ratePayloads ?? []) {
        for (const cat of rp.billingRatePayload?.billingRate?.billingCategoryRates ?? []) {
            for (const def of cat.billingRateDefinitions ?? []) {
                const comp = def.componentReference?.component;
                const lockDate = normalizeBillingRateAsOfDate(def.componentReference?.lockDate);
                if (comp && lockDate) map.set(comp, lockDate);
            }
        }
    }
    return map;
}

/** Upload effective date for a component wrapper (payload asOfDate, then plan lock date). */
export function resolveExtractedEffectiveDate({ wrapper, componentName, lockDateLookup }) {
    const payloadDate = normalizeBillingRateAsOfDate(wrapper?.billingRateComponentPayload?.asOfDate);
    if (payloadDate) return payloadDate;
    if (componentName && lockDateLookup?.has(componentName)) {
        return lockDateLookup.get(componentName);
    }
    return null;
}

/** Display effective date in the HRP reconciliation drawer. */
export function formatHrpReconciliationEffectiveDate(value) {
    const normalized = normalizeBillingRateAsOfDate(value);
    if (!normalized) return null;
    const parsed = new Date(`${normalized}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return normalized;
    return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const HRP_VERSION_EFF_DATE_SENTINEL_MIN = "-9223372036854776000";
const HRP_VERSION_EFF_DATE_SENTINEL_MAX = "9223372036854776000";

/** Formats an HRP versionEffDateKey (epoch ms or sentinel) for reconciliation display. */
export function formatHrpVersionEffDateKey(dateKey) {
    if (dateKey == null || dateKey === "") return null;
    const keyStr = String(dateKey);
    if (keyStr === HRP_VERSION_EFF_DATE_SENTINEL_MIN) return "Jan 1, 1800";
    if (keyStr === HRP_VERSION_EFF_DATE_SENTINEL_MAX) return "Jan 1, 3000";
    const ms = Number(dateKey);
    if (Number.isNaN(ms)) return null;
    const parsed = new Date(ms);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
}

function isHrpDateSentinel(key) {
    const keyStr = String(key);
    return keyStr === HRP_VERSION_EFF_DATE_SENTINEL_MIN
        || keyStr === HRP_VERSION_EFF_DATE_SENTINEL_MAX;
}

function usableHrpDateKey(key) {
    if (key == null || key === "" || isHrpDateSentinel(key)) return null;
    return key;
}

/** Latest usable epoch among HRP date keys (skips sentinels and non-numeric values). */
function pickLatestUsableHrpDateKey(keys) {
    let best = null;
    for (const key of keys ?? []) {
        const usable = usableHrpDateKey(key);
        if (usable == null) continue;
        const n = Number(usable);
        if (!Number.isFinite(n)) continue;
        if (best == null || n > best) best = n;
    }
    return best;
}

/** Latest non-sentinel planEffectiveDateKey among current active benefit plans. */
function pickHrpPlanEffectiveDateKey(accountBenefitPlans) {
    return pickLatestUsableHrpDateKey(
        dedupeActiveBenefitPlans(accountBenefitPlans).map((plan) => plan?.planEffectiveDateKey),
    );
}

function pickHrpSnapshotLatestEffectiveDateKey(hrpSnapshot, planEffectiveDateKey = null) {
    if (hrpSnapshot?.latestEffectiveDateKey != null) return hrpSnapshot.latestEffectiveDateKey;
    return pickLatestUsableHrpDateKey([
        planEffectiveDateKey ?? hrpSnapshot?.planEffectiveDateKey,
        ...(hrpSnapshot?.components ?? []).map((component) => component?.versionEffDateKey),
    ]);
}

/**
 * HRP drawer date: the latest real date among this component version,
 * the account's plan date, and (when provided) the snapshot-wide latest.
 */
export function resolveHrpComponentEffectiveDate(hrpComponent, {
    planEffectiveDateKey,
    latestEffectiveDateKey,
} = {}) {
    const key = pickLatestUsableHrpDateKey([
        latestEffectiveDateKey,
        hrpComponent?.versionEffDateKey,
        planEffectiveDateKey ?? hrpComponent?.planEffectiveDateKey,
    ]);
    if (key == null) return null;
    return formatHrpVersionEffDateKey(key);
}

function resolveMatchingStrategy(planType) {
    return planType === 'level-funded' ? 'level-funded' : 'aso';
}

function buildHrpMatchLabels({ componentName, componentId, feeDescription, accountHccId }) {
    return [
        ...new Set([
            normalizeHrpComponentName(feeDescription, accountHccId),
            normalizeHrpComponentName(componentName, accountHccId),
            stripInlineParentheticals(feeDescription),
            stripInlineParentheticals(normalizeHrpComponentName(componentName, accountHccId)),
            componentStemLabel(feeDescription),
            componentStemLabel(normalizeHrpComponentName(componentName, accountHccId)),
            componentStemLabel(stripInlineParentheticals(feeDescription)),
        ].filter(Boolean)),
    ];
}

function scoreHrpComponentNameProximity({ componentName, componentId, feeDescription, accountHccId, hrpComponent }) {
    const extractedLabels = buildHrpMatchLabels({ componentName, componentId, feeDescription, accountHccId });
    const hrpLabels = [
        normalizeHrpComponentName(hrpComponent.billingRateComponentName, accountHccId),
        hrpComponent.billingRateComponentName,
        hrpComponent.componentId,
    ].filter(Boolean);

    let score = 0;
    for (const extractedLabel of extractedLabels) {
        for (const hrpLabel of hrpLabels) {
            score = Math.max(score, scoreFeeLabelsForHrpMatch(extractedLabel, hrpLabel));
        }
    }
    return score;
}

function normalizeExactHrpMatchIdentifier(value) {
    return stripInlineParentheticals(String(value ?? "").trim());
}

/** Strip HCC prefix and map ASA/HRP label variants via canonicalizeFeeDescription. */
function normalizeForExactHrpMatch(value, accountHccId) {
    let text = normalizeExactHrpMatchIdentifier(value);
    text = normalizeHrpComponentName(text, accountHccId);
    return canonicalizeFeeDescription(text) ?? text;
}

/** Exact HRP id match after parenthetical strip and canonical fee-label normalization. */
function exactHrpIdentifiersMatch(left, right, accountHccId) {
    if (!left || !right) return false;
    if (left === right) return true;
    if (normalizeExactHrpMatchIdentifier(left) === normalizeExactHrpMatchIdentifier(right)) return true;

    const canonicalLeft = normalizeForExactHrpMatch(left, accountHccId);
    const canonicalRight = normalizeForExactHrpMatch(right, accountHccId);
    if (!canonicalLeft || !canonicalRight) return false;
    if (!vendorQualifiedFeeLabelsCompatible(canonicalLeft, canonicalRight)) return false;
    return canonicalLeft.toLowerCase() === canonicalRight.toLowerCase();
}

function findExactHrpComponentMatch({
    hrpComponents,
    isUnavailable,
    componentName,
    componentId,
    accountHccId,
}) {
    return (hrpComponents ?? []).find(
        (h) =>
            !isUnavailable(h.billingRateComponentKey)
            && hrpComponentExactMatch(h, componentName, componentId, accountHccId),
    ) ?? null;
}

function hrpComponentExactMatch(hrpComponent, componentName, componentId, accountHccId) {
    if (!hrpComponent) return false;
    return (
        exactHrpIdentifiersMatch(hrpComponent.billingRateComponentName, componentName, accountHccId)
        || exactHrpIdentifiersMatch(hrpComponent.componentId, componentId, accountHccId)
        || exactHrpIdentifiersMatch(hrpComponent.componentId, componentName, accountHccId)
        || exactHrpIdentifiersMatch(hrpComponent.billingRateComponentName, componentId, accountHccId)
    );
}

function findAsoHrpComponentMatch({
    hrpComponents,
    usedHrpKeys,
    blockedHrpKeys,
    componentName,
    componentId,
    accountHccId,
}) {
    const isUnavailable = (key) => usedHrpKeys.has(key) || blockedHrpKeys?.has(key);
    const exact = findExactHrpComponentMatch({
        hrpComponents,
        isUnavailable,
        componentName,
        componentId,
        accountHccId,
    });
    if (exact) return { match: exact, matchType: 'exact', matchScore: 1 };
    return { match: null, matchType: null, matchScore: 0 };
}

function findLfCbcHrpComponentMatch({
    hrpComponents,
    isUnavailable,
    componentName,
    componentId,
    feeDescription,
    accountHccId,
    billingCategoryCode,
}) {
    const normalizedCbc = normalizeBillingCategoryCode(billingCategoryCode);
    if (!normalizedCbc) return null;

    const cbcCandidates = (hrpComponents ?? []).filter((h) => {
        if (isUnavailable(h.billingRateComponentKey)) return false;
        return normalizeBillingCategoryCode(h.billingCategoryCode) === normalizedCbc;
    });

    if (cbcCandidates.length === 0) return null;

    let best = null;
    let bestScore = 0;
    let secondBestScore = 0;
    for (const h of cbcCandidates) {
        const score = scoreHrpComponentNameProximity({
            componentName,
            componentId,
            feeDescription,
            accountHccId,
            hrpComponent: h,
        });
        if (score > bestScore) {
            secondBestScore = bestScore;
            bestScore = score;
            best = h;
        } else if (score > secondBestScore) {
            secondBestScore = score;
        }
    }

    if (!best) return null;

    const ambiguous = cbcCandidates.length > 1 && (bestScore - secondBestScore) < 0.08;
    if (bestScore >= FUZZY_MATCH_THRESHOLD && !ambiguous) {
        const exactMatch = hrpComponentExactMatch(best, componentName, componentId, accountHccId);
        return {
            match: best,
            matchType: exactMatch
                ? 'exact'
                : (cbcCandidates.length === 1 ? 'cbc' : 'cbc+proximity'),
            matchScore: bestScore,
        };
    }

    return {
        match: best,
        matchType: 'cbc+weak',
        matchScore: bestScore,
    };
}

function findLfHrpComponentMatch({
    hrpComponents,
    usedHrpKeys,
    blockedHrpKeys,
    componentName,
    componentId,
    feeDescription,
    accountHccId,
    billingCategoryCode,
}) {
    const isUnavailable = (key) => usedHrpKeys.has(key) || blockedHrpKeys?.has(key);

    const cbcResult = findLfCbcHrpComponentMatch({
        hrpComponents,
        isUnavailable,
        componentName,
        componentId,
        feeDescription,
        accountHccId,
        billingCategoryCode,
    });
    if (
        cbcResult?.match
        && cbcResult.matchType !== 'cbc+weak'
        && (cbcResult.matchScore ?? 0) >= FUZZY_MATCH_THRESHOLD
    ) {
        return cbcResult;
    }

    const exact = findExactHrpComponentMatch({
        hrpComponents,
        isUnavailable,
        componentName,
        componentId,
        accountHccId,
    });
    if (exact) return { match: exact, matchType: 'exact', matchScore: 1 };

    if (cbcResult?.match) return cbcResult;

    return { match: null, matchType: null, matchScore: 0 };
}

function findHrpComponentMatch({
    matchingStrategy = 'aso',
    hrpComponents,
    usedHrpKeys,
    blockedHrpKeys,
    componentName,
    componentId,
    feeDescription,
    accountHccId,
    billingCategoryCode,
}) {
    if (matchingStrategy === 'level-funded') {
        return findLfHrpComponentMatch({
            hrpComponents,
            usedHrpKeys,
            blockedHrpKeys,
            componentName,
            componentId,
            feeDescription,
            accountHccId,
            billingCategoryCode,
        });
    }

    return findAsoHrpComponentMatch({
        hrpComponents,
        usedHrpKeys,
        blockedHrpKeys,
        componentName,
        componentId,
        accountHccId,
    });
}

function shouldConsumeHrpMatchKey({ matchingStrategy, matchType }) {
    if (matchingStrategy !== 'level-funded') return true;
    return !String(matchType ?? '').startsWith('cbc');
}

/** HRP components resolved on hrp-only rows must not be fuzzy-matched to new extracted payloads. */
function collectBlockedHrpKeys(previousRows) {
    const blocked = new Set();
    for (const row of previousRows ?? []) {
        if (!row?.confirmed || !row?.hrpComponentKey || row.extractedIndex != null) continue;
        if (row.userAction === "keep" || row.userAction === "skip" || row.skipped) {
            blocked.add(row.hrpComponentKey);
        }
    }
    return blocked;
}

function hrpNamesMismatch(hrpName, extractedLabel, accountHccId) {
    if (!hrpName || !extractedLabel) return false;
    const hrpNorm = normalizeHrpComponentName(hrpName, accountHccId);
    const extNorm = stripInlineParentheticals(String(extractedLabel).trim());
    if (hrpNorm.toLowerCase() === extNorm.toLowerCase()) return false;
    if (String(hrpName).trim().toLowerCase() === extNorm.toLowerCase()) return false;
    if (scoreRenewalFeeAgainstLabel(hrpNorm, extNorm) >= FUZZY_MATCH_THRESHOLD) return false;
    return true;
}

/** Rows that need manual review before proceeding (fuzzy links, HRP+renewal conflicts). */
export function hrpReconciliationNeedsReview(row) {
    return !row?.confirmed
        && !row?.skipped
        && (
            row.matchType?.includes('fuzzy')
            || row.matchType === 'cbc+weak'
            || row.matchType === 'hrp-only+renewal'
        );
}

/** Pending rows that are safe to bulk-update (exact matches, new fees, etc.). */
export function hrpReconciliationIsReady(row) {
    return !row?.confirmed && !row?.skipped && !hrpReconciliationNeedsReview(row);
}

/** User-facing labels for HRP reconciliation row actions (values stay keep/amend/create/skip). */
export const HRP_RECONCILIATION_ACTION_LABELS = {
    keep: "Use current HRP",
    amend: "Use extracted",
    create: "Create",
    skip: "Discard",
};

export const HRP_RECONCILIATION_ACTION_MENU_HINT = Object.values(HRP_RECONCILIATION_ACTION_LABELS).join(", ");

export function getHrpReconciliationRowStatus(row) {
    if (!row?.confirmed && !row?.skipped) return "Pending";
    if (row.skipped || row.userAction === "skip") return "Discarded";
    if (row.userAction === "keep") return "Using current HRP";
    if (row.userAction === "create") return "Created";
    if (row.userAction === "amend") return "Using extracted";
    return "Updated";
}

/** Current saved/draft action for a reconciliation row. */
export function getHrpReconciliationEffectiveAction(row) {
    if (row?.skipped || row?.userAction === "skip") return "skip";
    if (row?.confirmed && row?.userAction) return row.userAction;
    return row?.suggestedAction ?? "skip";
}

/** User-facing label for the saved reconciliation action. */
export function getHrpReconciliationActionLabel(row) {
    const action = getHrpReconciliationEffectiveAction(row);
    return HRP_RECONCILIATION_ACTION_LABELS[action] ?? action;
}

/** Remove a payload created by reconciliation and shift row extracted indices. */
export function removeHrpReconciliationComponentPayload(payloads, rows, extractedIndex) {
    const removedName = payloads[extractedIndex]?.billingRateComponentPayload?.billingRateComponent?.componentName ?? null;
    const nextPayloads = payloads.filter((_, i) => i !== extractedIndex);
    const nextRows = rows.map((row) => {
        if (row.extractedIndex == null) return row;
        if (row.extractedIndex === extractedIndex) {
            const reset = {
                ...row,
                extractedIndex: null,
                confirmed: false,
                skipped: false,
                userAction: null,
                hrpOnlyKeepCreated: false,
                drawerSectionKey: row.drawerSectionKey
                    ?? getHrpReconciliationDrawerSectionKey(row),
            };
            if (row.hrpComponentKey && row.rowId?.startsWith("extracted-")) {
                reset.rowId = `hrp-${row.hrpComponentKey}`;
            } else if (row.renewalFeeIndex != null) {
                reset.rowId = `renewal-${row.renewalFeeIndex}`;
            }
            return reset;
        }
        if (row.extractedIndex > extractedIndex) {
            const newIndex = row.extractedIndex - 1;
            return {
                ...row,
                extractedIndex: newIndex,
                ...(row.rowId?.startsWith("extracted-") ? { rowId: `extracted-${newIndex}` } : {}),
            };
        }
        return row;
    });
    return { payloads: nextPayloads, rows: nextRows, removedName };
}

/** True when skipping a reconciliation row should drop its component payload from step 2. */
export function shouldRemoveHrpReconciliationComponentOnSkip(row, { action, previousAction } = {}) {
    if (action !== "skip" || row?.extractedIndex == null) return false;

    if (row.confirmed) {
        return (
            previousAction === "create"
            || (previousAction === "keep" && row.hrpOnlyKeepCreated)
        );
    }

    if (getHrpReconciliationDrawerSectionKey(row) !== "new-from-upload") return false;

    const matchType = row.matchType ?? "";
    return matchType === "extracted-only" || matchType === "extracted-only+renewal";
}

export function mapHrpReconciliationByExtractedIndex(reconciliationRows) {
    const map = new Map();
    for (const row of reconciliationRows ?? []) {
        if (row.extractedIndex != null) map.set(row.extractedIndex, row);
    }
    return map;
}

/** Component names whose billing-rate lock dates must not be bulk-updated in step 3. */
export function buildPreserveLockDateComponentNames(componentPayloads, hrpReconciliation) {
    const names = new Set();
    const reconByIndex = mapHrpReconciliationByExtractedIndex(hrpReconciliation);

    (componentPayloads ?? []).forEach((wrapper, index) => {
        const componentName =
            wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;
        if (!componentName) return;

        if (wrapper.hrpKeepSource) {
            names.add(componentName);
            return;
        }

        const row = reconByIndex.get(index);
        if (row && getHrpReconciliationEffectiveAction(row) === 'keep') {
            names.add(componentName);
        }
    });

    return names;
}

function hrpRowAllowsKeep(row) {
    return Boolean(row?.hrpComponentName)
        && row.matchType !== "renewal-only"
        && row.matchType !== "extracted-only";
}

function hrpRowAllowsUpdate(row) {
    const matchType = row?.matchType ?? "";
    if (matchType === "hrp-only+renewal") return true;
    if (matchType === "extracted-only+renewal" && row?.renewalNewAmount != null) return true;
    return matchType !== "hrp-only"
        && matchType !== "renewal-only"
        && matchType !== "extracted-only"
        && matchType !== "extracted-only+renewal";
}

function hrpRowAllowsCreate(row) {
    const matchType = row?.matchType ?? "";
    return matchType === "renewal-only"
        || matchType === "extracted-only"
        || matchType === "extracted-only+renewal";
}

/** Label for the Extracted column. LF shows full component names; ASO keeps fee descriptions. */
export function getHrpReconciliationExtractedLabel(row, { planType } = {}) {
    const feeDescription = row?.extractedFeeDescription ?? null;
    const componentName = row?.extractedName ?? null;

    if (planType === 'level-funded') {
        if (row?.extractedIndex != null && componentName) return componentName;
        return feeDescription ?? componentName;
    }

    return feeDescription ?? componentName;
}

/** Dropdown options for a reconciliation row (values match userAction). */
export function getHrpReconciliationActionOptions(row, { preferKeep = false, excludeActions = [] } = {}) {
    const excluded = new Set(excludeActions);
    const options = [];
    const add = (value, label) => options.push({ value, label });

    if (preferKeep && hrpRowAllowsKeep(row)) add("keep", HRP_RECONCILIATION_ACTION_LABELS.keep);
    if (hrpRowAllowsUpdate(row)) add("amend", HRP_RECONCILIATION_ACTION_LABELS.amend);
    if (!preferKeep && hrpRowAllowsKeep(row)) add("keep", HRP_RECONCILIATION_ACTION_LABELS.keep);
    if (hrpRowAllowsCreate(row)) add("create", HRP_RECONCILIATION_ACTION_LABELS.create);
    if (!excluded.has("skip")) add("skip", HRP_RECONCILIATION_ACTION_LABELS.skip);
    return options;
}

/** Picks a valid draft action when the requested value is excluded for this row. */
export function resolveHrpReconciliationDraftAction(row, action, { preferKeep = false, excludeActions = [] } = {}) {
    const options = getHrpReconciliationActionOptions(row, { preferKeep, excludeActions });
    if (options.some((option) => option.value === action)) return action;
    return options[0]?.value ?? action;
}

/** Resolves a bulk drawer action for one row when that action is valid for its match type. */
export function resolveBulkHrpReconciliationAction(row, bulkAction) {
    if (bulkAction === "skip") return "skip";
    if (bulkAction === "keep") return hrpRowAllowsKeep(row) ? "keep" : null;
    if (bulkAction === "amend") return hrpRowAllowsUpdate(row) ? "amend" : null;
    if (bulkAction === "create") return hrpRowAllowsCreate(row) ? "create" : null;
    return null;
}

/** Applies a bulk action to every applicable row in the drawer draft. */
export function applyBulkHrpReconciliationDraftActions(existingDraft, rows, bulkAction) {
    const next = { ...(existingDraft ?? {}) };
    for (const row of rows ?? []) {
        const action = resolveBulkHrpReconciliationAction(row, bulkAction);
        if (action) next[row.rowId] = action;
    }
    return next;
}

/** Counts rows that would change under a bulk action. */
export function countBulkHrpReconciliationApplicable(rows, bulkAction) {
    return (rows ?? []).filter((row) => resolveBulkHrpReconciliationAction(row, bulkAction) != null).length;
}

/** Bulk action buttons for drawer / section headers. */
export function getHrpBulkActionDefinitions(rows) {
    const list = rows ?? [];
    return [
        {
            key: "keep",
            label: `${HRP_RECONCILIATION_ACTION_LABELS.keep} all`,
            bulkAction: "keep",
            count: countBulkHrpReconciliationApplicable(list, "keep"),
        },
        {
            key: "amend",
            label: `${HRP_RECONCILIATION_ACTION_LABELS.amend} all`,
            bulkAction: "amend",
            count: countBulkHrpReconciliationApplicable(list, "amend"),
        },
        {
            key: "create",
            label: `${HRP_RECONCILIATION_ACTION_LABELS.create} all`,
            bulkAction: "create",
            count: countBulkHrpReconciliationApplicable(list, "create"),
        },
        {
            key: "skip",
            label: `${HRP_RECONCILIATION_ACTION_LABELS.skip} all`,
            bulkAction: "skip",
            count: list.length,
        },
    ];
}

/** User-facing labels for per-row match badges in the HRP reconciliation table. */
export const HRP_RECONCILIATION_MATCH_LABELS = {
    exact: "Exact match",
    fuzzy: "Fuzzy match",
    cbc: "CBC match",
    "cbc+proximity": "CBC · name proximity",
    "cbc+weak": "CBC · needs review",
    "extracted-only": "New fee",
    "hrp-only": "HRP only",
    "renewal-only": "Renewal only",
    "exact+renewal": "Exact · renewal",
    "fuzzy+renewal": "Fuzzy · renewal",
    "extracted-only+renewal": "New fee · renewal",
    "hrp-only+renewal": "HRP only · renewal",
};

/** Drawer section order: matched upload fees first, then new fees, then HRP-only gaps. */
export const HRP_RECONCILIATION_DRAWER_SECTION_ORDER = [
    "matched",
    "new-from-upload",
    "hrp-only",
];

export const HRP_RECONCILIATION_DRAWER_SECTIONS = {
    matched: {
        key: "matched",
        title: "Matched to HRP",
        subtitle: "Fees from your upload linked to existing HRP components.",
        excludeBulkActions: ["skip"],
        showEffectiveDateColumn: true,
    },
    "new-from-upload": {
        key: "new-from-upload",
        title: "New from upload",
        subtitle: "Fees in your upload with no matching HRP component.",
        showEffectiveDateColumn: true,
    },
    "hrp-only": {
        key: "hrp-only",
        title: "Only in HRP",
        subtitle: "Existing HRP components not found in your upload.",
        showEffectiveDateColumn: true,
    },
};

/** Within-section row order: review-first, then confident links. */
const HRP_RECONCILIATION_MATCH_TYPE_ORDER = [
    "cbc+weak",
    "fuzzy+renewal",
    "fuzzy",
    "hrp-only+renewal",
    "cbc+proximity",
    "cbc",
    "exact+renewal",
    "exact",
    "extracted-only+renewal",
    "extracted-only",
    "hrp-only",
    "renewal-only",
];

function hrpReconciliationMatchTypeSortIndex(matchType) {
    const idx = HRP_RECONCILIATION_MATCH_TYPE_ORDER.indexOf(matchType);
    return idx >= 0 ? idx : HRP_RECONCILIATION_MATCH_TYPE_ORDER.length;
}

function resolveHrpReconciliationDrawerSectionKeyFromMatchType(matchType) {
    const base = String(matchType ?? "").split("+")[0];
    if (base === "hrp-only") return "hrp-only";
    if (base === "extracted-only" || base === "renewal-only" || base === "global-collision") {
        return "new-from-upload";
    }
    return "matched";
}

/** Maps a reconciliation row to a drawer section key. */
export function getHrpReconciliationDrawerSectionKey(row) {
    if (row?.drawerSectionKey) return row.drawerSectionKey;

    // Kept HRP-only rows upgrade matchType to exact on save — keep them in the HRP-only section.
    if (
        row?.hrpOnlyKeepCreated
        && (row.matchType === "exact" || row.matchType === "exact+renewal")
    ) {
        return "hrp-only";
    }

    return resolveHrpReconciliationDrawerSectionKeyFromMatchType(row?.matchType);
}

/** Groups reconciliation rows into drawer sections (matched / new / HRP-only). */
export function groupHrpReconciliationDrawerRows(rows, needsReview) {
    const bySection = new Map();
    for (const row of rows ?? []) {
        const sectionKey = getHrpReconciliationDrawerSectionKey(row);
        if (!bySection.has(sectionKey)) bySection.set(sectionKey, []);
        bySection.get(sectionKey).push(row);
    }

    return HRP_RECONCILIATION_DRAWER_SECTION_ORDER
        .filter((sectionKey) => (bySection.get(sectionKey)?.length ?? 0) > 0)
        .map((sectionKey) => {
            const groupRows = [...(bySection.get(sectionKey) ?? [])]
                .sort((a, b) => hrpReconciliationMatchTypeSortIndex(a.matchType)
                    - hrpReconciliationMatchTypeSortIndex(b.matchType));
            const meta = HRP_RECONCILIATION_DRAWER_SECTIONS[sectionKey];
            return {
                key: sectionKey,
                title: `${meta.title} (${groupRows.length})`,
                subtitle: meta.subtitle,
                rows: groupRows,
                preferKeep: groupRows.some((row) => needsReview(row)),
                excludeBulkActions: meta.excludeBulkActions ?? [],
                showEffectiveDateColumn: meta.showEffectiveDateColumn === true,
            };
        });
}

function feeTypeFromHrpBillingMethod(billingMethod) {
    const method = String(billingMethod ?? "").trim().toLowerCase();
    if (method === "pmpm") return "PMPM";
    if (method === "pspm") return "PEPM";
    return null;
}

/** Resolved fee type for extracted-rate display (wrapper fee type, then matched HRP billing method). */
export function resolveHrpReconciliationExtractedRateType(row) {
    if (row?.extractedFeeType) return row.extractedFeeType;
    return feeTypeFromHrpBillingMethod(row?.hrpBillingMethod);
}

/** Display fee type under amount (PEPM → PSPM). */
export function formatHrpReconciliationRateType(feeType) {
    if (!feeType) return null;
    const upper = String(feeType).trim().toUpperCase();
    if (upper === "PEPM") return "PSPM";
    if (upper === "PMPM") return "PMPM";
    return upper;
}

export function formatHrpReconciliationAmount(amount) {
    if (amount == null || amount === "") return null;
    const num = Number(amount);
    if (Number.isNaN(num)) return `$${amount}`;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/** Compares extracted and HRP rate amounts for drawer indicators (higher | lower | equal). */
export function compareHrpReconciliationRateAmounts(hrpAmount, extractedAmount) {
    if (
        hrpAmount == null || hrpAmount === ""
        || extractedAmount == null || extractedAmount === ""
    ) {
        return null;
    }

    const hrp = Number(hrpAmount);
    const extracted = Number(extractedAmount);
    if (Number.isNaN(hrp) || Number.isNaN(extracted)) {
        return String(hrpAmount) === String(extractedAmount) ? "equal" : null;
    }

    if (extracted > hrp) return "higher";
    if (extracted < hrp) return "lower";
    return "equal";
}

/**
 * Rewrites an extracted component payload to match the linked HRP component (Keep HRP).
 * Mutates componentPayloads in place.
 */
export function applyHrpKeepToComponentPayload({ componentPayloads, row, groupId, hrpComponent }) {
    if (row?.extractedIndex == null || !row?.hrpComponentName) return;

    let wrapper = componentPayloads[row.extractedIndex];
    if (!wrapper?.billingRateComponentPayload?.billingRateComponent) return;

    const componentName = row.hrpComponentName;
    const feeDescription = normalizeHrpComponentName(componentName, groupId) || componentName;
    const component = wrapper.billingRateComponentPayload.billingRateComponent;

    wrapper = {
        ...wrapper,
        feeDescription,
        hrpKeepSource: true,
        renewalDocumentSource: false,
        billingRateComponentPayload: {
            ...wrapper.billingRateComponentPayload,
            billingRateComponent: {
                ...component,
                componentName,
                description: hrpComponent?.componentDescription ?? component.description,
                definition: {
                    ...component.definition,
                    rateClassifications: {
                        ...component.definition?.rateClassifications,
                        componentId: row.hrpComponentName,
                    },
                },
            },
        },
    };

    const hrpRate = resolveHrpComponentRateAmount(hrpComponent);
    if (hrpRate != null) {
        wrapper = applyAmountToComponentWrapper(wrapper, hrpRate);
        wrapper = { ...wrapper, amount: hrpRate };
    }
    if (hrpComponent) {
        wrapper = applyHrpSubCategoryToWrapper(wrapper, hrpComponent);
    }

    applyComponentCollisionAmendment(wrapper, [{
        existingName: componentName,
        existingId: componentName,
        collidedOn: "both",
    }], groupId);

    componentPayloads[row.extractedIndex] = wrapper;
}

/**
 * Loads account-scoped components and per-plan rate read-back from HRP.
 */
export async function fetchHrpBillingSnapshot({
    accountKey,
    accountBenefitPlans,
    asOfDate,
    fetchBillingRateComponentsByAccountKey,
    fetchBillingRateAndComponents,
}) {
    const planEffectiveDateKey = pickHrpPlanEffectiveDateKey(accountBenefitPlans);
    const emptySnapshot = {
        components: [],
        plans: {},
        asOfDate: asOfDate ?? null,
        planEffectiveDateKey,
        latestEffectiveDateKey: planEffectiveDateKey,
    };
    if (!accountKey || !fetchBillingRateComponentsByAccountKey) {
        return emptySnapshot;
    }

    const normalizedAsOfDate = normalizeBillingRateAsOfDate(asOfDate);
    if (!normalizedAsOfDate) {
        return { ...emptySnapshot, asOfDate: null };
    }

    const dateQuery = { asOfDate: normalizedAsOfDate };
    const components = await fetchBillingRateComponentsByAccountKey({
        accountKey,
        ...dateQuery,
    }).catch(() => []);

    const plans = {};
    if (fetchBillingRateAndComponents && Array.isArray(accountBenefitPlans)) {
        await Promise.all(
            accountBenefitPlans.map(async (plan) => {
                if (!plan?.benefitPlanKey) return;
                const res = await fetchBillingRateAndComponents({
                    benefitPlanKey: plan.benefitPlanKey,
                    benefitPlanHccId: plan.benefitPlanHccId,
                    ...dateQuery,
                }).catch(() => null);
                if (res) plans[plan.benefitPlanKey] = res;
            }),
        );
    }

    const componentList = Array.isArray(components) ? components : [];
    return {
        components: componentList,
        plans,
        asOfDate: normalizedAsOfDate,
        planEffectiveDateKey,
        latestEffectiveDateKey: pickLatestUsableHrpDateKey([
            planEffectiveDateKey,
            ...componentList.map((component) => component?.versionEffDateKey),
        ]),
    };
}

/**
 * Fetches HRP snapshot and builds reconciliation rows for component payloads.
 * Used by proceed and JSON import so both follow the same HRP update workflow.
 */
export async function prepareHrpReconciliationContext({
    componentPayloads,
    fees = [],
    ratePayloads = [],
    planType = 'aso',
    accountHccId: accountHccIdHint,
    clientName,
    asOfDate: asOfDateHint = null,
    group = null,
    parsed = null,
    accountOverride = null,
    globalCollisionRows = [],
    fetchAccounts,
    fetchBenefitPlansByAccountKey,
    fetchBillingRateComponentsByAccountKey,
    fetchBillingRateAndComponents,
}) {
    let resolvedAccount = accountOverride?.accountKey ? accountOverride : null;
    if (!resolvedAccount && accountHccIdHint && fetchAccounts) {
        const res = await fetchAccounts({ accountHccId: accountHccIdHint, limit: 1 }).catch(() => null);
        resolvedAccount = res?.rows?.[0] ?? null;
    }
    if (!resolvedAccount && clientName && fetchAccounts) {
        const res = await fetchAccounts({ accountName: clientName, limit: 1 }).catch(() => null);
        resolvedAccount = res?.rows?.[0] ?? null;
    }

    const groupId = resolvedAccount?.accountHccId ?? accountHccIdHint ?? null;
    const asOfDate = extractBillingRateAsOfDate({
        componentPayloads,
        group,
        parsed,
        asOfDateHint,
    }) ?? getDefaultBillingRateAsOfDate();

    let hrpSnapshot = { components: [], plans: {}, asOfDate };
    let accountBenefitPlans = [];

    if (resolvedAccount?.accountKey && fetchBenefitPlansByAccountKey) {
        accountBenefitPlans = await fetchBenefitPlansByAccountKey({
            accountKey: resolvedAccount.accountKey,
        }).catch(() => []);
    }

    if (resolvedAccount?.accountKey) {
        hrpSnapshot = await fetchHrpBillingSnapshot({
            accountKey: resolvedAccount.accountKey,
            accountBenefitPlans,
            asOfDate,
            fetchBillingRateComponentsByAccountKey,
            fetchBillingRateAndComponents,
        });

        const fallbackAsOfDate = getDefaultBillingRateAsOfDate();
        if (
            (hrpSnapshot.components?.length ?? 0) === 0
            && fallbackAsOfDate
            && fallbackAsOfDate !== asOfDate
        ) {
            const retrySnapshot = await fetchHrpBillingSnapshot({
                accountKey: resolvedAccount.accountKey,
                accountBenefitPlans,
                asOfDate: fallbackAsOfDate,
                fetchBillingRateComponentsByAccountKey,
                fetchBillingRateAndComponents,
            });
            if ((retrySnapshot.components?.length ?? 0) > 0) {
                hrpSnapshot = retrySnapshot;
            }
        }
    }

    const renewalFees = filterBillableRenewalFees(fees);
    if (renewalFees.length > 0) {
        applyRenewalDocumentOverrides({
            componentPayloads,
            renewalFees,
            hrpComponents: hrpSnapshot.components,
            accountHccId: groupId,
        });
    }

    let hrpReconciliation = [];
    if (resolvedAccount?.accountKey && (hrpSnapshot.components?.length ?? 0) > 0) {
        hrpReconciliation = buildHrpReconciliationRows({
            hrpSnapshot,
            componentPayloads,
            ratePayloads,
            planType,
            accountHccId: groupId,
            renewalFees,
        });
        hrpReconciliation = enrichReconciliationRowsWithGlobalCollisions(
            hrpReconciliation,
            globalCollisionRows,
        );
    }

    return {
        hrpSnapshot,
        hrpReconciliation,
        groupId,
        hasHrpReconciliation: hrpReconciliation.length > 0,
        resolvedAccount,
    };
}

/**
 * Builds reconciliation rows comparing extracted component payloads to an HRP snapshot.
 * Renewal PDF fees are matched to extracted components, HRP components, or shown on their own.
 */
export function buildHrpReconciliationRows({
    hrpSnapshot,
    componentPayloads,
    ratePayloads = [],
    planType = 'aso',
    accountHccId,
    renewalFees,
    blockedHrpKeys,
}) {
    const hrpComponents = hrpSnapshot?.components ?? [];
    const hrpPlanEffectiveDateKey = hrpSnapshot?.planEffectiveDateKey ?? null;
    const hrpLatestEffectiveDateKey = pickHrpSnapshotLatestEffectiveDateKey(
        hrpSnapshot,
        hrpPlanEffectiveDateKey,
    );
    const matchingStrategy = resolveMatchingStrategy(planType);
    const componentBillingCategoryLookup = matchingStrategy === 'level-funded'
        ? buildComponentBillingCategoryLookup(ratePayloads)
        : null;
    const componentLockDateLookup = buildComponentLockDateLookup(ratePayloads);
    const usedHrpKeys = new Set();
    const matchedHrpKeys = new Set();
    const blocked = blockedHrpKeys ?? new Set();
    const rows = [];

    const renewalAssignments = assignRenewalFees(
        renewalFees ?? [],
        componentPayloads,
        hrpComponents,
        accountHccId,
    );
    const renewalByExtractedIndex = new Map();
    const renewalByHrpKey = new Map();
    const unmatchedRenewals = [];

    renewalAssignments.forEach((assignment) => {
        if (assignment.target === "extracted") {
            renewalByExtractedIndex.set(assignment.targetIndex, assignment);
        } else if (assignment.target === "hrp") {
            renewalByHrpKey.set(assignment.hrpComponentKey, assignment);
        } else {
            unmatchedRenewals.push(assignment);
        }
    });

    const renewalRowFields = (assignment) => {
        const fee = assignment?.fee;
        if (!fee) {
            return {
                renewalDescription: null,
                renewalCurrentAmount: null,
                renewalNewAmount: null,
                renewalFeeIndex: null,
                extractedFeeType: null,
                renewalMatchType: null,
            };
        }
        return {
            renewalDescription: stripInlineParentheticals(fee.description ?? "") || null,
            renewalCurrentAmount: fee.currentAmount ?? null,
            renewalNewAmount: fee.standardFeeAmount ?? null,
            renewalFeeIndex: assignment.feeIndex ?? null,
            extractedFeeType: fee.feeType ?? null,
            renewalMatchType: assignment.matchType ?? null,
        };
    };

    (componentPayloads ?? []).forEach((wrapper, extractedIndex) => {
        const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
        const name = component?.componentName ?? "";
        const componentId = component?.definition?.rateClassifications?.componentId ?? "";
        const feeDescription = stripInlineParentheticals(
            wrapper?.feeDescription
            ?? normalizeHrpComponentName(name, accountHccId)
            ?? name,
        );

        const { match, matchType, matchScore } = findHrpComponentMatch({
            matchingStrategy,
            hrpComponents,
            usedHrpKeys,
            blockedHrpKeys: blocked,
            componentName: name,
            componentId,
            feeDescription,
            accountHccId,
            billingCategoryCode: componentBillingCategoryLookup?.get(name) ?? null,
        });

        if (match) {
            matchedHrpKeys.add(match.billingRateComponentKey);
            if (shouldConsumeHrpMatchKey({ matchingStrategy, matchType })) {
                usedHrpKeys.add(match.billingRateComponentKey);
            }
        }

        const renewalAssignment = renewalByExtractedIndex.get(extractedIndex)
            ?? (match?.billingRateComponentKey
                ? renewalByHrpKey.get(match.billingRateComponentKey)
                : null);
        const renewalFields = renewalRowFields(renewalAssignment);
        const hasRenewal = renewalAssignment != null;
        const extractedAmount = getWrapperRateAmount(wrapper);
        const renewalNewAmount = hasRenewal
            ? renewalFields.renewalNewAmount
            : (renewalFields.renewalNewAmount ?? extractedAmount);
        const displayExtractedLabel = hasRenewal
            ? (renewalFields.renewalDescription ?? feeDescription)
            : feeDescription;
        const rowMatchType = renewalAssignment
            ? `${match ? matchType : "extracted-only"}+renewal`
            : (match ? matchType : "extracted-only");

        rows.push({
            rowId: `extracted-${extractedIndex}`,
            extractedIndex,
            extractedName: name,
            extractedFeeDescription: displayExtractedLabel,
            extractedAmount,
            ...renewalFields,
            extractedFeeType: renewalFields.extractedFeeType
                ?? wrapper?.feeType
                ?? feeTypeFromHrpBillingMethod(match?.billingMethod)
                ?? null,
            extractedEffectiveDate: resolveExtractedEffectiveDate({
                wrapper,
                componentName: name,
                lockDateLookup: componentLockDateLookup,
            }),
            hrpEffectiveDate: resolveHrpComponentEffectiveDate(match, {
                planEffectiveDateKey: hrpPlanEffectiveDateKey,
                latestEffectiveDateKey: hrpLatestEffectiveDateKey,
            }),
            renewalNewAmount,
            renewalRequired: wrapper?.renewalRequired === true,
            hrpComponentKey: match?.billingRateComponentKey ?? null,
            hrpComponentName: match?.billingRateComponentName ?? null,
            hrpBillingMethod: match?.billingMethod ?? null,
            hrpCurrentAmount: resolveHrpComponentRateAmount(match),
            hrpRateSubCategoryShortName: match?.rateSubCategoryShortName ?? null,
            matchScore: match ? (matchScore ?? (matchType === "exact" ? 1 : null)) : null,
            nameMismatch: match
                ? hrpNamesMismatch(
                    match.billingRateComponentName,
                    getHrpReconciliationExtractedLabel(
                        {
                            extractedIndex,
                            extractedName: name,
                            extractedFeeDescription: displayExtractedLabel,
                        },
                        { planType },
                    ),
                    accountHccId,
                )
                : false,
            matchType: rowMatchType,
            drawerSectionKey: resolveHrpReconciliationDrawerSectionKeyFromMatchType(rowMatchType),
            suggestedAction: match || renewalFields.renewalNewAmount != null ? "amend" : "create",
            confirmed: false,
            skipped: false,
            userAction: null,
        });
    });

    hrpComponents.forEach((h) => {
        if (matchedHrpKeys.has(h.billingRateComponentKey)) return;
        if (!shouldShowHrpOnlyComponent(h, componentPayloads, accountHccId, planType)) return;

        const renewalAssignment = renewalByHrpKey.get(h.billingRateComponentKey);

        const renewalFields = renewalRowFields(renewalAssignment);
        const hasRenewalNew = renewalFields.renewalNewAmount != null;
        const rowMatchType = renewalAssignment ? "hrp-only+renewal" : "hrp-only";

        rows.push({
            rowId: `hrp-${h.billingRateComponentKey}`,
            extractedIndex: null,
            extractedName: null,
            extractedFeeDescription: renewalFields.renewalDescription,
            extractedAmount: null,
            ...renewalFields,
            renewalRequired: false,
            hrpComponentKey: h.billingRateComponentKey,
            hrpComponentName: h.billingRateComponentName,
            hrpBillingMethod: h.billingMethod,
            hrpCurrentAmount: resolveHrpComponentRateAmount(h),
            hrpRateSubCategoryShortName: h.rateSubCategoryShortName ?? null,
            hrpEffectiveDate: resolveHrpComponentEffectiveDate(h, {
                planEffectiveDateKey: hrpPlanEffectiveDateKey,
                latestEffectiveDateKey: hrpLatestEffectiveDateKey,
            }),
            matchType: rowMatchType,
            drawerSectionKey: resolveHrpReconciliationDrawerSectionKeyFromMatchType(rowMatchType),
            suggestedAction: hasRenewalNew ? "amend" : "keep",
            matchScore: renewalAssignment ? 0.81 : null,
            nameMismatch: Boolean(renewalFields.renewalDescription),
            confirmed: false,
            skipped: false,
            userAction: null,
        });
    });

    unmatchedRenewals.forEach((assignment) => {
        const renewalFields = renewalRowFields(assignment);
        rows.push({
            rowId: `renewal-${assignment.feeIndex}`,
            extractedIndex: null,
            extractedName: null,
            extractedFeeDescription: renewalFields.renewalDescription,
            extractedAmount: null,
            extractedEffectiveDate: null,
            ...renewalFields,
            renewalRequired: false,
            hrpComponentKey: null,
            hrpComponentName: null,
            hrpBillingMethod: null,
            matchType: "renewal-only",
            drawerSectionKey: "new-from-upload",
            suggestedAction: "create",
            confirmed: false,
            skipped: false,
            userAction: null,
        });
    });

    return rows;
}

/** Rebuilds reconciliation rows while preserving user decisions on existing rowIds. */
export function refreshHrpReconciliationRows({
    hrpSnapshot,
    componentPayloads,
    ratePayloads = [],
    planType = 'aso',
    accountHccId,
    renewalFees,
    previousRows,
    globalCollisionRows = [],
}) {
    const blockedHrpKeys = collectBlockedHrpKeys(previousRows);
    const next = enrichReconciliationRowsWithGlobalCollisions(
        buildHrpReconciliationRows({
            hrpSnapshot,
            componentPayloads,
            ratePayloads,
            planType,
            accountHccId,
            renewalFees,
            blockedHrpKeys,
        }),
        globalCollisionRows,
    );
    return next.map((row) => {
        const existing = (previousRows ?? []).find((p) => p.rowId === row.rowId);
        if (existing?.confirmed || existing?.skipped) return existing;
        // After Create, rowId may change from renewal-N to extracted-N for the same fee.
        const fromRenewal = (previousRows ?? []).find(
            (p) =>
                (p.confirmed || p.skipped)
                && p.renewalFeeIndex != null
                && p.renewalFeeIndex === row.renewalFeeIndex,
        );
        if (fromRenewal) {
            return {
                ...row,
                confirmed: fromRenewal.confirmed,
                skipped: fromRenewal.skipped,
                userAction: fromRenewal.userAction,
                extractedIndex: fromRenewal.extractedIndex ?? row.extractedIndex,
            };
        }
        const fromKeptHrp = (previousRows ?? []).find(
            (p) =>
                p.confirmed
                && p.userAction === "keep"
                && p.hrpComponentKey
                && p.hrpComponentKey === row.hrpComponentKey
                && row.extractedIndex != null
                && (p.extractedIndex === row.extractedIndex || p.rowId === `hrp-${row.hrpComponentKey}`),
        );
        if (fromKeptHrp) {
            return {
                ...row,
                confirmed: true,
                skipped: false,
                userAction: "keep",
                extractedIndex: row.extractedIndex,
            };
        }
        return row;
    });
}

export function getUnconfirmedHrpReconciliation(reconciliationRows) {
    return (reconciliationRows ?? []).filter((row) => !row.confirmed && !row.skipped);
}

/** True when every reconciliation row has been saved in the drawer (confirmed or discarded). */
export function isHrpReconciliationFullyResolved(reconciliationRows) {
    const rows = reconciliationRows ?? [];
    if (rows.length === 0) return false;
    return rows.every((row) => row.confirmed || row.skipped);
}

export function updateHrpReconciliationRow(reconciliationRows, rowId, patch) {
    return (reconciliationRows ?? []).map((row) =>
        (row.rowId === rowId ? { ...row, ...patch } : row),
    );
}

export function confirmAllSuggestedHrpReconciliation(reconciliationRows) {
    return (reconciliationRows ?? []).map((row) => ({
        ...row,
        confirmed: true,
        skipped: row.suggestedAction === "skip",
        userAction: row.suggestedAction,
    }));
}

/** Applies confirmed reconciliation amounts onto component payloads. Create vs Amend follows name collisions. */
export function applyHrpReconciliationToPayloads({
    componentPayloads,
    reconciliationRows,
    groupId,
    globalCollisionRows = [],
}) {
    if (!Array.isArray(componentPayloads) || !Array.isArray(reconciliationRows)) return;

    const amendCollisionRows = reconciliationRows
        .filter((row) => {
            const action = row.userAction ?? row.suggestedAction;
            return row.confirmed && !row.skipped && action === "amend" && row.hrpComponentName;
        })
        .map((row) => ({
            existingName: row.hrpComponentName,
            existingId: row.hrpComponentName,
            collidedOn: "both",
        }));

    if (amendCollisionRows.length > 0) {
        applyComponentCollisionAmendments(componentPayloads, amendCollisionRows, groupId);
    }

    reconciliationRows.forEach((row) => {
        if (row.extractedIndex == null || !row.confirmed || row.skipped) return;
        let wrapper = componentPayloads[row.extractedIndex];
        if (!wrapper) return;

        const action = row.userAction ?? row.suggestedAction;
        if (action === "keep") return;

        const amount = row.renewalFeeIndex != null
            ? row.renewalNewAmount
            : (row.renewalNewAmount ?? row.extractedAmount);
        if (amount != null && action !== "skip") {
            componentPayloads[row.extractedIndex] = applyAmountToComponentWrapper(wrapper, amount);
            if (row.matchType && row.matchType !== "extracted-only") {
                componentPayloads[row.extractedIndex].renewalSource = row.matchType;
            }
        } else if (row.renewalFeeIndex != null && action !== "skip") {
            if (row.renewalDescription && wrapper.feeDescription !== row.renewalDescription) {
                wrapper = { ...wrapper, feeDescription: row.renewalDescription };
            }
            if (row.extractedFeeType && wrapper.feeType !== row.extractedFeeType) {
                wrapper = { ...wrapper, feeType: row.extractedFeeType };
            }
            componentPayloads[row.extractedIndex] = {
                ...wrapper,
                renewalDocumentSource: true,
            };
        }
    });

    // Global name collisions always win over reconciliation "create" decisions.
    applyComponentCollisionAmendments(componentPayloads, globalCollisionRows, groupId);
}

